import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isOrangePiBoard } from '@/utils/orangePi';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import { effectiveRouterVersion } from '@/utils/routerVersion';
import { isStandardCoinWiring } from '@/utils/coinWiring';
import { createPortal } from 'react-dom';
import useAuthStore from '@/store/authStore';
import ClientAccessSection from '@/components/Admin/Prompt/ClientAccessSection';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getLayoutDefaults } from '@/api/store';
import { colors } from '@/constants/colors';
import AntennaIpsSection from './AntennaIpsSection';
import { toast } from 'react-toastify';
import Btn from '../UI/Btn';
import WebsiteBlockingSection from './WebsiteBlockingSection';
import DurationUnitInput, { decomposeMinutes } from '../UI/DurationUnitInput';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUIStore } from '@/store/useUIStore';
import { openPortalPreview } from '@/utils/portalPreview';
import type { VendoDevice } from '@/pages/Admin/Vendo';
import { getDataPlan, formatMb } from '@/api/dataPlans';
import {
   getRouterRecoveryLoginApi,
   getAdminVendoDevice,
   getAdminDeviceRoadHistory,
} from '@/api/admin';
import {
   updateDevice,
   listDevices,
   listDeviceRatesApi,
   createDeviceRateApi,
   updateDeviceRateApi,
   deleteDeviceRateApi,
   copyRatesToDevicesApi,
   bulkGenerateVouchersApi,
   bulkDeleteVouchersApi,
   listDeviceVouchersApi,
   listVoucherRateGroupsApi,
   exportVouchersCsvApi,
   type ApiDeviceVoucher,
   type ApiVoucherRateGroup,
   type BulkVoucher,
   deleteDeviceApi,
   type BlockingSubVendo,
   pushLoginHtml,
   listTransfersApi,
   getClientAccessApi,
   cancelTransferApi,
   updateNodeMcuWifi,
   isSsidMismatch,
   type SsidMismatchError,
   getCoinBoxDiagnosis,
   type CoinBoxDiagnosis,
   renameRuijieSsid,
   setCoinConfigApi,
   markDeviceStolen,
   reflashHistory,
   reflashRestore,
   type ReflashSnapshot,
   type ApiDeviceTransfer,
   type ApiVendoDevice,
   type PortalConfig,
   getDeviceUpdateStatus,
} from '@/api/vendo';
import { themeProgressDevice } from '@/utils/portalThemeProgress';
import { hexSyncOf, isHexHybrid } from '@/utils/hexPortalProgress';
import RouterSyncProgress from '../UI/RouterSyncProgress';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import PortalAdvancedDrawer from './PortalAdvancedDrawer';
import { normalizePortalConfig } from './portalVibes';
import NodeMcuFlasher from '../Vendo/NodeMcuFlasher';
import DeviceStatusBadge, { compositeStatus } from '../Vendo/DeviceStatusBadge';
import PinMapEditor from '../Vendo/PinMapEditor';
import UpdatesTab, { outdatedCount } from './UpdatesTab';
import ConnectionHistory from './ConnectionHistory';
import { isReportStale, rogueDhcpLine, tunnelState } from './deviceDiagText';
import { pickApiDevice, FLEET_REFETCH_MS } from './apiDeviceSource';
import WanTab from './WanTab';
import { wanCapable, customerVlanCapable } from '@/utils/fleetVersions';
import CustomerVlanCard from './CustomerVlanCard';
import { mikrotikPortRoles } from '../Vendo/MikrotikPortRolesCard';
import { coinBoxHoldsRename, coinBoxRescueName } from '@/utils/coinBoxRenameHold';
import SsidRenameProgress from '../UI/SsidRenameProgress';
import PortalThemeProgress from '../UI/PortalThemeProgress';
import {
   isPickableDevice,
   buildPendingTransferIds,
} from '@/utils/deviceFilters';

type SettingsTab =
   | 'rates'
   | 'session'
   | 'hardware'
   | 'connection'
   | 'updates'
   | 'wan'
   | 'theme'
   | 'ownership';

interface Props {
   // `_initialTab` is an optional intent flag set by the caller to land
   // the modal on a specific tab (e.g. delete-from-list lands on Hardware
   // where the Danger Zone lives). Underscore-prefixed so it doesn't
   // clash with anything on the device API shape.
   // `_apiDevice` is set by the ADMIN fleet table: the operator devices
   // store only holds the logged-in user's devices, so for another
   // tenant's device the store lookup misses — the caller passes the raw
   // API doc so settings/WiFi fields hydrate with the tenant's real
   // values instead of defaults.
   data: VendoDevice & { _initialTab?: SettingsTab; _apiDevice?: ApiVendoDevice };
}

type PortalThemeKey = 'gaming' | 'midnight' | 'clean' | 'sunset' | 'ocean' | 'custom';

type PortalThemePreview = {
   key: PortalThemeKey;
   name: string;
   description: string;
   /** swatches used to paint the card thumbnail */
   pageBg: string;
   accent: string;
   card: string;
   title: string;
};

// Customizable sits in the first row with an illustrated card (Ray
// 2026-10-23: owners did not notice it at the end of the list).
const PORTAL_THEMES: PortalThemePreview[] = [
   { key: 'gaming',   name: 'Gaming',   description: 'Dark purple glow, neon accents. Default.', pageBg: '#0c0c1d', accent: '#6C5CE7', card: 'rgba(255,255,255,0.06)', title: '#f0f0f5' },
   { key: 'midnight', name: 'Midnight', description: 'Deep blue-black, subtle cyan highlights.',   pageBg: '#020617', accent: '#3760f9', card: 'rgba(30,41,59,0.6)',   title: '#e2e8f0' },
   { key: 'custom',   name: 'Customizable', description: 'Pro & fully customizable — layout, background, slider, sounds & effects.', pageBg: '#0e1116', accent: '#4f7cff', card: 'rgba(255,255,255,0.05)', title: '#f3f5f9' },
   { key: 'sunset',   name: 'Sunset',   description: 'Warm orange/pink gradient, cozy feel.',      pageBg: 'linear-gradient(160deg,#2a1517,#4b1c30,#6b2c1e)', accent: '#f97316', card: 'rgba(255,255,255,0.08)', title: '#fed7aa' },
   { key: 'ocean',    name: 'Ocean',    description: 'Teal + aqua, breezy aquatic palette.',       pageBg: '#042f3a', accent: '#06b6d4', card: 'rgba(255,255,255,0.06)', title: '#ecfeff' },
   { key: 'clean',    name: 'Clean',    description: 'Light mode, white + subtle gray.',           pageBg: '#f8fafc', accent: '#0f766e', card: '#ffffff',              title: '#0f172a' },
];

// The preview host lives in utils/portalPreview.ts — the Devices page's
// The card's "Open portal" opens the REAL portal; this button is the theme
// designer's sample-data preview (works offline / locked).

// Rows in the modal. `_id` is undefined for newly-added drafts (those
// become POSTs on Save). Existing rows from the server become PATCHes.
// Removed rows (server _id missing from this list at Save time) become
// DELETEs.
type RatePlan = {
   key: string;       // local stable key for React (random for drafts, _id for server rows)
   _id?: string;      // server id when known
   price: string;
   duration: string;  // minutes — stays string to keep existing input/serialize plumbing
   label: string;
   description: string;
   type: 'coin' | 'voucher';
   /**
    * VALIDITY (minutes): how long the time stays usable, counted from the
    * moment the customer paid. null = use the device default.
    *
    * Was labelled "Pause limit" until 2026-09-04 and meant something else —
    * a window measured from the customer's last disconnect that restarted
    * every time he came back, so it could never expire the time of a customer
    * who dropped in once a day. Operators had been setting it as validity all
    * along, which is what piso wifi calls it, so it is now that.
    */
   validityMinutes: number | null;
};

type SettingsForm = {
   bandwidthDown: string;          // settings.defaultDownload
   bandwidthUp: string;            // settings.defaultUpload
   allowPause: boolean;            // settings.pauseEnabled
   allowRoaming: boolean;          // settings.roamingEnabled
   defaultValidity: string;        // settings.maxPauseDuration (the server's name for "Default validity (pause)")
   ratePerPeso: string;            // settings.ratePerPeso
   blockTethering: boolean;        // !settings.allowTethering (default ON = blocked)
   antiWorm: boolean;              // settings.antiWorm — Track F, router-wide (main vendo)
   antiTorrent: boolean;           // settings.antiTorrent
   antiDetect: boolean;            // settings.antiDetect
   portalMode: 'coin' | 'voucher' | 'both'; // settings.portalMode
   showVoucherRates: boolean;      // settings.showVoucherRates — "Vouchers" button on the portal
   showGame: boolean;              // settings.showGame — "Play game" link on the portal
   showRates: boolean;             // settings.showRates — "View Rates": the Wi-Fi rate list on the portal
};

const DEFAULT_FORM: SettingsForm = {
   bandwidthDown: '5',
   bandwidthUp: '5',
   allowPause: true,
   allowRoaming: true,
   defaultValidity: '1440',
   ratePerPeso: '6',
   blockTethering: true,
   antiWorm: false,
   antiTorrent: false,
   antiDetect: false,
   portalMode: 'both',
   showVoucherRates: true,
   showGame: true,
   showRates: true,
};

// OrangePi One (H3) header GPIO lines commonly usable for a coin/slot rig
// (libgpiod names). Dropdown options for the Custom coin-wiring tab — safer than
// free text. eAZy standard is coin=PA12, slot=PA11 (the image default; see utils/coinWiring).
const OPI_ONE_PINS = [
   'PA1', 'PA6', 'PA7', 'PA8', 'PA9', 'PA10', 'PA11', 'PA12', 'PA13', 'PA14',
   'PA18', 'PA19', 'PA20', 'PA21', 'PC4', 'PC7', 'PD14', 'PG6', 'PG7', 'PG8',
   'PG9', 'PG11',
];

const DeviceSettings = ({ data }: Props) => {
   const { handleClose, handleShow } = useUIStore();
   const queryClient = useQueryClient();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const patchDeviceNameInStore = useVendoDevicesStore((s) => s.patchDeviceName);
   // Pull the full API-shape device from the store so we can surface
   // sensitive fields (router.password) that the display type strips.
   // Admin path: the store misses (it holds the admin's own devices),
   // so fall back to the raw doc the fleet table passed in.
   const storeDevice = useVendoDevicesStore((s) =>
      s.devices.find((d) => d._id === data._id)
   );
   // 🔴 `data._apiDevice` is frozen at the moment the fleet table was clicked,
   // and on the admin path it used to be the ONLY source — so this modal
   // served an hours-old row as live (measured 2026-08-23). Re-ask on open.
   //
   // Same query key as the fleet table, so this is not a second endpoint: it
   // shares that cache, and `refetchOnMount: 'always'` makes opening the modal
   // refresh the table behind it too. Only enabled on the ADMIN path —
   // `_apiDevice` being set is precisely what says the fleet table opened this;
   // an operator calling getAdminVendoDevices would be a 403.
   //
   // 2026-09-28: this used to share the fleet LIST's cache. That list is now a
   // projection of the table's columns (the full list was 5.6 MB a minute), so
   // the modal asks for its ONE router in full — rates, portal layout, WAN,
   // NodeMCU WiFi all live only here now.
   const { data: adminDevice } = useQuery({
      queryKey: ['admin', 'vendo', 'device', data._id],
      queryFn: () => getAdminVendoDevice(data._id),
      enabled: !!data._apiDevice,
      refetchOnMount: 'always',
      refetchInterval: FLEET_REFETCH_MS,
   });
   const freshDevice = adminDevice && adminDevice._id === data._id ? adminDevice : undefined;
   const apiDevice = pickApiDevice(
      storeDevice as ApiVendoDevice | undefined,
      freshDevice as unknown as ApiVendoDevice | undefined,
      data._apiDevice
   );
   // The recovery login only works from the owner port when there is one
   // (setup pins it to 192.168.88.0/24); otherwise from the customer LAN.
   const recoveryOwnerPort =
      ((apiDevice?.router as { ownerPort?: string | null } | undefined)?.ownerPort ?? '') || '';
   // A sub-vendo's portal lives on its PARENT's router: theme progress is
   // judged by the parent's check-in, not by a router the sub does not have.
   const parentRouter = useVendoDevicesStore((s) =>
      apiDevice?.parent ? s.devices.find((d) => d._id === apiDevice.parent)?.router : undefined
   );
   // Ownership tab: a sub-vendo moves WITH its main (server refuses to
   // transfer a sub on its own), so the tab names the main to go to; a main
   // says how many subs ride along with it.
   const parentName = useVendoDevicesStore((s) =>
      apiDevice?.parent ? s.devices.find((d) => d._id === apiDevice.parent)?.name : undefined
   );
   const subCount = useVendoDevicesStore((s) =>
      s.devices.filter((d) => d.parent === data._id).length
   );
   const themeDevice = apiDevice ? themeProgressDevice(apiDevice, parentRouter ?? null) : undefined;
   // Inline-editable device name. `displayName` is the source of truth
   // post-mount — `data.name` is a frozen snapshot from when the modal
   // opened, so we maintain our own copy that updates optimistically.
   const [displayName, setDisplayName] = useState(data.name);
   const [editingName, setEditingName] = useState(false);
   const [nameDraft, setNameDraft] = useState(data.name);
   const [nameError, setNameError] = useState<string | null>(null);
   useEffect(() => {
      setDisplayName(data.name);
      setNameDraft(data.name);
   }, [data.name]);
   const [tab, setTab] = useState<SettingsTab>(data._initialTab || 'rates');
   // When this router changed how it reaches us. Admin path only — the route
   // requires the devices tab, so an operator asking would be a 403. Fetched
   // only while the Connection tab is open: a router that has never switched
   // has one baseline row and nothing to show, so this is not worth a request
   // on every modal open.
   const { data: roadHistory } = useQuery({
      queryKey: ['admin', 'device-road-history', data._id],
      queryFn: () => getAdminDeviceRoadHistory(data._id),
      enabled: !!data._apiDevice && tab === 'connection',
   });
   const [form, setForm] = useState<SettingsForm>(DEFAULT_FORM);
   const [rates, setRates] = useState<RatePlan[]>([]);
   // Snapshot of the server state at load time — used at Save to diff
   // and emit the right create/patch/delete calls.
   const [serverRates, setServerRates] = useState<RatePlan[]>([]);
   const [theme, setTheme] = useState<PortalThemeKey>(
      (data.portalTheme as PortalThemeKey) || 'gaming'
   );
   // Customizable-theme config (only meaningful when theme === 'custom').
   // Seeded from the saved config, normalized so missing nested keys default.
   const [portalConfig, setPortalConfig] = useState<PortalConfig>(() =>
      normalizePortalConfig((data as { portalConfig?: PortalConfig | null }).portalConfig)
   );
   const [advancedOpen, setAdvancedOpen] = useState(false);
   // Warm the admin per-layout defaults while the operator is still on the
   // settings tabs, so the Advanced customization drawer has them the
   // instant it opens instead of showing "Loading your defaults…".
   useQuery({
      queryKey: ['layout-defaults'],
      queryFn: () => getLayoutDefaults(),
      staleTime: 0,
   });
   const [savingConfig, setSavingConfig] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   // Two-step delete confirm. Idle → confirming → deleting → (closed on success).
   // 'cascade-confirming' = the server refused because sub-vendos are still
   // attached; the operator is shown their names and asked to delete all.
   type DeleteState = 'idle' | 'confirming' | 'cascade-confirming' | 'deleting';
   const [deleteState, setDeleteState] = useState<DeleteState>('idle');
   const [blockingSubs, setBlockingSubs] = useState<BlockingSubVendo[]>([]);
   const [stolen, setStolen] = useState<boolean>(!!(data as { markedStolen?: boolean }).markedStolen);
   const [stolenSaving, setStolenSaving] = useState(false);
   /**
    * This vendo was SOLD by the person looking at it (Ray, 2026-08-30). They
    * keep the settings — the buyer is usually not technical, and talking a
    * non-technical owner through a firmware update over chat is how it worked
    * before. What they lose is everything that changes WHO OWNS IT or destroys
    * the buyer's business: the Ownership tab and the whole Danger Zone.
    *
    * The server is the real gate. `allowSeller` is mounted route by route, so
    * transfer, delete, reset-sales, mark-stolen, move-licence and the client
    * sub-login all refuse a seller regardless of what this file renders —
    * hiding them here only avoids offering buttons that would come back 404.
    */
   const soldByMe = (data as { isCurrentOwner?: boolean }).isCurrentOwner === false;
   // Another router on the LAN handing out addresses (an owner's AP with DHCP
   // on). Above the tabs: it explains a dark NodeMCU whichever tab is open.
   const rogueDhcp = rogueDhcpLine(apiDevice?.router?.diag);
   const soldToEmail = (data as { soldTransfer?: { toEmail?: string } }).soldTransfer
      ?.toEmail;

   // Reflash Router (main + MikroTik only) opens its own modal.
   const isMainVendo = !data.parentId;
   const isMikrotik = data.platform === 'mikrotik';
   const isOpenwrt = data.platform === 'openwrt';
   // OrangePi (SBC): on-board coin reader (no NodeMCU), wired external AP (its
   // own SSID isn't ours to set), and no Ruijie-specific flows. Hide the
   // NodeMCU, Change-WiFi-Name, Rebind-Ruijie, and Remove-SmartFi
   // sections for it — they don't apply and just confuse. (Custom OrangePi pin
   // config + its own Remove/rebind come later — items E/D.)
   // hwBoard lives at the TOP LEVEL on the fleet device (mapped from
   // router.hwBoard), and under router on the raw API device — check both so the
   // OrangePi sections show regardless of which object we're handed.
   const hwBoard =
      apiDevice?.router?.hwBoard || (data as { hwBoard?: string }).hwBoard || '';
   const isOrangePi = isOrangePiBoard(hwBoard);
   // A board with NO RADIO of its own: OrangePi and mini PC. Its customers -
   // and its NodeMCU - are on the OPERATOR's access point, whose name is not
   // ours to know, so it must be asked for. A SUB-vendo is the same case even
   // when the parent has a radio: its customers live on its own VLAN's access
   // point, not on the parent's WiFi.
   const boardHasNoRadio = isOrangePi || hwBoard.startsWith('x86-64');

   // Router local login (Hardware tab). Platform admins only — this is a live
   // router password, and the server refuses the request for anyone else, so
   // the gate here is only to avoid showing a button that would 403.
   const { user: adminUser } = useAuthStore() as {
      user?: { role?: string } | null;
   };
   const isPlatformAdmin = adminUser?.role === 'admin';
   const [recoveryLogin, setRecoveryLogin] = useState<{
      username: string;
      password: string;
   } | null>(null);
   const [recoveryState, setRecoveryState] = useState<
      'idle' | 'loading' | 'missing'
   >('idle');
   const handleShowRecoveryLogin = async () => {
      setRecoveryState('loading');
      try {
         // data._id is the Mongo id; data.id is the "ESP-…" device code. The
         // endpoint takes the Mongo id and 400s on the other one.
         const res = await getRouterRecoveryLoginApi(data._id);
         setRecoveryLogin({ username: res.username, password: res.password });
         setRecoveryState('idle');
      } catch (err) {
         // A 404 here is normal, not a failure: routers set up before this
         // existed get their login on the next config generation. Say that
         // plainly instead of showing a red error for expected state.
         const status = (err as { response?: { status?: number } })?.response
            ?.status;
         if (status === 404) {
            setRecoveryState('missing');
         } else {
            setRecoveryState('idle');
            toast.error('Could not read the router login');
         }
      }
   };

   // Updates tab (2d): light poll just for the tab's "outdated" count pill
   // (the tab body has its own fuller query). Skipped for sub-vendos with
   // no NodeMCU — they have nothing operator-updatable.
   const hasCoinBox = !!(data as { nodeMcu?: { mac?: string } }).nodeMcu?.mac;

   // Live up/down pip on the Connection tab, so the tab itself answers "is it
   // reachable right now" without being opened. Same 4-minute threshold the box
   // uses, so the tab and the panel inside can never disagree.
   const connectionDot: 'ok' | 'bad' | 'unknown' = (() => {
      if (!apiDevice?.router?.diag) return 'unknown';
      // Freshness FIRST. A box that went quiet four hours ago still carries a
      // report saying "connected 30 seconds" — that was true when it was sent
      // and is a lie now. Ray caught exactly this on his bench box.
      if (isReportStale(apiDevice?.router?.lastSeenAt)) return 'bad';
      // 🔴 This used to be `hs >= 0 && hs <= 240 ? 'ok' : 'bad'`, so the
      // server's -1 "not reported" default lit a red pip on 79 healthy vendos.
      // One helper now decides it for the pip and the card inside, so the tab
      // and the panel can never disagree.
      const state = tunnelState(apiDevice.router.diag.hsAge);
      return state === 'up' ? 'ok' : state === 'down' ? 'bad' : 'unknown';
   })();
   const showUpdatesTab = isMainVendo || hasCoinBox;
   // Internet (WAN): a main Ruijie/OrangePi whose firmware actually carries the
   // CGI. Without the version check the tab appeared on every OpenWRT main
   // vendo — 240 of 396 on prod — and every Apply on one of those came back as
   // "Could not reach the router" about a router that was perfectly healthy.
   // A MikroTik Hybrid main gets the same tab: the server applies the setting
   // over the router's API (services/mikrotikWan.ts), no agent needed.
   const isMikrotikHybridMain = isMainVendo && isMikrotik && apiDevice?.router?.localMode === true;
   // Every MikroTik main gets the tab too, for the "Access Point VLAN" card:
   // same place as on OpenWrt (A51, Ray 2026-10-29). A cloud MikroTik shows
   // only that card; the uplink settings stay Hybrid-only.
   const showWanTab =
      isMainVendo && ((isOpenwrt && wanCapable(apiDevice?.router?.firmwareVersion)) || isMikrotik);
   const { data: updateStatus } = useQuery({
      queryKey: ['device-update-status', data._id],
      queryFn: () => getDeviceUpdateStatus(data._id),
      enabled: showUpdatesTab,
      staleTime: 15000,
   });
   const outdated = outdatedCount(updateStatus);

   const handleToggleStolen = async () => {
      if (stolenSaving) return;
      const next = !stolen;
      setStolenSaving(true);
      try {
         await markDeviceStolen(data._id, next);
         setStolen(next);
         refetchDevices();
         toast.success(
            next
               ? 'Flagged lost/stolen — re-registration on another account is now blocked'
               : 'Removed the lost/stolen flag'
         );
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Could not update');
      } finally {
         setStolenSaving(false);
      }
   };
   // Inline NodeMCU re-flash on the Hardware tab. Operators replacing a
   // faulty NodeMCU or recovering from a stale identity (NodeMCU was
   // flashed for a deleted device) need a way to re-run the flasher
   // without the wizard's setupStatus≠done gate.
   const [showFlasher, setShowFlasher] = useState(false);
   // Three acknowledgments the operator must tick before the Re-flash
   // button enables. Framed as a hardware-swap action so the button
   // isn't treated as a casual reset — re-flashing a working chip risks
   // mismatched identity and confusing support cases.
   const [swapAcks, setSwapAcks] = useState({
      brokenOrMissing: false,
      replacementConnected: false,
      understandsOverwrite: false,
   });
   const allSwapAcksTicked =
      swapAcks.brokenOrMissing &&
      swapAcks.replacementConnected &&
      swapAcks.understandsOverwrite;
   // Update NodeMCU (WiFi): edit the AP the wireless NodeMCU joins, save, then
   // re-flash to apply (creds are baked into the chip). Separate flasher state
   // from the Replace flow so the two don't entangle.
   const [wifiSsid, setWifiSsid] = useState(
      apiDevice?.nodeMcu?.wifiSsid ||
         (boardHasNoRadio ? '' : apiDevice?.router?.ssid) ||
         ''
   );
   const [wifiPassword, setWifiPassword] = useState(apiDevice?.nodeMcu?.wifiPassword || '');
   const [savingWifi, setSavingWifi] = useState(false);
   const [wifiSaved, setWifiSaved] = useState(false);
   // Set when the server refuses a WiFi name this router does not broadcast. On
   // a Ruijie the NodeMCU has no other way to reach us, so a wrong name here is
   // a dead vendo that looks like a dead board — operators have replaced working
   // hardware over it. Shown inline with the router's real name; a bridged
   // access point of your own is still allowed, just confirmed.
   const [ssidWarn, setSsidWarn] = useState<SsidMismatchError | null>(null);
   // "Why is my NodeMCU not reporting?" — answered from the router itself.
   // Operators have been buying replacement coin boards for router-side faults
   // because nothing here could tell the two apart.
   const [diag, setDiag] = useState<CoinBoxDiagnosis | null>(null);
   // Folded by default: the legend is for the operator meeting these words for
   // the first time, not for every visit to this tab.
   const [legendOpen, setLegendOpen] = useState(false);
   // Both the diagnosis button and the legend appear ONLY while the vendo is
   // degraded (Ray, 2026-08-10). That is the one state either of them helps
   // with; on a healthy vendo they are two questions nobody is asking.
   const isDegraded =
      compositeStatus(data.status, data.routerStatus) === 'degraded';

   /**
    * On a Ruijie the NodeMCU joins the ROUTER'S OWN WiFi, so there is exactly
    * one correct value for this field and the product already knows it — the
    * rename endpoint writes both names together. An editable box whose only
    * right answer is one we can compute is not a feature, it is a trap: every
    * keystroke can strand the board, and none can improve anything. Operators
    * kept meeting it while looking for "rename my WiFi" and typing a new name
    * here, which renames nothing and points the chip at a network that does
    * not exist (Ray, 2026-08-10 — "the operator mostly confuse the wifi name
    * change"). Warning them afterwards did not work; nobody reads a warning.
    *
    * MikroTik is the opposite case: that NodeMCU joins an access point SmartFi
    * neither manages nor can see, so the operator knows something we do not and
    * the field has to stay. OrangePi never reaches here — it reads coins over
    * GPIO and has no coin board at all.
    */
   /**
    * Wired vs wireless USED to be implied by whether the SSID field was blank —
    * the server still reads it that way. That was survivable while the field
    * was on screen; once it is hidden on Ruijie there is no way left to say
    * "this box is wired" (Ray, 2026-08-10: "what if he flash the nodemcu to
    * wired?"). So the choice becomes an explicit toggle here, mirroring the one
    * the setup wizard already shows, and it writes through the same endpoint:
    * a name means wireless, blank means wired.
    */
   const savedConnMode =
      (apiDevice?.nodeMcu as { connMode?: string } | undefined)?.connMode ===
      'ethernet'
         ? 'ethernet'
         : 'wifi';
   const [connMode, setConnMode] = useState<'wifi' | 'ethernet'>(savedConnMode);
   useEffect(() => setConnMode(savedConnMode), [savedConnMode]);

   // Bench 2026-09-24: on a mini PC sub-vendo this said "joins SmartFi WiFi -
   // same as your WiFi" and baked that name into the chip. A mini PC has no
   // radio, nothing broadcasts that name, so the NodeMCU could never join and
   // the card stayed Degraded. Only a board that HAS its own WiFi, and only a
   // main vendo, has one right answer we can fill in.
   const coinBoxJoinsOwnRouter =
      isOpenwrt && !boardHasNoRadio && isMainVendo && connMode === 'wifi';

   // The Wi-Fi it had before Wired, so Wireless brings it back (Ray, 2026-10-10:
   // Wired then Wireless replaced SMARTFI_DEV_2 with "SmartFiWifi" and lost the
   // password). Seeded from the server, which keeps it while the box is wired.
   const lastWifiRef = useRef({
      ssid:
         apiDevice?.nodeMcu?.wifiSsid ||
         ((apiDevice?.nodeMcu as { lastWifiSsid?: string } | undefined)?.lastWifiSsid ?? ''),
      pass:
         apiDevice?.nodeMcu?.wifiPassword ||
         ((apiDevice?.nodeMcu as { lastWifiPassword?: string } | undefined)?.lastWifiPassword ?? ''),
   });

   const changeConnMode = (mode: 'wifi' | 'ethernet') => {
      if (mode === connMode) return;
      setConnMode(mode);
      if (mode === 'ethernet' && wifiSsid.trim()) {
         lastWifiRef.current = { ssid: wifiSsid.trim(), pass: wifiPassword };
      }
      // Wireless on a Ruijie has exactly one right answer, so fill it in for
      // them; anywhere else bring back the Wi-Fi it had.
      const nextSsid =
         mode === 'ethernet'
            ? ''
            : isOpenwrt && !boardHasNoRadio
              ? currentSsid
              : lastWifiRef.current.ssid || wifiSsid;
      const nextPass = mode === 'ethernet' ? '' : lastWifiRef.current.pass || wifiPassword;
      setWifiSsid(nextSsid);
      setWifiPassword(nextPass);
      void saveNodeMcuWifi({ silent: true, ssidOverride: nextSsid, passwordOverride: nextPass });
   };
   // ⚠️ Ray removed the "this NodeMCU uses a different WiFi" escape hatch
   // (2026-08-10): one more thing to read is one more thing to get wrong. So a
   // Ruijie NodeMCU can no longer be pointed at another access point FROM THIS
   // SCREEN. Two of 256 prod boxes are in that state today (CINDY, CHITO
   // DUMALE) and both are working — nothing breaks, but if one ever needs
   // changing it now takes a re-flash or a support-side edit. See
   // [[smartfi_ssid_mismatch_fleet_audit]].
   const [diagLoading, setDiagLoading] = useState(false);
   const runDiagnosis = async () => {
      setDiagLoading(true);
      setDiag(null);
      try {
         setDiag(await getCoinBoxDiagnosis(data._id));
      } catch (err: any) {
         toast.error(
            err?.response?.data?.error || 'Could not check the NodeMCU right now'
         );
      } finally {
         setDiagLoading(false);
      }
   };
   // Reveals the "Replacing a broken/lost chip?" acknowledgments under the single
   // Coin-box section. The routine re-flash uses showFlasher directly.
   const [showReplace, setShowReplace] = useState(false);

   const saveNodeMcuWifi = async (opts?: {
      silent?: boolean;
      allowMismatch?: boolean;
      /** Save THIS name instead of the field's. React state set in the same
       *  tick is not visible here, so the "use the router's name" button has to
       *  pass it explicitly or we would re-save the rejected one. */
      ssidOverride?: string;
      /** Same reason as ssidOverride: the password set in the same tick. */
      passwordOverride?: string;
   }): Promise<boolean> => {
      setSavingWifi(true);
      try {
         const res = await updateNodeMcuWifi(data._id, {
            wifiSsid: (opts?.ssidOverride ?? wifiSsid).trim(),
            wifiPassword: opts?.passwordOverride ?? wifiPassword,
            ...(opts?.allowMismatch ? { allowMismatch: true } : {}),
         });
         setSsidWarn(null);
         if (opts?.silent) {
            setWifiSaved(true);
            window.setTimeout(() => setWifiSaved(false), 1800);
         } else {
            toast.success(res.msg);
         }
         void useVendoDevicesStore.getState().fetch();
         return true;
      } catch (err: any) {
         // Not an error the operator should have to read in a toast that
         // disappears — it needs a decision, next to the field, with the
         // router's real name in front of them.
         const mismatch = isSsidMismatch(err);
         if (mismatch) {
            setSsidWarn(mismatch);
            return false;
         }
         toast.error(err?.response?.data?.error || 'Failed to save WiFi');
         return false;
      } finally {
         setSavingWifi(false);
      }
   };
   // Optimistic auto-save on blur — only when something actually changed vs the
   // last loaded values, so tabbing through fields doesn't fire pointless saves.
   const savedWifiRef = useRef({
      ssid:
         apiDevice?.nodeMcu?.wifiSsid ||
         (boardHasNoRadio ? '' : apiDevice?.router?.ssid) ||
         '',
      pass: apiDevice?.nodeMcu?.wifiPassword || '',
   });
   const autoSaveWifi = () => {
      if (
         wifiSsid.trim() === savedWifiRef.current.ssid &&
         wifiPassword === savedWifiRef.current.pass
      ) {
         return;
      }
      const attempt = { ssid: wifiSsid.trim(), pass: wifiPassword };
      const previous = savedWifiRef.current;
      savedWifiRef.current = attempt;
      // Put the marker back if the save didn't land (e.g. it needs the
      // SSID-mismatch confirm), so a later blur retries instead of the field
      // believing a value that was never persisted.
      void saveNodeMcuWifi({ silent: true }).then((ok) => {
         if (!ok && savedWifiRef.current === attempt) {
            savedWifiRef.current = previous;
         }
      });
   };

   // Router broadcast WiFi name (Ruijie only) — the name customers see. Distinct
   // from the NodeMCU join field above. Pushed to the box on its next heartbeat
   // (agent 1.0.12+); renaming briefly drops WiFi clients.
   const [ssidDraft, setSsidDraft] = useState('');
   const [savingSsid, setSavingSsid] = useState(false);
   // The instant OUR Save succeeded — the progress banner shows from this
   // at once, instead of waiting for a refetch (the admin fleet query only
   // re-asks every FLEET_REFETCH_MS, so it used to appear only on reload).
   const [ssidSavedAt, setSsidSavedAt] = useState<number | null>(null);
   // Portal theme save instant — drives the "sending to the router" banner.
   const [themeSavedAt, setThemeSavedAt] = useState<number | null>(null);
   // A MikroTik Hybrid serves prices, look, name and codes from files the
   // cloud writes to it: every save in this modal is "sent to the router", and
   // one line under Save says when it is live there (Ray 2026-10-21).
   const [routerSavedAt, setRouterSavedAt] = useState<number | null>(null);
   const hexHybridRoot = isHexHybrid(
      (apiDevice?.parent ? parentRouter : apiDevice?.router) as
         | { platform?: string | null; localMode?: boolean | null }
         | null
         | undefined
   );
   const hexSync = hexSyncOf(apiDevice, parentRouter ?? null);
   // Every source this modal may be reading the device from.
   const refetchDevice = () => {
      void useVendoDevicesStore.getState().fetch();
      if (data._apiDevice) {
         void queryClient.invalidateQueries({ queryKey: ['admin', 'vendo', 'device', data._id] });
         void queryClient.invalidateQueries({ queryKey: ['admin', 'vendo', 'devices'] });
      }
   };
   useEffect(() => {
      setSsidDraft(apiDevice?.router?.ssid || '');
   }, [apiDevice?.router?.ssid]);
   const currentSsid = apiDevice?.router?.ssid || '';
   // With the NodeMCU silent a saved rename parks invisibly forever — see
   // coinBoxHoldsRename. The server refuses it too (409 coinbox-offline).
   // Exception: the rescue direction (the name the NodeMCU was flashed for)
   // stays savable, so a name-mismatched box can be fixed from here.
   const coinBoxWaiting = apiDevice ? coinBoxHoldsRename(apiDevice, ssidDraft) : false;
   const coinBoxRescue = apiDevice ? coinBoxRescueName(apiDevice) : '';
   const saveSsid = async () => {
      const next = ssidDraft.trim();
      if (!next || next === currentSsid) return;
      setSavingSsid(true);
      try {
         await renameRuijieSsid(data._id, next);
         toast.success('WiFi name saved — the router will apply it shortly');
         setSsidSavedAt(Date.now());
         refetchDevice();
      } catch (err: any) {
         toast.error(err?.response?.data?.msg || 'Failed to rename WiFi');
      } finally {
         setSavingSsid(false);
      }
   };

   // Coin/slot GPIO wiring (OrangePi/SBC). Blank fields fall back to the image's
   // baked defaults. Pushed to the box on its next heartbeat (rewrites coin.conf
   // + restarts coinbridge). Lets a different board/carrier work without a rebuild.
   const savedCoin = apiDevice?.router?.coinConfig;
   const [coinCfg, setCoinCfg] = useState({
      gpioLine: savedCoin?.gpioLine ?? '',
      setLine: savedCoin?.setLine ?? '',
      setOpen: savedCoin?.setOpen ?? '',
      setClosed: savedCoin?.setClosed ?? '',
      debounceMs:
         savedCoin?.debounceMs != null ? String(savedCoin.debounceMs) : '',
   });
   // Standard = the eAZy defaults (most builds); Custom = per-board GPIO remap.
   // The server stores the standard set EXPLICITLY on "Standard" (so the router
   // is actually pushed PA12/PA11), so "saved" alone no longer means custom —
   // compare against the standard set.
   const [coinMode, setCoinMode] = useState<'standard' | 'custom'>(
      isStandardCoinWiring(savedCoin) ? 'standard' : 'custom'
   );
   const [savingCoin, setSavingCoin] = useState(false);
   // Same poll trap as the settings form: seed on content, not identity.
   const coinConfigContent = JSON.stringify(apiDevice?.router?.coinConfig ?? null);
   useEffect(() => {
      const c = apiDevice?.router?.coinConfig;
      setCoinCfg({
         gpioLine: c?.gpioLine ?? '',
         setLine: c?.setLine ?? '',
         setOpen: c?.setOpen ?? '',
         setClosed: c?.setClosed ?? '',
         debounceMs: c?.debounceMs != null ? String(c.debounceMs) : '',
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [coinConfigContent]);
   // Optimistic: reflect the change instantly (toast + flip the tab), fire the
   // PATCH in the background, and only surface an error if it actually fails.
   const saveCoinConfig = () => {
      setCoinMode('custom');
      toast.success("Coin wiring saved — applies on the box's next check-in");
      setCoinConfigApi(data._id, {
         gpioLine: coinCfg.gpioLine.trim(),
         setLine: coinCfg.setLine.trim(),
         setOpen: coinCfg.setOpen.trim(),
         setClosed: coinCfg.setClosed.trim(),
         debounceMs: coinCfg.debounceMs.trim()
            ? Number(coinCfg.debounceMs)
            : null,
      })
         .then(() => void useVendoDevicesStore.getState().fetch())
         .catch((err: any) => {
            toast.error(
               err?.response?.data?.error || 'Save failed — the change was not applied'
            );
         });
   };
   const resetCoinConfig = () => {
      setCoinMode('standard');
      setCoinCfg({
         gpioLine: '',
         setLine: '',
         setOpen: '',
         setClosed: '',
         debounceMs: '',
      });
      toast.success('Coin wiring set to standard (eAZy) wiring');
      setCoinConfigApi(data._id, { reset: true })
         .then(() => void useVendoDevicesStore.getState().fetch())
         .catch((err: any) => {
            toast.error(err?.response?.data?.error || 'Reset failed');
         });
   };

   // Re-push captive portal pages. Triggered manually when login.html
   // template changes server-side, or after recovery when the existing
   // pushed file is stale.
   type PushState = 'idle' | 'pushing' | 'pushed' | 'error';
   const [pushPagesState, setPushPagesState] = useState<PushState>('idle');

   const handlePushCaptivePages = async () => {
      if (pushPagesState === 'pushing') return;
      setPushPagesState('pushing');
      try {
         await pushLoginHtml(data._id);
         setPushPagesState('pushed');
         toast.success('login.html and status.html pushed to router', { autoClose: 2500 });
         setTimeout(() => setPushPagesState('idle'), 3000);
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            (err as Error)?.message ||
            'Push failed';
         setPushPagesState('error');
         toast.error(msg);
         setTimeout(() => setPushPagesState('idle'), 4000);
      }
   };


   // Exit-to-OpenWRT (Lite only): server-side this IS a device delete (frees
   // the WG slot + detaches the license); the difference is the UX — we keep
   // the modal open and walk the operator through the router-local final step
   // (agent self-unclaims on 401 within ~2 min on fw 1.0.13+, unlocking the
   // local flash page where "Remove SmartFi → official OpenWRT" lives).
   const [exitState, setExitState] = useState<
      'idle' | 'confirming' | 'working' | 'done'
   >('idle');
   const handleExitToOpenwrt = async () => {
      if (exitState !== 'confirming') return;
      setExitState('working');
      try {
         await deleteDeviceApi(data._id);
         await refetchDevices();
         queryClient.invalidateQueries({ queryKey: ['admin', 'vendo'] });
         setExitState('done'); // keep the modal open — show the local steps
      } catch (err: unknown) {
         const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ||
            (err as Error)?.message ||
            'Failed to remove device';
         toast.error(msg);
         setExitState('idle');
      }
   };

   const handleDelete = async (cascade = false) => {
      if (deleteState !== (cascade ? 'cascade-confirming' : 'confirming')) return;
      setDeleteState('deleting');
      try {
         const res = await deleteDeviceApi(data._id, { cascade });
         if (res.routerCleanupError) {
            // Mongo delete succeeded but router cleanup never ran (router
            // unreachable, auth failed, etc.). Warn the operator so they
            // know orphan VLAN/hotspot config is sitting on the router.
            toast.warn(
               `Device "${displayName}" deleted, but router cleanup failed: ${res.routerCleanupError}. Re-apply the router config when it's back online to clear orphan state.`,
               { autoClose: 7000 }
            );
         } else if (res.routerCleanup && res.routerCleanup.failed.length > 0) {
            const fails = res.routerCleanup.failed.length;
            toast.warn(
               `Device "${displayName}" deleted. Removed ${res.routerCleanup.removed} resources, but ${fails} failed — check server logs.`,
               { autoClose: 5000 }
            );
         } else {
            toast.success(
               cascade && blockingSubs.length
                  ? `Device "${displayName}" and ${blockingSubs.length} sub-vendo${blockingSubs.length === 1 ? '' : 's'} deleted`
                  : `Device "${displayName}" deleted`,
               { autoClose: 2000 }
            );
         }
         await refetchDevices();
         queryClient.invalidateQueries({ queryKey: ['admin', 'vendo'] });
         handleClose();
      } catch (err: unknown) {
         const resp = (
            err as {
               response?: { status?: number; data?: { error?: string; subs?: BlockingSubVendo[] } };
            }
         )?.response;
         // Attached sub-vendos block the delete. A main whose setup never
         // finished keeps its subs out of the device grid entirely (they
         // only nest under a finished main), so "delete them first" is a
         // dead end — list them here and offer to delete all at once.
         if (!cascade && resp?.status === 409 && resp.data?.subs?.length) {
            setBlockingSubs(resp.data.subs);
            setDeleteState('cascade-confirming');
            return;
         }
         const msg =
            resp?.data?.error || (err as Error)?.message || 'Failed to delete device';
         toast.error(msg);
         setDeleteState('idle');
      }
   };

   // Load existing rate plans for this device. Auto-refetches if the
   // device id changes (modal reused for different devices).
   const { data: serverRatesData } = useQuery({
      queryKey: ['device-rates', data._id],
      queryFn: () => listDeviceRatesApi(data._id),
      enabled: !!data._id,
   });

   useEffect(() => {
      if (!serverRatesData) return;
      const mapped: RatePlan[] = serverRatesData.map((r) => ({
         key: r._id,
         _id: r._id,
         price: String(r.price),
         duration: String(r.duration),
         label: r.label,
         description: r.description ?? '',
         type: r.type === 'voucher' ? 'voucher' : 'coin',
         // ONE place for pause validity: the rate row. A rate saved blank is
         // shown with the value it is already using (the vendo's old default,
         // 1 day unless changed) and saved that way on the next Save — no
         // hidden fallback for operators to hunt for (Ray, 2026-09-14).
         validityMinutes:
            r.validityMinutes ?? (apiDevice?.settings?.maxPauseDuration ?? 1440),
      }));
      setRates(mapped);
      setServerRates(mapped);
   }, [serverRatesData]);

   // Hydrate the form from the device's persisted settings. Default
   // values are only relevant for the brief render before apiDevice is
   // in the store; once it's available, mirror every field so operators
   // see and edit real values.
   //
   // Keyed on the settings' CONTENT, not the object. The store and the fleet
   // query hand this modal a new `settings` object on every poll, and keyed on
   // that reference this effect re-seeded the form each time — an untick came
   // back a few seconds later, until Save (Ray, 2026-09-28). A real change
   // (Save, or another admin's edit) still lands, because its content differs.
   const settingsContent = JSON.stringify(apiDevice?.settings ?? null);
   useEffect(() => {
      const s = apiDevice?.settings;
      if (!s) return;
      setForm((prev) => ({
         ...prev,
         bandwidthDown: String(s.defaultDownload ?? prev.bandwidthDown),
         bandwidthUp: String(s.defaultUpload ?? prev.bandwidthUp),
         allowPause: (s as any).pauseEnabled ?? prev.allowPause,
         // Absent on a vendo saved before this setting existed → ON. Carry-over
         // already works today; defaulting to off would silently remove it.
         allowRoaming: (s as any).roamingEnabled ?? prev.allowRoaming,
         // 🔴 The server stores this as maxPauseDuration. From 2026-09-09 to
         // 2026-09-13 the form saved it under a name the server does not know
         // (see __tests__/defaultValiditySaveName), so it was dropped and every
         // vendo stayed on the 24 h default ("pause validity always 24 hours").
         defaultValidity: String((s as any).maxPauseDuration ?? prev.defaultValidity),
         ratePerPeso: String(s.ratePerPeso ?? prev.ratePerPeso),
         blockTethering: !(s.allowTethering ?? false),
         // Never saved: ON for an OpenWrt router, off for a MikroTik (server
         // readRouterSecurity, Ray 2026-10-23).
         antiWorm: (s as any).antiWorm ?? apiDevice?.router?.platform === 'openwrt',
         antiTorrent: (s as any).antiTorrent ?? apiDevice?.router?.platform === 'openwrt',
         antiDetect: (s as any).antiDetect === true,
         portalMode: (s as any).portalMode ?? prev.portalMode,
         // Absent on a vendo saved before these existed → ON.
         showVoucherRates: (s as any).showVoucherRates !== false,
         showGame: (s as any).showGame !== false,
         showRates: (s as any).showRates !== false,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [settingsContent]);

   const openPreview = (key: PortalThemeKey) => {
      // Use the real device's portalSlug so the preview fetches real
      // rates/device data. Portal falls back to mock content if the
      // server returns nothing, and always shows a preview banner.
      openPortalPreview(data.portalSlug, key);
   };

   // Picking the Customizable card IS the request to customize — open the
   // drawer in the same click. The old "Advanced options" button sat under
   // the whole theme grid, so on a phone it landed off-screen and most
   // operators never found it. Tapping the card again re-opens the drawer,
   // which is the way back after closing it. Only fires on a real click, so
   // a device already saved as 'custom' does NOT pop the drawer on open.
   const selectTheme = (key: PortalThemeKey) => {
      setTheme(key);
      if (key === 'custom') setAdvancedOpen(true);
   };

   const update = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
   };

   // Persist the customizable-theme config straight from the Advanced drawer
   // (selects the 'custom' theme at the same time). Lets the operator save +
   // preview without leaving the drawer.
   const saveCustomConfig = async () => {
      setSavingConfig(true);
      try {
         await updateDevice(data._id, { portalTheme: 'custom', portalConfig });
         setThemeSavedAt(Date.now());
         setRouterSavedAt(Date.now());
         setTheme('custom');
         await refetchDevices();
         toast.success('Portal customization saved', { autoClose: 2000 });
      } catch (err: any) {
         toast.error(err?.response?.data?.error ?? 'Failed to save customization');
      } finally {
         setSavingConfig(false);
      }
   };

   // addRate takes the type so the new draft lands on whichever sub-tab
   // (Coin or Voucher) the operator is currently looking at.
   const addRate = (type: 'coin' | 'voucher'): string => {
      // Returns the draft's key so the mobile rates view can open the
      // new row in edit mode immediately.
      const key = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setRates((prev) => [
         ...prev,
         {
            key,
            price: '',
            duration: '',
            label: '',
            description: '',
            type,
            validityMinutes: 1440, // 1 day (Ray, 2026-09-14)
         },
      ]);
      return key;
   };

   const updateRate = <K extends keyof RatePlan>(key: string, field: K, value: RatePlan[K]) => {
      setRates((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
   };

   const removeRate = (key: string) => {
      setRates((prev) => prev.filter((r) => r.key !== key));
   };

   const startEditName = () => {
      setNameDraft(displayName);
      setNameError(null);
      setEditingName(true);
   };

   const cancelEditName = () => {
      setEditingName(false);
      setNameError(null);
   };

   const commitEditName = async () => {
      const trimmed = nameDraft.trim();
      if (!trimmed) {
         setNameError('Name is required');
         return;
      }
      if (trimmed.length > 50) {
         setNameError('Name must be 50 characters or fewer');
         return;
      }
      if (trimmed === displayName) {
         cancelEditName();
         return;
      }
      const prevName = displayName;
      setDisplayName(trimmed);
      patchDeviceNameInStore(data._id, trimmed);
      setEditingName(false);
      setNameError(null);
      try {
         await updateDevice(data._id, { name: trimmed });
      } catch (err: any) {
         setDisplayName(prevName);
         patchDeviceNameInStore(data._id, prevName);
         const msg = err?.response?.data?.error ?? err?.message ?? 'Failed to rename device';
         toast.error(msg);
      }
   };

   const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         void commitEditName();
      } else if (e.key === 'Escape') {
         e.preventDefault();
         cancelEditName();
      }
   };

   const handleSave = async () => {
      if (isLoading) return;
      setIsLoading(true);
      try {
         // Validate rate rows before any server work — surface inline
         // errors via toast rather than partial-saving.
         const cleaned: RatePlan[] = [];
         for (const r of rates) {
            const price = Number(r.price);
            const duration = Number(r.duration);
            if (Number.isNaN(price) || price < 0) {
               throw new Error(`Rate "${r.label || '(no label)'}" has invalid price`);
            }
            if (Number.isNaN(duration) || duration < 1) {
               throw new Error(
                  `Rate "${r.label || '(no label)'}" needs a duration of at least 1 minute`
               );
            }
            if (!r.label.trim()) {
               throw new Error(`Rate at ₱${r.price || '?'} is missing a label`);
            }
            cleaned.push({ ...r, label: r.label.trim() });
         }

         // Diff against the server snapshot we captured at load time:
         //  - rows with _id missing locally → DELETE
         //  - rows with _id locally but changed fields → PATCH
         //  - rows without _id → POST (drafts)
         const localById = new Map(cleaned.filter((r) => r._id).map((r) => [r._id!, r]));
         const deletions = serverRates
            .filter((sr) => sr._id && !localById.has(sr._id))
            .map((sr) => deleteDeviceRateApi(sr._id!));

         const patches = cleaned
            .filter((r) => r._id)
            .filter((r) => {
               const orig = serverRates.find((sr) => sr._id === r._id);
               if (!orig) return false;
               return (
                  String(orig.price) !== r.price ||
                  String(orig.duration) !== r.duration ||
                  orig.label !== r.label ||
                  (orig.description ?? '') !== (r.description ?? '') ||
                  orig.type !== r.type ||
                  (orig.validityMinutes ?? null) !== (r.validityMinutes ?? null)
               );
            })
            .map((r) =>
               updateDeviceRateApi(r._id!, {
                  price: Number(r.price),
                  duration: Number(r.duration),
                  label: r.label,
                  description: r.description,
                  type: r.type,
                  validityMinutes: r.validityMinutes,
               })
            );

         const creates = cleaned
            .filter((r) => !r._id)
            .map((r) =>
               createDeviceRateApi(data._id, {
                  price: Number(r.price),
                  duration: Number(r.duration),
                  label: r.label,
                  description: r.description,
                  type: r.type,
                  validityMinutes: r.validityMinutes,
               })
            );

         await Promise.all([...deletions, ...patches, ...creates]);
         if (deletions.length || patches.length || creates.length) setRouterSavedAt(Date.now());

         // Invalidate the rates query so the modal's sub-tabs show the
         // newly-saved rates immediately. Without this, the local `rates`
         // state still reflects pre-save drafts (no _id on new rows) and
         // a follow-up save would re-attempt the same creates — the
         // server's unique-index then rejects with "already exists".
         if (deletions.length || patches.length || creates.length) {
            queryClient.invalidateQueries({ queryKey: ['device-rates', data._id] });
         }

         // Theme save. For the customizable theme, always send the config
         // alongside (the operator may have edited it without changing the
         // theme key). For preset themes, only patch when the key changed.
         if (theme === 'custom') {
            await updateDevice(data._id, { portalTheme: 'custom', portalConfig });
         setThemeSavedAt(Date.now());
         setRouterSavedAt(Date.now());
         } else if (theme !== data.portalTheme) {
            await updateDevice(data._id, { portalTheme: theme });
         setThemeSavedAt(Date.now());
         setRouterSavedAt(Date.now());
         }

         // Persist Session/Network + ratePerPeso settings. Send the full
         // settings sub-doc preserving every existing field — server's
         // $set replaces nested objects wholesale, so partial sends would
         // wipe siblings.
         if (apiDevice?.settings) {
            const newSettings = {
               ...apiDevice.settings,
               defaultDownload: Number(form.bandwidthDown) || apiDevice.settings.defaultDownload,
               defaultUpload: Number(form.bandwidthUp) || apiDevice.settings.defaultUpload,
               pauseEnabled: form.allowPause,
               roamingEnabled: form.allowRoaming,
               maxPauseDuration: Number(form.defaultValidity) >= 0
                  ? Number(form.defaultValidity)
                  : apiDevice.settings.maxPauseDuration,
               ratePerPeso: Number(form.ratePerPeso) > 0
                  ? Number(form.ratePerPeso)
                  : apiDevice.settings.ratePerPeso,
               allowTethering: !form.blockTethering,
               // Track F: router-wide — only a main vendo carries them.
               ...(apiDevice.parent
                  ? {}
                  : { antiWorm: form.antiWorm, antiTorrent: form.antiTorrent, antiDetect: form.antiDetect }),
               portalMode: form.portalMode,
               // Both → the voucher screen is always there; Coin only → never;
               // only Voucher only keeps the operator's own choice.
               showVoucherRates:
                  form.portalMode === 'both'
                     ? true
                     : form.portalMode === 'coin'
                     ? false
                     : form.showVoucherRates,
               showGame: form.showGame,
               showRates: form.showRates,
            };
            // Field-by-field comparison — only patch when something changed.
            const changed = (Object.keys(newSettings) as (keyof typeof newSettings)[])
               .some((k) => newSettings[k] !== apiDevice.settings[k]);
            if (changed) {
               await updateDevice(data._id, { settings: newSettings });
            }
         }

         await refetchDevices();
         // Admin fleet table (if that's where we were opened from) reads
         // its own query cache — refresh it so the row reflects the save.
         queryClient.invalidateQueries({ queryKey: ['admin', 'vendo'] });
         toast.success(`Settings saved for ${displayName}`, { autoClose: 2000 });
         // Modal stays open after save so operator can keep editing or
         // jump to another tab. Closing was annoying when iterating —
         // had to reopen the modal to verify the save took.
         // handleClose();
      } catch (err: any) {
         const msg = err?.response?.data?.error ?? err?.message ?? 'Failed to save settings';
         toast.error(msg);
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Wrapper>
         <Summary>
            {editingName ? (
               <NameEditWrap>
                  <NameInput
                     autoFocus
                     value={nameDraft}
                     $invalid={!!nameError}
                     onChange={(e) => {
                        setNameDraft(e.target.value);
                        if (nameError) setNameError(null);
                     }}
                     onKeyDown={handleNameKeyDown}
                     onBlur={() => void commitEditName()}
                     maxLength={50}
                     aria-label="Device name"
                  />
                  {nameError && <NameErrorText>{nameError}</NameErrorText>}
               </NameEditWrap>
            ) : (
               <NameDisplayWrap
                  onClick={startEditName}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        startEditName();
                     }
                  }}
                  title="Click to rename"
               >
                  <DeviceTitle>{displayName}</DeviceTitle>
                  <EditPencil aria-hidden viewBox="0 0 24 24">
                     <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                  </EditPencil>
               </NameDisplayWrap>
            )}
            <DeviceMeta>
               <MetaId>{data.id}</MetaId>
               <TypePill $main={data.type === 'main'}>
                  {data.type === 'main' ? 'Main' : 'Sub'}
               </TypePill>
               <DeviceStatusBadge status={data.status} routerStatus={data.routerStatus} />
               {apiDevice?.router?.firmwareVersion && (
                  <VersionPill
                     title={
                        effectiveRouterVersion(apiDevice.router.firmwareVersion, (apiDevice.router as any).bundleVersion).detail ??
                        'Router config version'
                     }
                  >
                     Router {effectiveRouterVersion(apiDevice.router.firmwareVersion, (apiDevice.router as any).bundleVersion).shown}
                  </VersionPill>
               )}
               {apiDevice?.nodeMcu?.firmwareVersion && (
                  <VersionPill title="NodeMCU firmware">
                     NodeMCU {String(apiDevice.nodeMcu.firmwareVersion).split('-')[0]}
                  </VersionPill>
               )}
            </DeviceMeta>
         </Summary>

         {soldByMe && (
            <SoldBanner>
               You sold this vendo{soldToEmail ? <> to <strong>{soldToEmail}</strong></> : ''}.
               You can still set it up and keep it updated — but it belongs to
               them now, so you cannot transfer, delete or reset it.
            </SoldBanner>
         )}

         {rogueDhcp && <RogueDhcpBanner>{rogueDhcp}</RogueDhcpBanner>}

         <TabBar>
            {([
               { key: 'rates', label: 'Rate Plans', count: rates.length },
               { key: 'session', label: 'Session & Network' },
               { key: 'hardware', label: 'Hardware' },
               ...(showUpdatesTab
                  ? [{ key: 'updates', label: 'Updates', count: outdated || undefined }]
                  : []),
               // Lite main vendos only: a MikroTik's uplink comes from the setup
               // script, and a sub-vendo shares the main's router.
               ...(showWanTab ? [{ key: 'wan', label: 'Internet (WAN)' }] : []),
               { key: 'theme', label: 'Portal Theme' },
               // Not for a vendo you sold — moving it on again is the new
               // owner's to do, and nobody else's.
               ...(soldByMe ? [] : [{ key: 'ownership', label: 'Ownership' }]),
               // LAST on purpose (Ray, 2026-08-11). It is not a place an
               // operator goes to do something — it is where they go to find
               // out what happened, which is a different errand and belongs at
               // the end of the row. Openwrt-only: a MikroTik reports none of
               // this and an empty tab is worse than no tab.
               // ONLY once the box has actually reported. On the day this
               // shipped, 267 of 269 boxes had no report — an empty tab on
               // nearly every device is worse than no tab, and it would train
               // operators to ignore the one place that will matter. It appears
               // for each of them the moment their box gets the firmware.
               ...((isOpenwrt || isMikrotikHybridMain) && isMainVendo && !!apiDevice?.router?.diag
                  ? [{ key: 'connection', label: 'Connection', dot: connectionDot }]
                  : []),
            ] as { key: SettingsTab; label: string; count?: number; dot?: 'ok' | 'bad' | 'unknown' }[]).map((t) => (
               <TabItem
                  key={t.key}
                  $active={tab === t.key}
                  onClick={() => setTab(t.key)}
               >
                  {t.dot && <TabArrow state={t.dot} />}
                  {t.label}
                  {t.count !== undefined && <TabCount>{t.count}</TabCount>}
               </TabItem>
            ))}
         </TabBar>

         <TabBody>
            {tab === 'rates' && (
               <RatesTabPanel
                  apiDevice={apiDevice}
                  deviceId={data._id}
                  rates={rates}
                  updateRate={updateRate}
                  removeRate={removeRate}
                  addRate={addRate}
                  ratePerPeso={form.ratePerPeso}
                  setRatePerPeso={(v) => update('ratePerPeso', v)}
               />
            )}

            {tab === 'session' && (
               <Panel>
                  <Section>
                     <SectionTitle>Captive portal tabs</SectionTitle>
                     <PortalModeRow>
                        {(
                           [
                              {
                                 v: 'both',
                                 label: 'Both',
                                 desc: 'Coin + Voucher tabs visible',
                                 icon: (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                       <circle cx="8" cy="12" r="5" />
                                       <rect x="13" y="7" width="9" height="10" rx="1.5" />
                                    </svg>
                                 ),
                              },
                              {
                                 v: 'coin',
                                 label: 'Coin only',
                                 desc: 'Hide Voucher tab',
                                 icon: (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                       <circle cx="12" cy="12" r="9" />
                                       <path d="M9 12h6" />
                                       <path d="M12 9v6" />
                                    </svg>
                                 ),
                              },
                              {
                                 v: 'voucher',
                                 label: 'Voucher only',
                                 desc: 'Hide Coin tab — for hotels/resorts without a coin acceptor',
                                 icon: (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                       <rect x="2" y="6" width="20" height="12" rx="2" />
                                       <path d="M7 10h10" />
                                       <path d="M7 14h6" />
                                    </svg>
                                 ),
                              },
                           ] as const
                        ).map((opt) => (
                           <PortalModeOpt
                              key={opt.v}
                              $active={form.portalMode === opt.v}
                              type="button"
                              onClick={() => update('portalMode', opt.v)}
                           >
                              {form.portalMode === opt.v && (
                                 <PortalModeCheck>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                       <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                 </PortalModeCheck>
                              )}
                              <PortalModeIcon $active={form.portalMode === opt.v}>
                                 {opt.icon}
                              </PortalModeIcon>
                              <PortalModeLbl>{opt.label}</PortalModeLbl>
                              <PortalModeDesc>{opt.desc}</PortalModeDesc>
                           </PortalModeOpt>
                        ))}
                     </PortalModeRow>
                     {/* "Show voucher screen" is a Voucher-only choice. Both
                         always has the screen (its Voucher tab IS the screen),
                         Coin only never does — so the box only shows for
                         Voucher only, and the saved value follows the mode
                         (Ray, 2026-09-15). */}
                     {form.portalMode === 'voucher' && (
                     <ToggleRow style={{ marginTop: 14 }}>
                        <ToggleLabel>
                           Show voucher plans
                           <HelperText style={{ margin: '2px 0 0' }}>
                              Adds a "Vouchers" button on the portal that opens your voucher
                              plans. Off = no button; customers type their code on the home
                              screen.
                           </HelperText>
                        </ToggleLabel>
                        <ToggleInput
                           type="checkbox"
                           checked={form.showVoucherRates}
                           onChange={(e) => update('showVoucherRates', e.target.checked)}
                        />
                     </ToggleRow>
                     )}
                     {/* "View Rates" (Ray, 2026-10-28, operator ask): some shops
                         do not want prices on the portal. Off hides the Wi-Fi
                         rate list and its button on every layout; coins and
                         vouchers keep working. */}
                     <ToggleRow>
                        <ToggleLabel>
                           View Rates
                           <HelperText style={{ margin: '2px 0 0' }}>
                              Customers can open the Wi-Fi rate list. Off = no list,
                              no button; coins still work.
                           </HelperText>
                        </ToggleLabel>
                        <ToggleInput
                           type="checkbox"
                           checked={form.showRates}
                           onChange={(e) => update('showRates', e.target.checked)}
                        />
                     </ToggleRow>
                     <ToggleRow>
                        <ToggleLabel>
                           Show "Play game"
                           <HelperText style={{ margin: '2px 0 0' }}>
                              The small game customers can play while waiting.
                           </HelperText>
                        </ToggleLabel>
                        <ToggleInput
                           type="checkbox"
                           checked={form.showGame}
                           onChange={(e) => update('showGame', e.target.checked)}
                        />
                     </ToggleRow>
                  </Section>

                  <Section>
                     <SectionTitle>Bandwidth (Mbps)</SectionTitle>
                     <InlineRow>
                        <InlineField>
                           <Lbl>Download</Lbl>
                           <CompactInput
                              type="number"
                              $w="80px"
                              value={form.bandwidthDown}
                              placeholder="5"
                              onChange={(e) => update('bandwidthDown', e.target.value)}
                           />
                        </InlineField>
                        <InlineField>
                           <Lbl>Upload</Lbl>
                           <CompactInput
                              type="number"
                              $w="80px"
                              value={form.bandwidthUp}
                              placeholder="5"
                              onChange={(e) => update('bandwidthUp', e.target.value)}
                           />
                        </InlineField>
                     </InlineRow>
                  </Section>

                  <Section>
                     <SectionTitle>Pause / Resume</SectionTitle>
                     <HelperText>
                        Lets customers pause their time and come back later. How
                        long paused time is kept is set per rate in the{' '}
                        <b>Rate Plans</b> tab.
                     </HelperText>
                     <ToggleRow>
                        <ToggleLabel>Allow customer pause</ToggleLabel>
                        <ToggleInput
                           type="checkbox"
                           checked={form.allowPause}
                           onChange={(e) => update('allowPause', e.target.checked)}
                        />
                     </ToggleRow>
                     {/* Unticked also stops the walk-away auto-pause, by design
                         (the sweeps obey pauseEnabled). khzwn's LaDo, 2026-09-28:
                         he unticked it and then asked why walk-aways stopped
                         pausing — the box has to say so. */}
                     <HelperText>
                        Also turns off auto-pause when a customer walks away.
                     </HelperText>
                     {/* How long paused time is kept is set in Rate Plans, next
                         to the rates it governs — two screens for one rule
                         confused operators (Ray, 2026-09-14). */}
                  </Section>

                  {/* OpenWRT only. MikroTik vendos are deliberately fully
                      centralised and have no such setting — showing an
                      inoperative switch there would imply control that does not
                      exist. (Ray, 2026-08-14) */}
                  {apiDevice?.router?.platform === 'openwrt' && (
                     <Section>
                        <SectionTitle>Roaming</SectionTitle>
                        <HelperText>
                           Lets a customer who bought time — or who has{' '}
                           <b>Unlimited</b> — at another of your vendos keep it
                           when they come here. Switch it off and everyone pays
                           at this vendo. It never affects your own sub-vendos,
                           and never cuts someone who is already online.
                        </HelperText>
                        <ToggleRow>
                           <ToggleLabel>Accept customers from my other vendos</ToggleLabel>
                           <ToggleInput
                              type="checkbox"
                              checked={form.allowRoaming}
                              onChange={(e) => update('allowRoaming', e.target.checked)}
                           />
                        </ToggleRow>
                     </Section>
                  )}

                  <Section>
                     <SectionTitle>Tethering</SectionTitle>
                     <ToggleRow>
                        <ToggleLabel>Block tethering (recommended)</ToggleLabel>
                        {/* OpenWrt (Ruijie, OrangePi, …) blocks tethering in the
                            image itself, always - the switch did nothing there and
                            read as if it did (Ray 2026-10-23). Shown ticked and fixed. */}
                        <ToggleInput
                           type="checkbox"
                           aria-label="Block tethering"
                           checked={apiDevice?.router?.platform === 'openwrt' ? true : form.blockTethering}
                           disabled={apiDevice?.router?.platform === 'openwrt'}
                           onChange={(e) => update('blockTethering', e.target.checked)}
                        />
                     </ToggleRow>
                     {apiDevice?.router?.platform === 'openwrt' ? (
                        <HelperText>Always on for this router — tethering is blocked by the router itself.</HelperText>
                     ) : (
                        <HelperText>
                           Drops packets with TTL=63 (the signature of tethered traffic)
                           via MikroTik mangle + filter rules. Catches casual tethering
                           on the ~90% of phones that use the OS default. Toggle off if
                           customers report false-positive blocks (e.g. Cloudflare WARP
                           or VPN users). Saves apply live — no router reboot needed.
                        </HelperText>
                     )}
                  </Section>

                  {!apiDevice?.parent && (
                     <Section>
                        <SectionTitle>Security</SectionTitle>
                        <ToggleRow>
                           <ToggleLabel>Block virus spread between customers</ToggleLabel>
                           <ToggleInput
                              id="sec-worm"
                              aria-label="Block virus spread between customers"
                              type="checkbox"
                              checked={form.antiWorm}
                              onChange={(e) => update('antiWorm', e.target.checked)}
                           />
                        </ToggleRow>
                        <HelperText>
                           Stops one customer's phone or laptop from attacking another (file-sharing
                           ports used by viruses). Customers on the same WiFi can no longer see each
                           other — a phone casting to a TV on your WiFi would stop working.
                        </HelperText>
                        <ToggleRow>
                           <ToggleLabel>Block torrent downloads</ToggleLabel>
                           <ToggleInput
                              id="sec-torrent"
                              aria-label="Block torrent downloads"
                              type="checkbox"
                              checked={form.antiTorrent}
                              onChange={(e) => update('antiTorrent', e.target.checked)}
                           />
                        </ToggleRow>
                        <HelperText>
                           One customer downloading torrents can slow the WiFi for everyone.
                           Websites, videos and games keep working.
                        </HelperText>
                        <ToggleRow>
                           <ToggleLabel>Hide that WiFi is shared (anti-detect)</ToggleLabel>
                           <ToggleInput
                              id="sec-detect"
                              aria-label="Hide that WiFi is shared (anti-detect)"
                              type="checkbox"
                              checked={form.antiDetect}
                              onChange={(e) => update('antiDetect', e.target.checked)}
                           />
                        </ToggleRow>
                        <HelperText>
                           Makes your internet line look like one device to your ISP, and the router
                           stops answering pings from the internet. Turn on only if your ISP limits
                           shared connections.
                        </HelperText>
                     </Section>
                  )}

                  <WebsiteBlockingSection deviceId={data._id} />

                  {/* Antenna / radio IPs (2026-10-04) — main vendo only: a
                      sub-vendo shares its main's router. */}
                  {isMainVendo && (
                     <AntennaIpsSection
                        deviceId={data._id}
                        vlans={apiDevice?.router?.vlans ?? []}
                     />
                  )}
               </Panel>
            )}

            {tab === 'updates' && (
               <Panel>
                  <PanelHint>
                     Keep this vendo's software current. Updates come from
                     SmartFi — you only choose <em>when</em> to apply them.
                     A firmware update briefly restarts the router.
                  </PanelHint>
                  <UpdatesTab
                     deviceId={data._id}
                     routerStatus={data.routerStatus}
                     variant={apiDevice?.router?.variant === 'hybrid' ? 'hybrid' : 'cloud'}
                     platform={apiDevice?.router?.platform}
                     isSubVendo={!isMainVendo}
                     // Revert is admin only and the SERVER enforces it; hiding
                     // the control just avoids offering a button that 403s.
                     canRevert={isPlatformAdmin}
                  />
               </Panel>
            )}

            {tab === 'connection' && (
               <Panel>
                  <PanelHint>
                     {/* "this box" — the one word an operator cannot resolve.
                         It is the ROUTER that keeps this record, not the coin
                         box, and the two fail for completely different reasons. */}
                     What this router says happened to it. It records changes to
                     its own connection as they happen, keeps them through a
                     power cut, and sends them on the ordinary internet — the one
                     path that keeps working when the SmartFi tunnel is down.
                  </PanelHint>
                  <ConnectionHistory
                     diag={apiDevice?.router?.diag}
                     lastSeenAt={apiDevice?.router?.lastSeenAt}
                     nodeMcu={apiDevice?.nodeMcu}
                     apBssids={apiDevice?.router?.apBssids}
                     roadHistory={roadHistory?.events}
                  />
               </Panel>
            )}

            {tab === 'wan' && (
               <Panel>
                  {(isOpenwrt || isMikrotikHybridMain) && (
                     <WanTab
                        deviceId={data._id}
                        wan={apiDevice?.router?.wan}
                        routerStatus={data.routerStatus}
                        onRefresh={refetchDevices}
                        diag={apiDevice?.router?.diag}
                        lastSeenAt={apiDevice?.router?.lastSeenAt}
                        wanTraffic={apiDevice?.router?.wanTraffic}
                     />
                  )}
                  {/* Customer side of the same port: tagged customers (5V5 preset,
                      managed switch) join the main LAN — tracker A8, 2026-10-27. */}
                  {isOpenwrt && customerVlanCapable(apiDevice?.router?.firmwareVersion) && (
                     <CustomerVlanCard
                        deviceId={data._id}
                        lanIsolation={apiDevice?.router?.lanIsolation}
                        mainVlanId={apiDevice?.router?.mainVlanId}
                        mainSubnet={apiDevice?.router?.mainSubnet}
                        wanVlanId={apiDevice?.router?.wan?.vlanId}
                        zoneIds={(apiDevice?.router?.vlans ?? []).map((z) => z.vlanId)}
                        mainVlanLiveAt={apiDevice?.router?.mainVlanLiveAt ?? null}
                        trunkIface={apiDevice?.router?.vlanTrunkIface}
                        wanDev={apiDevice?.router?.wanDev}
                        onRefresh={refetchDevices}
                     />
                  )}
                  {/* MikroTik: the main vendo rides the tag on the Trunk ports, no
                      sub-vendo needed (A51, Ray 2026-10-29). */}
                  {isMikrotik && (
                     <CustomerVlanCard
                        deviceId={data._id}
                        lanIsolation={apiDevice?.router?.lanIsolation}
                        mainVlanId={apiDevice?.router?.mainVlanId}
                        mainSubnet={apiDevice?.router?.mainSubnet}
                        zoneIds={(apiDevice?.router?.vlans ?? []).map((z) => z.vlanId)}
                        mainVlanLiveAt={apiDevice?.router?.mainVlanLiveAt ?? null}
                        onRefresh={refetchDevices}
                        mikrotik={{
                           trunkPorts: mikrotikPortRoles(apiDevice?.router as never)
                              .filter((x) => x.role === 'trunk')
                              .map((x) => x.port),
                           liveConfig: apiDevice?.router?.mainVlanLiveConfig,
                           hasSubs: (apiDevice?.router?.vlans ?? []).length > 0,
                        }}
                     />
                  )}
               </Panel>
            )}

            {tab === 'theme' && (
               <Panel>
                  <PanelHint>
                     The portal is <strong>cloud-hosted</strong> (served from SmartFi cloud, not the local
                     router), so the theme applies to every customer that connects to this vendo's WiFi.
                     Preview opens the portal in a new tab with <code>?theme=</code> query param.
                  </PanelHint>
                  <ThemeGrid>
                     {PORTAL_THEMES.map((t) => {
                        const selected = theme === t.key;
                        return (
                           <ThemeCard
                              key={t.key}
                              $selected={selected}
                              $accent={t.accent}
                              $special={t.key === 'custom'}
                              onClick={() => selectTheme(t.key)}
                           >
                              {t.key === 'custom' ? (
                                 <CustomSwatch />
                              ) : (
                              <ThemeSwatch $pageBg={t.pageBg}>
                                 <SwatchHeader $color={t.title}>{t.name}</SwatchHeader>
                                 <SwatchCard $bg={t.card} $border={t.accent}>
                                    <SwatchDot $color={t.accent} />
                                    <SwatchLine $color={t.title} />
                                    <SwatchLineShort $color={t.title} />
                                 </SwatchCard>
                                 <SwatchBtn $color={t.accent} />
                              </ThemeSwatch>
                              )}
                              <ThemeMeta>
                                 <ThemeName>{t.name}</ThemeName>
                                 <ThemeDesc>{t.description}</ThemeDesc>
                              </ThemeMeta>
                              <ThemeFooter>
                                 {selected ? (
                                    <SelectedPill>✓ Selected</SelectedPill>
                                 ) : (
                                    <SelectPlaceholder />
                                 )}
                                 <PreviewLink
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       openPreview(t.key);
                                    }}
                                 >
                                    ↗ Preview
                                 </PreviewLink>
                              </ThemeFooter>
                           </ThemeCard>
                        );
                     })}
                  </ThemeGrid>
                  {theme === 'custom' && (
                     <CustomCallout>
                        <div>
                           <CustomCalloutTitle>
                              ⚙ Customizable theme selected
                           </CustomCalloutTitle>
                           <CustomCalloutSub>
                              The customization options open when you pick this
                              theme — layout, background, image slider, sounds
                              and effects. Tap the{' '}
                              <strong>Customizable</strong> card again to
                              reopen them.
                           </CustomCalloutSub>
                        </div>
                     </CustomCallout>
                  )}
               </Panel>
            )}

            {tab === 'ownership' && (
               <Panel>
                  <OwnershipTab
                     device={data}
                     isSubVendo={!isMainVendo}
                     mainName={parentName}
                     subCount={subCount}
                     onOpenSell={() => {
                        handleClose();
                        setTimeout(
                           () =>
                              handleShow({
                                 template: 'sell-device',
                                 title: 'Transfer Device',
                                 data: { device: data },
                              }),
                           50
                        );
                     }}
                  />
               </Panel>
            )}

            {tab === 'hardware' && (
               <Panel>
                  {/* FIRST on the tab, deliberately (Ray, 2026-08-10). This is
                      what an operator opens Hardware to do, and when it sat
                      below the NodeMCU they met the NodeMCU's join field
                      first and renamed the wrong thing. Put the answer where
                      the question lands.

                      Applied on the box's next check-in (needs agent 1.0.12+);
                      the same update writes the NodeMCU's name too. */}
                  {isMainVendo && isOpenwrt && !boardHasNoRadio && (
                     <Section>
                        <SectionTitle>Change WiFi Name (SSID)</SectionTitle>
                        <HelperText>
                           The WiFi name your customers see. The NodeMCU is
                           updated to match automatically, so there is nothing
                           else to set. On Save, the router reloads WiFi within
                           ~30 seconds — <b>no reboot</b>; internet and paid
                           sessions stay up. Connected devices reconnect to the
                           new name, and paying customers keep their remaining
                           time.
                        </HelperText>
                        <SsidRow>
                           <SsidInput
                              value={ssidDraft}
                              maxLength={32}
                              onChange={(e) => setSsidDraft(e.target.value)}
                              placeholder="e.g. Juan WiFi"
                           />
                           <SsidSaveBtn
                              disabled={
                                 savingSsid ||
                                 coinBoxWaiting ||
                                 !ssidDraft.trim() ||
                                 ssidDraft.trim() === currentSsid
                              }
                              onClick={saveSsid}
                           >
                              {savingSsid ? 'Saving…' : 'Save'}
                           </SsidSaveBtn>
                        </SsidRow>
                        {apiDevice && (
                           <SsidRenameProgress
                              device={apiDevice}
                              savedAt={ssidSavedAt}
                              onPoll={refetchDevice}
                           />
                        )}
                        {coinBoxWaiting && (
                           <CoinBoxWaitNote>
                              Waiting for the NodeMCU — it is offline right
                              now. The WiFi name can only change while the
                              NodeMCU is online, so it can learn the new
                              name first and follow the router to it.
                              {coinBoxRescue && coinBoxRescue !== currentSsid && (
                                 <>
                                    {' '}One exception: you can still set it to{' '}
                                    <b>{coinBoxRescue}</b> — the name the coin
                                    box was flashed with — to help it find the
                                    network again.
                                 </>
                              )}
                           </CoinBoxWaitNote>
                        )}
                     </Section>
                  )}

                  {/*
                     Router local login — PLATFORM ADMINS ONLY, and fetched
                     only when asked. The password is not pulled with the rest
                     of the device data on every modal open: it opens a live
                     router, so it should not sit in memory (or in a network
                     log) unless someone actually needs it.
                  */}
                  {isPlatformAdmin && isMikrotik && isMainVendo && (
                     <Section>
                        <SectionTitle>Router local login (admin only)</SectionTitle>
                        <HelperText>
                           Opens this router when the SmartFi tunnel is down.
                           Setup pins every normal login to the tunnel, so
                           without this a dead tunnel means a factory reset is
                           the only way back in.
                           <br />
                           Do not give this to the operator: it lets them read the
                           whole config.
                        </HelperText>
                        {/* WHERE to connect -- it only works from one place, and
                            the old text never said which (Ray, 2026-10-09). */}
                        <RecoveryWhere data-testid="recovery-where">
                           {recoveryOwnerPort ? (
                              <>
                                 1. Plug a laptop into <strong>port {recoveryOwnerPort.replace(/^ether/, '')}</strong> ({recoveryOwnerPort}) of the router.
                                 <br />
                                 2. Open <strong>Winbox</strong> (or SSH) to <strong>192.168.88.1</strong>, by IP, not by MAC.
                              </>
                           ) : (
                              <>
                                 1. Join the router's customer WiFi or plug into a LAN port.
                                 <br />
                                 2. Open <strong>Winbox</strong> (or SSH) to <strong>10.0.0.1</strong>, by IP, not by MAC.
                              </>
                           )}
                           <br />
                           A browser (WebFig) does not open it.
                        </RecoveryWhere>
                        {!recoveryLogin ? (
                           <ReflashBtn
                              onClick={handleShowRecoveryLogin}
                              disabled={recoveryState === 'loading'}
                           >
                              {recoveryState === 'loading'
                                 ? 'Loading…'
                                 : 'Show login'}
                           </ReflashBtn>
                        ) : (
                           <>
                              <Lbl>Username</Lbl>
                              <CompactInput readOnly value={recoveryLogin.username} />
                              <Lbl>Password</Lbl>
                              <CompactInput readOnly value={recoveryLogin.password} />
                              <ReflashBtn
                                 onClick={() => {
                                    navigator.clipboard
                                       ?.writeText(recoveryLogin.password)
                                       .then(() => toast.success('Password copied'))
                                       .catch(() =>
                                          toast.error('Could not copy — select it by hand')
                                       );
                                 }}
                              >
                                 Copy password
                              </ReflashBtn>
                           </>
                        )}
                        {recoveryState === 'missing' && (
                           <HelperText>
                              This router does not have one yet. It is created the
                              next time its setup config is generated or applied.
                           </HelperText>
                        )}
                     </Section>
                  )}

                  {/* Whitelist management moved to the Sessions list
                      (2026-06-12): whitelisted MACs appear there as
                      always-active "Unlimited" rows — Kick removes them.
                      Adding stays in Sessions → +Time → Permanent. */}

                  {/*
                     Dev-only: re-push captive portal pages. Hidden in
                     production — login.html is template-driven and
                     re-pushed automatically on relevant server changes
                     (template edits, slug rename, etc) via background
                     jobs or re-running the wizard. Exposing the button
                     to store owners would let them spam the router REST
                     unnecessarily and confuses the support story.
                  */}
                  {import.meta.env.DEV && (
                     <Section>
                        <SectionTitle>Captive portal pages (dev only)</SectionTitle>
                        <HelperText>
                           Re-push login.html and status.html to the router. Use after
                           editing the templates server-side, or if the existing pushed
                           files are stale (e.g. branding change, slug change). Hidden
                           in production.
                        </HelperText>
                        <ReflashBtn
                           onClick={handlePushCaptivePages}
                           disabled={pushPagesState === 'pushing'}
                        >
                           {pushPagesState === 'pushing'
                              ? 'Pushing…'
                              : pushPagesState === 'pushed'
                              ? 'Pushed ✓'
                              : 'Re-push captive pages'}
                        </ReflashBtn>
                     </Section>
                  )}

                  {/* The OrangePi MAIN reads coins on its own GPIO pins, so it
                      has no NodeMCU section. Its SUB-vendos do have one: each
                      sits on its own VLAN with its own NodeMCU (bench
                      2026-09-24 - the section was hidden for them too). */}
                  {(!isOrangePi || !isMainVendo) && (
                  <Section>
                     <SectionTitle>NodeMCU</SectionTitle>
                     {/* Only offered when the vendo is actually degraded — that
                         is the state this answers (router fine, NodeMCU quiet).
                         Offering it on a healthy vendo invites a check whose only
                         possible reply is "everything is fine". */}
                     {isDegraded && (
                        <InlineRow>
                           <DiagnoseBtn
                              onClick={runDiagnosis}
                              disabled={diagLoading}
                           >
                              {diagLoading
                                 ? 'Checking the router…'
                                 : '⚠️ Why is the NodeMCU degraded? Click this.'}
                           </DiagnoseBtn>
                        </InlineRow>
                     )}
                     {diag && (
                        <DiagBox $good={diag.verdict === 'healthy'}>
                           <WarnTitle>{diag.title}</WarnTitle>
                           <WarnBody>{diag.detail}</WarnBody>
                        </DiagBox>
                     )}

                     {/* The router and the NodeMCU are two separate things, and
                         the words on the card say so — but only if you already
                         know what they mean. Shown only while degraded, folded,
                         so it never shouts at someone whose vendo is fine. */}
                     {isDegraded && (
                        <LegendToggle
                           type="button"
                           aria-expanded={legendOpen}
                           onClick={() => setLegendOpen((v) => !v)}
                        >
                           {legendOpen ? '▾' : '▸'} What degraded means?
                        </LegendToggle>
                     )}
                     {isDegraded && legendOpen && (
                        <Legend>
                           <LegendNote>
                              A vendo has <strong>two</strong> parts that can fail
                              on their own: the <strong>router</strong> (the
                              internet box) and the <strong>NodeMCU</strong>, also
                              called the <strong>NodeMCU</strong> (the board that
                              counts coins). The card shows one word for each.
                           </LegendNote>

                           <LegendGroup>The router</LegendGroup>
                           <LegendRow>
                              <Swatch $tone="good" />
                              <div>
                                 <LegendName>Connected</LegendName>
                                 <LegendWhat>
                                    The router has power and internet, and it is
                                    talking to SmartFi.
                                 </LegendWhat>
                              </div>
                           </LegendRow>
                           <LegendRow>
                              <Swatch $tone="warn" />
                              <div>
                                 <LegendName>Agent down</LegendName>
                                 <LegendWhat>
                                    The router has internet, but the SmartFi program
                                    inside it stopped. Coins still drop, but paying
                                    customers get no internet.{' '}
                                    <strong>Reboot the router.</strong>
                                 </LegendWhat>
                              </div>
                           </LegendRow>
                           <LegendRow>
                              <Swatch $tone="bad" />
                              <div>
                                 <LegendName>Disconnected</LegendName>
                                 <LegendWhat>
                                    The router is not reaching SmartFi at all. Check
                                    its power and its internet cable.
                                 </LegendWhat>
                              </div>
                           </LegendRow>

                           <LegendGroup>The vendo overall</LegendGroup>
                           <LegendRow>
                              <Swatch $tone="good" />
                              <div>
                                 <LegendName>Online</LegendName>
                                 <LegendWhat>
                                    Both parts are working. The router has internet
                                    and the NodeMCU is reporting. Nothing to do.
                                 </LegendWhat>
                              </div>
                           </LegendRow>
                           <LegendRow>
                              <Swatch $tone="warn" />
                              <div>
                                 <LegendName>Degraded</LegendName>
                                 <LegendWhat>
                                    One part is down, usually the NodeMCU. The
                                    router is fine, so existing sessions can still
                                    browse the internet — but the coin slot will
                                    reject coins. This is the one to look at today.
                                 </LegendWhat>
                              </div>
                           </LegendRow>
                           <LegendRow>
                              <Swatch $tone="bad" />
                              <div>
                                 <LegendName>Offline</LegendName>
                                 <LegendWhat>
                                    Both parts are down. Usually the whole place has
                                    no power or no internet.
                                 </LegendWhat>
                              </div>
                           </LegendRow>

                           <LegendNote>
                              <strong>Last seen</strong> is the last time the coin
                              box reported. It only says “just now” when the router
                              and the NodeMCU are both working.
                           </LegendNote>
                        </Legend>
                     )}
                     <HelperText>
                        The NodeMCU's WiFi + pin wiring are baked into the chip on
                        flash. Edit them here (they auto-save), then{' '}
                        <strong>re-flash</strong> to apply.
                        {!coinBoxJoinsOwnRouter && (
                           <>
                              {' '}
                              The WiFi name must be your AP's{' '}
                              <strong>exact</strong> name; leave it blank for a
                              wired NodeMCU.
                           </>
                        )}
                     </HelperText>

                     {/* Explicit, mirroring the setup wizard. Previously this
                         was inferred from a blank SSID box, which stopped being
                         reachable the moment that box was hidden. */}
                     <Lbl>How the NodeMCU connects</Lbl>
                     <ConnTabs>
                        <ConnTab
                           type="button"
                           $active={connMode === 'wifi'}
                           disabled={savingWifi}
                           onClick={() => changeConnMode('wifi')}
                        >
                           Wireless (WiFi)
                        </ConnTab>
                        <ConnTab
                           type="button"
                           $active={connMode === 'ethernet'}
                           disabled={savingWifi}
                           onClick={() => changeConnMode('ethernet')}
                        >
                           Wired (Ethernet)
                        </ConnTab>
                     </ConnTabs>

                     {connMode === 'ethernet' ? (
                        <JoinRow>
                           <JoinLabel>NodeMCU</JoinLabel>
                           <JoinName>Wired into a LAN port</JoinName>
                           <JoinMark $ok>✓ no WiFi name needed</JoinMark>
                        </JoinRow>
                     ) : coinBoxJoinsOwnRouter ? (
                        /* Ruijie: the box joins the router's own WiFi, so there
                           is one correct value and the rename above already
                           writes it. Shown as a fact to check, never as a field
                           to fill — the input and the password were the two most
                           confusing things on this tab (Ray, 2026-08-10). */
                        <JoinRow>
                           <JoinLabel>NodeMCU joins</JoinLabel>
                           <JoinName>{wifiSsid || currentSsid || '—'}</JoinName>
                           {wifiSsid && currentSsid && (
                              <JoinMark $ok={wifiSsid === currentSsid}>
                                 {wifiSsid === currentSsid
                                    ? '✓ same as your WiFi'
                                    : '✕ not your WiFi name'}
                              </JoinMark>
                           )}
                        </JoinRow>
                     ) : (
                        <>
                           <Lbl>
                              WiFi name (SSID) — your AP's exact name
                              <SaveHint>
                                 {savingWifi
                                    ? ' · Saving…'
                                    : wifiSaved
                                      ? ' · Saved ✓'
                                      : ''}
                              </SaveHint>
                           </Lbl>
                           <CompactInput
                              value={wifiSsid}
                              placeholder="e.g. SmartFiWiFi"
                              onChange={(e) => {
                                 setWifiSsid(e.target.value);
                                 setSsidWarn(null);
                              }}
                              onBlur={autoSaveWifi}
                           />
                        </>
                     )}
                     {ssidWarn && (
                        <WarnBox>
                           <WarnTitle>
                              This is not the WiFi name this router broadcasts
                           </WarnTitle>
                           <WarnBody>
                              The router broadcasts <b>{ssidWarn.routerSsid}</b>. You
                              are setting the NodeMCU to join{' '}
                              <b>{ssidWarn.boxSsid}</b>. If that other WiFi is not on
                              this router's network, the NodeMCU will never connect
                              and the vendo will not earn.
                           </WarnBody>
                           <InlineRow>
                              <ReflashBtn
                                 onClick={() => {
                                    const fix = ssidWarn.routerSsid;
                                    setWifiSsid(fix);
                                    setSsidWarn(null);
                                    void saveNodeMcuWifi({
                                       silent: true,
                                       ssidOverride: fix,
                                    });
                                 }}
                                 disabled={savingWifi}
                              >
                                 Use "{ssidWarn.routerSsid}"
                              </ReflashBtn>
                              <ReplaceLink
                                 onClick={() =>
                                    void saveNodeMcuWifi({
                                       silent: true,
                                       allowMismatch: true,
                                    })
                                 }
                              >
                                 Keep it — I use my own access point
                              </ReplaceLink>
                           </InlineRow>
                        </WarnBox>
                     )}
                     {connMode === 'wifi' && !coinBoxJoinsOwnRouter && (
                        <>
                           <Lbl>WiFi password</Lbl>
                           <CompactInput
                              value={wifiPassword}
                              placeholder="leave blank for an open WiFi (most piso setups)"
                              onChange={(e) => setWifiPassword(e.target.value)}
                              onBlur={autoSaveWifi}
                           />
                        </>
                     )}
                     <Lbl style={{ marginTop: 10 }}>Coin-box pins (advanced)</Lbl>
                     <PinMapEditor
                        deviceId={data._id}
                        // Wired vs wireless is the NodeMCU's own connection (W5500
                        // Ethernet vs built-in WiFi), the operator's choice — a
                        // wired NodeMCU can plug into ANY router (Ruijie too). So
                        // Ethernet CS follows connMode, never the router platform.
                        wired={
                           (apiDevice?.nodeMcu as { connMode?: string } | undefined)
                              ?.connMode === 'ethernet'
                        }
                        initialPins={
                           (
                              apiDevice?.nodeMcu as
                                 | {
                                      pins?: {
                                         coinPulse: number | null;
                                         coinPower: number | null;
                                         w5500Cs: number | null;
                                         relay: number | null;
                                      };
                                   }
                                 | undefined
                           )?.pins
                        }
                     />
                     {!showFlasher ? (
                        <>
                           <InlineRow>
                              <ReflashBtn
                                 onClick={async () => {
                                    await saveNodeMcuWifi({ silent: true });
                                    setShowFlasher(true);
                                 }}
                                 disabled={savingWifi}
                              >
                                 Re-flash NodeMCU
                              </ReflashBtn>
                           </InlineRow>
                           {!showReplace ? (
                              <ReplaceLink onClick={() => setShowReplace(true)}>
                                 Replacing a broken or lost chip?
                              </ReplaceLink>
                           ) : (
                              <>
                                 <HelperText>
                                    Use this only if the current NodeMCU is broken,
                                    missing, or won't connect. It writes this
                                    device's identity to a <strong>new</strong> chip;
                                    the old chip then stops working for this device.
                                 </HelperText>
                                 <SwapAckList>
                                    <SwapAckRow>
                                       <ToggleInput
                                          type="checkbox"
                                          checked={swapAcks.brokenOrMissing}
                                          onChange={(e) =>
                                             setSwapAcks((s) => ({
                                                ...s,
                                                brokenOrMissing: e.target.checked,
                                             }))
                                          }
                                       />
                                       <SwapAckLabel>
                                          My current NodeMCU is broken, missing, or won't connect
                                       </SwapAckLabel>
                                    </SwapAckRow>
                                    <SwapAckRow>
                                       <ToggleInput
                                          type="checkbox"
                                          checked={swapAcks.replacementConnected}
                                          onChange={(e) =>
                                             setSwapAcks((s) => ({
                                                ...s,
                                                replacementConnected: e.target.checked,
                                             }))
                                          }
                                       />
                                       <SwapAckLabel>
                                          I have a replacement NodeMCU plugged into this computer via USB
                                       </SwapAckLabel>
                                    </SwapAckRow>
                                    <SwapAckRow>
                                       <ToggleInput
                                          type="checkbox"
                                          checked={swapAcks.understandsOverwrite}
                                          onChange={(e) =>
                                             setSwapAcks((s) => ({
                                                ...s,
                                                understandsOverwrite: e.target.checked,
                                             }))
                                          }
                                       />
                                       <SwapAckLabel>
                                          I understand this writes "{displayName}" identity to the
                                          new chip, and the old chip will stop working for this device
                                       </SwapAckLabel>
                                    </SwapAckRow>
                                 </SwapAckList>
                                 <ReflashBtn
                                    onClick={async () => {
                                       await saveNodeMcuWifi({ silent: true });
                                       setShowFlasher(true);
                                    }}
                                    disabled={!allSwapAcksTicked}
                                 >
                                    Replace NodeMCU
                                 </ReflashBtn>
                              </>
                           )}
                        </>
                     ) : (
                        <NodeMcuFlasher
                           deviceId={data._id}
                           onSuccess={() => {
                              toast.success(
                                 `NodeMCU flashed for ${displayName}. Power-cycle it, then watch Devices for the dot to go green.`,
                                 { autoClose: 5000 }
                              );
                              setShowFlasher(false);
                              setShowReplace(false);
                              setSwapAcks({
                                 brokenOrMissing: false,
                                 replacementConnected: false,
                                 understandsOverwrite: false,
                              });
                           }}
                           onSkip={() => {
                              setShowFlasher(false);
                              setSwapAcks({
                                 brokenOrMissing: false,
                                 replacementConnected: false,
                                 understandsOverwrite: false,
                              });
                           }}
                        />
                     )}
                  </Section>
                  )}

                  {/* OrangePi/SBC: on-board coin reader — the wiring is GPIO, set
                      here and pushed to the box (no reflash). Standard = eAZy
                      defaults (most builds); Custom = remap for a different board. */}
                  {isOrangePi && isMainVendo && (
                     <Section>
                        <SectionTitle>Coin wiring (GPIO pins)</SectionTitle>
                        <PinTabRow>
                           <PinTab
                              type="button"
                              $active={coinMode === 'standard'}
                              onClick={() => setCoinMode('standard')}
                           >
                              Standard
                           </PinTab>
                           <PinTab
                              type="button"
                              $active={coinMode === 'custom'}
                              onClick={() => setCoinMode('custom')}
                           >
                              Custom
                           </PinTab>
                        </PinTabRow>

                        {coinMode === 'standard' ? (
                           <>
                              <HelperText>
                                 Standard <strong>eAZy v3.5</strong> wiring — most
                                 OrangePi builds use this. It's the default; you
                                 only need Custom for a different board.
                              </HelperText>
                              <PinGrid>
                                 <PinCell>
                                    <PinLbl>Coin pin</PinLbl>
                                    <PinVal>PA12</PinVal>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Slot pin</PinLbl>
                                    <PinVal>PA11</PinVal>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Powered level</PinLbl>
                                    <PinVal>HIGH (1)</PinVal>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Cut level</PinLbl>
                                    <PinVal>LOW (0)</PinVal>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Debounce</PinLbl>
                                    <PinVal>50 ms</PinVal>
                                 </PinCell>
                              </PinGrid>
                              {savedCoin && (
                                 <ReflashBtn
                                    disabled={savingCoin}
                                    onClick={resetCoinConfig}
                                    style={{ marginTop: '0.75rem' }}
                                 >
                                    {savingCoin ? 'Saving…' : 'Use standard wiring'}
                                 </ReflashBtn>
                              )}
                           </>
                        ) : (
                           <>
                              <HelperText>
                                 For a different board — pick which GPIO lines your
                                 coin/slot wires use. Blank <strong>Slot pin</strong>{' '}
                                 = no slot control (acceptor stays on). Applies on
                                 the box's next check-in — no reflash.
                              </HelperText>
                              <PinGrid>
                                 <PinCell>
                                    <PinLbl>Coin pin</PinLbl>
                                    <PinSelect
                                       value={coinCfg.gpioLine}
                                       onChange={(e) =>
                                          setCoinCfg({
                                             ...coinCfg,
                                             gpioLine: e.target.value,
                                          })
                                       }
                                    >
                                       <option value="">default (PA12)</option>
                                       {OPI_ONE_PINS.map((p) => (
                                          <option key={p} value={p}>
                                             {p}
                                          </option>
                                       ))}
                                    </PinSelect>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Slot / power pin</PinLbl>
                                    <PinSelect
                                       value={coinCfg.setLine}
                                       onChange={(e) =>
                                          setCoinCfg({
                                             ...coinCfg,
                                             setLine: e.target.value,
                                          })
                                       }
                                    >
                                       <option value="">none (always on)</option>
                                       {OPI_ONE_PINS.map((p) => (
                                          <option key={p} value={p}>
                                             {p}
                                          </option>
                                       ))}
                                    </PinSelect>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Powered level</PinLbl>
                                    <PinSelect
                                       value={coinCfg.setOpen}
                                       onChange={(e) =>
                                          setCoinCfg({
                                             ...coinCfg,
                                             setOpen: e.target.value,
                                          })
                                       }
                                    >
                                       <option value="">default (HIGH)</option>
                                       <option value="1">HIGH (1)</option>
                                       <option value="0">LOW (0)</option>
                                    </PinSelect>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Cut level</PinLbl>
                                    <PinSelect
                                       value={coinCfg.setClosed}
                                       onChange={(e) =>
                                          setCoinCfg({
                                             ...coinCfg,
                                             setClosed: e.target.value,
                                          })
                                       }
                                    >
                                       <option value="">default (LOW)</option>
                                       <option value="1">HIGH (1)</option>
                                       <option value="0">LOW (0)</option>
                                    </PinSelect>
                                 </PinCell>
                                 <PinCell>
                                    <PinLbl>Debounce (ms)</PinLbl>
                                    <CompactInput
                                       $w="100%"
                                       value={coinCfg.debounceMs}
                                       placeholder="50"
                                       onChange={(e) =>
                                          setCoinCfg({
                                             ...coinCfg,
                                             debounceMs: e.target.value,
                                          })
                                       }
                                    />
                                 </PinCell>
                              </PinGrid>
                              <ReflashBtn
                                 disabled={savingCoin}
                                 onClick={saveCoinConfig}
                                 style={{ marginTop: '0.75rem' }}
                              >
                                 {savingCoin ? 'Saving…' : 'Save coin wiring'}
                              </ReflashBtn>
                           </>
                        )}
                     </Section>
                  )}

                  {/* WiFi + pins + the replace path are all in the single
                      NodeMCU section above. */}

                  {/* Router credentials section removed (anti-config-copy):
                      operators are locked out of direct router login, so we no
                      longer surface the smartfi-api creds in the dashboard. */}

                  {/* Reboot Router. The two platforms travel different roads
                      and the difference decides when the button is offered.

                      MikroTik sends /system/reboot over the WG tunnel, so it can
                      only be done while the router is reachable — offering a
                      reboot we cannot deliver would be a lie.

                      A Lite box takes it on its ordinary HEARTBEAT, which keeps
                      working while the tunnel is dead. So it is offered even
                      when the panel says offline — that is precisely when an
                      operator wants it, and it is the case that put a customer's
                      box three hours out of reach on 2026-08-11. It queues: the
                      box takes it on its next check-in, or when it returns. */}
                  {isMainVendo &&
                     (isMikrotik ||
                        // A Lite box can only obey this from 1.0.25. Showing the
                        // button on older firmware would be a button that does
                        // nothing — the operator presses it, the dashboard says
                        // "sent", and the box never moves. `diag` is the proof
                        // the box is on a version that reports (1.0.24+); the
                        // reboot arrived one image later, so this errs one
                        // version late rather than one early.
                        (isOpenwrt && !!apiDevice?.router?.diag)) && (
                     <Section>
                        <SectionTitle>Reboot Router</SectionTitle>
                        <HelperText>
                           Restart this router remotely. All customers lose
                           WiFi for about 1–2 minutes while it comes back —
                           paid time is kept. Use this if the router is acting
                           up but still online.
                           {isOpenwrt ? (
                              <>
                                 {' '}
                                 The box takes this on its next check-in, so it
                                 works even while SmartFi cannot reach it — and
                                 if it is truly switched off, it will restart the
                                 moment it comes back.
                              </>
                           ) : (
                              data.routerStatus !== 'connected' && (
                                 <>
                                    {' '}
                                    <b>
                                       Reboot is unavailable while the router is
                                       offline.
                                    </b>
                                 </>
                              )
                           )}
                        </HelperText>
                        <ReflashBtn
                           disabled={!isOpenwrt && data.routerStatus !== 'connected'}
                           title={
                              !isOpenwrt && data.routerStatus !== 'connected'
                                 ? 'Router is offline — reboot needs a reachable router.'
                                 : undefined
                           }
                           onClick={() => {
                              handleClose();
                              setTimeout(
                                 () =>
                                    handleShow({
                                       template: 'reboot-router',
                                       title: 'Reboot Router',
                                       data: { device: data },
                                    }),
                                 50
                              );
                           }}
                        >
                           Reboot Router
                        </ReflashBtn>
                     </Section>
                  )}

                  {/* Reflash Router — main MikroTik vendos only. Re-onboards
                      the device onto a fresh/factory-reset router: server
                      RESTORES (same serial) or REPLACES (new serial → re-keys
                      the banked license). Ruijie reports its serial via the
                      agent, not REST, so it's excluded here. */}
                  {isMainVendo && isMikrotik && (
                     <Section>
                        <SectionTitle>Reflash Router (MikroTik)</SectionTitle>
                        <HelperText>
                           Factory-reset this router, or swapping in a new one?
                           Reflash puts it back online for this vendo. Your
                           license and remaining time carry over — you are not
                           charged again.
                        </HelperText>
                        <ReflashBtn
                           onClick={() => {
                              handleClose();
                              setTimeout(
                                 () =>
                                    handleShow({
                                       template: 'reflash-router',
                                       title: 'Reflash Router (MikroTik)',
                                       data: { device: data },
                                    }),
                                 50
                              );
                           }}
                        >
                           Reflash Router (MikroTik)
                        </ReflashBtn>
                     </Section>
                  )}

                  {/* Rebind a hard-reset / reflashed / replacement Lite box to
                      this device via its claim code. The box reconnects AS this
                      device (license/subs/portal kept); the serial reconciles the
                      license (same = resume, different = re-key).

                      Now shown for OrangePi too. The server guard was never
                      Ruijie-only — validateRebindTarget just requires an OpenWRT
                      MAIN device, which OrangePi has always satisfied — so only
                      this `!isOrangePi` hide stood in the way. Without it an
                      operator had to DELETE and re-register the device after every
                      SD-card reflash, losing the name, license, sub-vendos and
                      portal slug each time (Ray, 2026-07-30). Reflashing an
                      OrangePi is routine, which makes rebind matter more here than
                      on Ruijie, not less. Coin pin config and localMode live on the
                      device and are re-pushed on the next heartbeat, so they
                      survive too. */}
                  {isMainVendo && isOpenwrt && (
                     <Section>
                        <SectionTitle>Rebind Router</SectionTitle>
                        <HelperText>
                           {isOrangePi
                              ? 'Reflashed the SD card, or swapping in another OrangePi? Rebind it with its claim code so it comes back as this same vendo — keeping your name, license, sub-vendos, portal and coin pin settings. No need to delete and register it again.'
                              : 'Hard-reset or replacing this Ruijie? Rebind it with its claim code to keep your license and remaining time — the Ruijie equivalent of Reflash.'}
                        </HelperText>
                        <ReflashBtn
                           onClick={() => {
                              handleClose();
                              setTimeout(
                                 () =>
                                    handleShow({
                                       template: 'rebind-router',
                                       title: 'Rebind Router',
                                       data: { device: data },
                                    }),
                                 50
                              );
                           }}
                        >
                           Rebind Router
                        </ReflashBtn>
                     </Section>
                  )}

                  {isMainVendo && isMikrotik && (
                     <ReflashHistorySection device={data} />
                  )}

                  {soldByMe ? null : (
                  <DangerZone>
                     <DangerHeader>Danger Zone</DangerHeader>
                     <DangerBody>
                        <DangerItem>
                        <DangerItemTitle>Lost or stolen</DangerItemTitle>
                        <DangerText>
                           {stolen
                              ? '⚠ Flagged lost/stolen — re-registering this hardware on another account is blocked.'
                              : 'Report this device lost or stolen. Blocks anyone from re-registering this hardware on another SmartFi account.'}
                        </DangerText>
                        <DangerBtn
                           onClick={handleToggleStolen}
                           disabled={stolenSaving}
                        >
                           {stolenSaving
                              ? 'Saving…'
                              : stolen
                              ? 'Remove lost/stolen flag'
                              : 'Mark lost/stolen'}
                        </DangerBtn>
                        </DangerItem>
                        {isMainVendo && isOpenwrt && !isOrangePi && (
                           <DangerItem>
                              <DangerItemTitle>
                                 Remove SmartFi (back to OpenWRT)
                              </DangerItemTitle>
                              <DangerText>
                                 Remove SmartFi and return this router to plain{' '}
                                 <strong>official OpenWRT</strong>. This removes the device
                                 from your account (license credit is NOT refunded); the
                                 final flash step happens on the router&apos;s own local
                                 page. Requires router firmware <strong>1.0.13+</strong> —
                                 update the router first if it&apos;s older.
                              </DangerText>
                              {exitState === 'idle' && (
                                 <DangerBtn
                                    onClick={() => setExitState('confirming')}
                                    disabled={isLoading}
                                 >
                                    Remove from SmartFi (→ OpenWRT)…
                                 </DangerBtn>
                              )}
                              {exitState === 'confirming' && (
                                 <ConfirmRow>
                                    <ConfirmText>
                                       Remove <strong>{displayName}</strong> from SmartFi?
                                    </ConfirmText>
                                    <DangerBtn onClick={handleExitToOpenwrt}>
                                       Yes, remove
                                    </DangerBtn>
                                    <CancelLink
                                       type="button"
                                       onClick={() => setExitState('idle')}
                                    >
                                       Cancel
                                    </CancelLink>
                                 </ConfirmRow>
                              )}
                              {exitState === 'working' && (
                                 <DangerBtn disabled>
                                    Removing…
                                 </DangerBtn>
                              )}
                              {exitState === 'done' && (
                                 <DangerText as="div">
                                    <strong>
                                       Removed from SmartFi. Final step, at the router:
                                    </strong>
                                    <ol style={{ margin: '8px 0 0 18px', padding: 0 }}>
                                       <li>
                                          Wait about 2 minutes — the router notices it was
                                          removed and unlocks its local page.
                                       </li>
                                       <li>
                                          Connect to the router&apos;s WiFi and open{' '}
                                          <strong>10.0.0.1</strong> in a browser.
                                       </li>
                                       <li>
                                          Tap <strong>Advanced — Update firmware</strong>,
                                          then{' '}
                                          <strong>
                                             Remove SmartFi → official OpenWRT
                                          </strong>
                                          . The router needs its internet (WAN) cable
                                          plugged in.
                                       </li>
                                    </ol>
                                 </DangerText>
                              )}
                           </DangerItem>
                        )}
                        <DangerItem>
                        <DangerItemTitle>Delete</DangerItemTitle>
                        <DangerText>
                           Permanently delete this device. The license credit (if used) is NOT
                           refunded. Historical sessions stay in reports as orphan rows.
                           {' '}Server refuses if the device has live sessions — kick them first.
                           Attached sub-vendos are listed so you can delete them together.
                        </DangerText>
                        {deleteState === 'idle' && (
                           <DangerBtn
                              onClick={() => setDeleteState('confirming')}
                              disabled={isLoading}
                           >
                              Delete this device
                           </DangerBtn>
                        )}
                        {deleteState === 'confirming' && (
                           <ConfirmRow>
                              <ConfirmText>
                                 Delete <strong>{displayName}</strong>?
                              </ConfirmText>
                              <DangerBtn onClick={() => handleDelete()}>Yes, delete</DangerBtn>
                              <CancelLink type="button" onClick={() => setDeleteState('idle')}>
                                 Cancel
                              </CancelLink>
                           </ConfirmRow>
                        )}
                        {deleteState === 'cascade-confirming' && (
                           <ConfirmRow>
                              <ConfirmText>
                                 <strong>{displayName}</strong> still has{' '}
                                 {blockingSubs.length} sub-vendo
                                 {blockingSubs.length === 1 ? '' : 's'} attached:{' '}
                                 {blockingSubs
                                    .map(
                                       (s) =>
                                          `${s.name}${s.setupStatus && s.setupStatus !== 'done' ? ' (setup unfinished)' : ''}`
                                    )
                                    .join(', ')}
                                 . Delete all {blockingSubs.length + 1}?
                              </ConfirmText>
                              <DangerBtn onClick={() => handleDelete(true)}>
                                 Yes, delete all {blockingSubs.length + 1}
                              </DangerBtn>
                              <CancelLink type="button" onClick={() => setDeleteState('idle')}>
                                 Cancel
                              </CancelLink>
                           </ConfirmRow>
                        )}
                        {deleteState === 'deleting' && (
                           <DangerBtn disabled>Deleting…</DangerBtn>
                        )}
                        </DangerItem>
                     </DangerBody>
                  </DangerZone>
                  )}
               </Panel>
            )}
         </TabBody>

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title={tab === 'wan' ? 'Close' : 'Cancel'} type="outline" size="small" />
            </div>
            {/* "Save Settings" saves the rest of this modal and has never had
                anything to do with the WAN tab, which applies through its own
                Apply button because the router has to judge and possibly undo
                the change. Ray pressed the big blue one, watched it do nothing,
                and reasonably assumed his fixed address was saved — it was
                never sent. A button that looks like the primary action and
                silently ignores the form above it is worse than no button, so
                on this tab it is not there. */}
            {tab !== 'wan' && (
               <div onClick={handleSave}>
                  <Btn
                     title={isLoading ? 'Saving...' : 'Save Settings'}
                     type={isLoading ? 'disabled' : 'normal'}
                     size="small"
                     bgcolor={isLoading ? colors.GRAY10 : colors.PRIMARY}
                     textcolor={isLoading ? colors.GRAY1 : colors.WHITE}
                  />
               </div>
            )}
         </BtnRow>
         {hexHybridRoot ? (
            <RouterSyncProgress sync={hexSync} savedAt={routerSavedAt} onPoll={refetchDevice} />
         ) : (
            themeDevice && (
               <PortalThemeProgress
                  device={themeDevice}
                  savedAt={themeSavedAt}
                  onPoll={refetchDevice}
               />
            )
         )}
         <PortalAdvancedDrawer
            open={advancedOpen}
            value={portalConfig}
            deviceId={data._id}
            onChange={setPortalConfig}
            onClose={() => setAdvancedOpen(false)}
            onSave={saveCustomConfig}
            onPreview={() => openPreview('custom')}
            saving={savingConfig}
            progress={
               hexHybridRoot ? (
                  <RouterSyncProgress sync={hexSync} savedAt={routerSavedAt} onPoll={refetchDevice} />
               ) : themeDevice ? (
                  <PortalThemeProgress
                     device={themeDevice}
                     savedAt={themeSavedAt}
                     onPoll={refetchDevice}
                  />
               ) : null
            }
         />
      </Wrapper>
   );
};

/* ─── Ownership tab — pending-transfer awareness ────────────── */

const OwnershipTab: React.FC<{
   device: VendoDevice;
   /** A sub-vendo cannot be transferred on its own — it moves with its main. */
   isSubVendo?: boolean;
   mainName?: string;
   /** Sub-vendos that move together with this main. */
   subCount?: number;
   onOpenSell: () => void;
}> = ({ device, isSubVendo = false, mainName, subCount = 0, onOpenSell }) => {
   // Seller's email — the first of the 3 login fields on the basic
   // portal (sub-login hand-off).
   const { user: authUser } = useAuthStore() as {
      user?: { email?: string } | null;
   };
   // When a sub-login already EXISTS it becomes the tab's main content;
   // the full-transfer card collapses behind a small link (Ray).
   const { data: accessData } = useQuery({
      queryKey: ['client-access', device._id],
      queryFn: () => getClientAccessApi(device._id),
   });
   const hasSubLogin = !!accessData?.access;
   const [showTransfer, setShowTransfer] = useState(false);
   const queryClient = useQueryClient();
   const { data: outgoing = [], isLoading } = useQuery({
      queryKey: ['transfers', 'outgoing'],
      queryFn: () => listTransfersApi('outgoing'),
   });
   const pending = outgoing.find((t: ApiDeviceTransfer) => {
      const did = typeof t.device === 'object' ? t.device._id : t.device;
      return did === device._id && t.status === 'pending';
   });
   const [linkCopied, setLinkCopied] = React.useState(false);
   const [cancelling, setCancelling] = React.useState(false);

   if (isLoading) {
      return (
         <Section>
            <SectionTitle>Transfer this device</SectionTitle>
            <LoadingNote>Checking transfer state…</LoadingNote>
         </Section>
      );
   }

   if (pending) {
      const acceptLink = pending.signupToken
         ? `${window.location.origin}/transfers/handoff?token=${encodeURIComponent(pending.signupToken)}`
         : `${window.location.origin}/wifi-devices?reviewTransfer=${pending._id}`;
      const handleCopy = async () => {
         try {
            await navigator.clipboard.writeText(acceptLink);
            setLinkCopied(true);
            toast.success('Link copied');
         } catch {
            toast.error('Copy failed — select the link manually');
         }
      };
      const handleCancel = async () => {
         if (cancelling) return;
         setCancelling(true);
         try {
            await cancelTransferApi(device._id);
            toast.success('Transfer cancelled');
            queryClient.invalidateQueries({ queryKey: ['transfers'] });
         } catch (err: any) {
            toast.error(err?.response?.data?.error || 'Cancel failed');
         } finally {
            setCancelling(false);
         }
      };
      return (
         <Section>
            <SectionTitle>Transfer in progress</SectionTitle>
            <PendingPanel>
               <PendingMetaRow>
                  <PendingLabel>Waiting for buyer to accept</PendingLabel>
                  <PendingPaymentBadge $type={pending.paymentType}>
                     {pending.paymentType === 'full-cash'
                        ? `₱${(pending.fullCashAmount ?? 0).toLocaleString()}`
                        : pending.paymentType === 'installment'
                        ? 'Installment'
                        : 'Free transfer'}
                  </PendingPaymentBadge>
               </PendingMetaRow>
               <PendingBody>
                  Sent to <strong>{pending.toEmail}</strong>
                  {' · expires '}
                  {new Date(pending.expiresAt).toLocaleDateString()}
               </PendingBody>
               {pending.note && <PendingNote>"{pending.note}"</PendingNote>}
               <PendingLinkRow>
                  <PendingLinkInput
                     readOnly
                     value={acceptLink}
                     onFocus={(e) => e.currentTarget.select()}
                  />
                  <PendingCopyBtn type="button" onClick={handleCopy}>
                     {linkCopied ? 'Copied ✓' : 'Copy link'}
                  </PendingCopyBtn>
               </PendingLinkRow>
               <PendingHelp>
                  The buyer must open this link from a SmartFi account with
                  the matching email. You can't start another transfer for
                  this device until they accept, reject, or you cancel.
               </PendingHelp>
               <PendingCancelBtn
                  type="button"
                  onClick={handleCancel}
                  disabled={cancelling}
               >
                  {cancelling ? 'Cancelling…' : 'Cancel transfer'}
               </PendingCancelBtn>
            </PendingPanel>
         </Section>
      );
   }

   if (hasSubLogin && !showTransfer) {
      return (
         <Section>
            <SectionTitle>Buyers portal access</SectionTitle>
            <ClientAccessSection
               deviceId={device._id}
               sellerEmail={authUser?.email || ''}
            />
            <TransferInsteadLink
               type="button"
               onClick={() => setShowTransfer(true)}
            >
               Want a full email transfer?
            </TransferInsteadLink>
         </Section>
      );
   }

   return (
      <Section>
         {hasSubLogin && (
            <TransferInsteadLink type="button" onClick={() => setShowTransfer(false)}>
               ‹ Back to the sub login
            </TransferInsteadLink>
         )}
         <SectionTitle>Transfer this device</SectionTitle>
         {isSubVendo ? (
            <OwnershipCard>
               <OwnershipIcon>🔗</OwnershipIcon>
               <OwnershipHeading>This sub-vendo moves with its main</OwnershipHeading>
               <OwnershipText>
                  A sub-vendo runs on its main vendo's router, so it cannot
                  change hands on its own. Transfer{' '}
                  <strong>{mainName || 'the main vendo'}</strong> from its
                  Ownership tab and this sub-vendo goes to the buyer with it.
               </OwnershipText>
            </OwnershipCard>
         ) : (
         <OwnershipCard>
            <OwnershipIcon>📦</OwnershipIcon>
            <OwnershipHeading>Ready to hand it off?</OwnershipHeading>
            <OwnershipText>
               A transfer keeps the captive portal URL, NodeMCU pairing, and any
               remaining license time — the buyer just sees it in their
               dashboard. Pick <strong>Paid</strong> (we record the amount for
               your audit log) or <strong>Plain Transfer</strong> (free
               hand-off).
            </OwnershipText>
            <OwnershipFeatures>
               <OwnershipFeature>
                  <FeatureDot $color={colors.SUCCESS} />
                  Portal URL & QR sticker stay the same
               </OwnershipFeature>
               <OwnershipFeature>
                  <FeatureDot $color={colors.PRIMARY} />
                  License time travels with the device
               </OwnershipFeature>
               <OwnershipFeature>
                  <FeatureDot $color={colors.BLUE} />
                  {subCount > 0
                     ? `Your ${subCount} sub-vendo${subCount === 1 ? '' : 's'} move together with the main`
                     : 'Sub-vendos move together with the main'}
               </OwnershipFeature>
            </OwnershipFeatures>
            <StartTransferBtn type="button" onClick={onOpenSell}>
               Start transfer →
            </StartTransferBtn>
         </OwnershipCard>
         )}
         {/* Second hand-off option: buyer has no email / seller keeps
             ownership → simple sub-login for the basic portal. */}
         {!hasSubLogin && (
            <ClientAccessSection
               deviceId={device._id}
               sellerEmail={authUser?.email || ''}
            />
         )}
      </Section>
   );
};

const TransferInsteadLink = styled.button`
   margin-top: 12px;
   border: none;
   background: transparent;
   padding: 0;
   font-size: 12.5px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   cursor: pointer;
   text-decoration: underline;
   text-underline-offset: 2px;
`;

const Wrapper = styled.div`
   width: 720px;
   max-width: 100%;
   /* Cap at 78vh and clip — TabBody inside has flex:1 + min-height:0
    * + overflow-y:auto, so anything past 78vh scrolls inside the box
    * instead of stretching the modal past the viewport. */
   max-height: 78vh;
   overflow: hidden;
   display: flex;
   flex-direction: column;
   gap: 10px;
   @media (max-width: 768px) {
      width: 100%;
      max-height: 85vh;
   }
`;

const Summary = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   padding: 8px 12px;
   border-radius: 8px;
   background: var(--app-brand-tint);
   flex-wrap: wrap;
   flex-shrink: 0;
`;

const DeviceTitle = styled.span`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const NameDisplayWrap = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 2px 6px;
   margin: -2px -6px;
   border-radius: 6px;
   cursor: text;
   transition: background 120ms ease;
   &:hover, &:focus-visible {
      background: rgba(0, 0, 0, 0.05);
      outline: none;
   }
`;

const EditPencil = styled.svg`
   width: 12px;
   height: 12px;
   fill: ${colors.GRAY5};
   opacity: 0.6;
   ${NameDisplayWrap}:hover & {
      opacity: 1;
   }
`;

const NameEditWrap = styled.div`
   display: inline-flex;
   flex-direction: column;
   gap: 2px;
`;

const NameInput = styled.input<{ $invalid: boolean }>`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   background: ${colors.WHITE};
   border: 1px solid ${({ $invalid }) => ($invalid ? colors.RED : colors.GRAY3)};
   border-radius: 6px;
   padding: 2px 6px;
   outline: none;
   min-width: 160px;
   &:focus {
      border-color: ${({ $invalid }) => ($invalid ? colors.RED : colors.PRIMARY)};
      box-shadow: 0 0 0 2px ${({ $invalid }) =>
         $invalid ? `${colors.RED}22` : `${colors.PRIMARY}22`};
   }
`;

const NameErrorText = styled.span`
   font-size: 10px;
   font-weight: 600;
   color: ${colors.RED};
`;

const DeviceMeta = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 8px;
`;

const VersionPill = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 1px 7px;
   border-radius: 10px;
   font-size: 10px;
   font-weight: 600;
   font-family: monospace;
   background: ${colors.GRAY5}18;
   color: ${colors.GRAY3};
   border: 1px solid ${colors.GRAY5}33;
`;

const MetaId = styled.span`
   font-family: monospace;
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const TypePill = styled.span<{ $main: boolean }>`
   display: inline-block;
   padding: 1px 7px;
   border-radius: 10px;
   font-size: 9px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${({ $main }) => ($main ? `${colors.PRIMARY}15` : `${colors.ORANGE}22`)};
   color: ${({ $main }) => ($main ? colors.PRIMARY : colors.ORANGE)};
`;

const RogueDhcpBanner = styled.div`
   font-size: 12px;
   line-height: 1.5;
   font-weight: 600;
   color: ${colors.GRAY_DARK};
   background: ${colors.WARNING}12;
   border: 1px solid ${colors.WARNING}30;
   border-left: 4px solid ${colors.WARNING};
   border-radius: 10px;
   padding: 10px 14px;
   margin: 0 0 12px;
`;

const SoldBanner = styled.div`
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY_DARK};
   background: ${colors.WARNING}12;
   border: 1px solid ${colors.WARNING}30;
   border-radius: 10px;
   padding: 10px 14px;
   margin: 0 0 12px;
   strong {
      font-weight: 700;
   }
`;

const TabBar = styled.div`
   display: flex;
   gap: 0;
   border-bottom: 1px solid ${colors.GRAY12};
   overflow-x: auto;
   flex-shrink: 0;
`;

const TabItem = styled.button<{ $active: boolean }>`
   background: none;
   border: none;
   padding: 8px 14px;
   font-size: 13px;
   font-weight: ${({ $active }) => ($active ? 600 : 400)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   cursor: pointer;
   white-space: nowrap;
   display: flex;
   align-items: center;
   gap: 6px;
   transition: all 0.15s;
   margin-bottom: -1px;
   &:hover { color: ${colors.PRIMARY}; }
`;

/**
 * Up / down arrow on the Connection tab, NOT a dot (Ray, 2026-08-11): the header
 * already carries a status dot, and a second dot beside it reads as noise rather
 * than as a second fact. An arrow says which WAY the box has gone, which is the
 * thing this tab is about.
 *
 * Drawn in the same stroke style as the other icons in this file.
 */
const TabArrow = ({ state }: { state: 'ok' | 'bad' | 'unknown' }) => (
   <TabArrowBox $state={state} aria-hidden>
      <svg
         width="14"
         height="14"
         viewBox="0 0 24 24"
         fill="none"
         stroke="currentColor"
         strokeWidth="2.4"
         strokeLinecap="round"
         strokeLinejoin="round"
      >
         {state === 'ok' && <path d="M12 19V5M5 12l7-7 7 7" />}
         {state === 'bad' && <path d="M12 5v14M19 12l-7 7-7-7" />}
         {/* Never heard from: a dash, not an arrow. We have no direction to
             report and guessing one would be worse than saying nothing. */}
         {state === 'unknown' && <path d="M5 12h14" />}
      </svg>
   </TabArrowBox>
);

const TabArrowBox = styled.span<{ $state: 'ok' | 'bad' | 'unknown' }>`
   display: inline-flex;
   align-items: center;
   flex: 0 0 auto;
   color: ${({ $state }) =>
      $state === 'ok' ? '#16a34a' : $state === 'bad' ? '#c0392b' : '#9ca3af'};
`;

const TabCount = styled.span`
   font-size: 10px;
   font-weight: 700;
   background: ${colors.GRAY14};
   color: ${colors.GRAY3};
   padding: 1px 6px;
   border-radius: 10px;
   min-width: 18px;
   text-align: center;
`;

const TabBody = styled.div`
   overflow-y: auto;
   height: 330px;
   padding-top: 4px;
`;

const Panel = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const PanelHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   padding: 6px 10px;
   background: ${colors.GRAY14};
   border-radius: 6px;
`;

const REFLASH_MODEL_LABEL: Record<string, string> = {
   'hex-rb750gr3': 'hEX',
   rb4011: 'RB4011',
   ccr2004: 'CCR2004',
   ccr2116: 'CCR2116',
};

// Reflash snapshots — Restore rolls the device back to a prior router state.
const ReflashHistorySection: React.FC<{ device: VendoDevice }> = ({ device }) => {
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const { data, refetch } = useQuery({
      queryKey: ['reflash-history', device._id],
      queryFn: () => reflashHistory(device._id),
   });
   const [confirmAt, setConfirmAt] = useState<string | null>(null);
   const [busy, setBusy] = useState(false);
   const history = data?.history ?? [];
   // Block Revert while a reflash is in progress (mid-setup or the ~3-min
   // "Setting up the router" window) — reverting then would fight the reflash.
   const midReflash =
      device.setupStatus !== 'done' ||
      (!!device.reflashFinishingAt &&
         Date.now() - new Date(device.reflashFinishingAt).getTime() < 3 * 60 * 1000);
   if (!history.length) return null;

   const restore = async (snap: ReflashSnapshot) => {
      setBusy(true);
      try {
         const r = await reflashRestore(device._id, snap.at);
         toast.success(
            `Reverted to ${REFLASH_MODEL_LABEL[r.restoredTo.model] || r.restoredTo.model} (serial ${r.restoredTo.serial}). Power on your original router — it will reconnect.`,
            { autoClose: 5000 }
         );
         setConfirmAt(null);
         refetch();
         refetchDevices();
      } catch (err) {
         const e = err as { response?: { data?: { error?: string } } };
         toast.error(e?.response?.data?.error || 'Restore failed');
      } finally {
         setBusy(false);
      }
   };

   return (
      <Section>
         <SectionTitle>Reflash history</SectionTitle>
         <HelperText>
            Snapshots from before each reflash. <b>Revert</b> rolls this device
            back to that exact state, then your <b>original router reconnects</b>.
            ⚠ Only works if you still have the original router and it has{' '}
            <b>not been wiped / factory-reset</b> — Revert doesn't re-push config,
            it relies on the original router keeping its own. Use it if a reflash
            came up wrong.
         </HelperText>
         {midReflash && (
            <HelperText style={{ color: colors.WARNING }}>
               ⏳ A reflash is in progress — Revert is paused until it finishes.
            </HelperText>
         )}
         {history.map((h) => (
            <HistoryRow key={h.at}>
               <div>
                  <HistoryMain>
                     {REFLASH_MODEL_LABEL[h.model] || h.model} ·{' '}
                     {h.serial || 'no serial'}
                  </HistoryMain>
                  <HistoryMeta>{new Date(h.at).toLocaleString()}</HistoryMeta>
               </div>
               {confirmAt === h.at ? (
                  <ConfirmRow>
                     <RestoreBtn disabled={busy} onClick={() => restore(h)}>
                        {busy ? 'Reverting…' : 'Yes, revert'}
                     </RestoreBtn>
                     <CancelLink type="button" onClick={() => setConfirmAt(null)}>
                        Cancel
                     </CancelLink>
                  </ConfirmRow>
               ) : (
                  <RestoreBtn
                     disabled={midReflash}
                     onClick={() => setConfirmAt(h.at)}
                  >
                     Revert
                  </RestoreBtn>
               )}
            </HistoryRow>
         ))}
      </Section>
   );
};

const HistoryRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
   padding: 9px 0;
   border-top: 1px solid #f0f0f0;
`;
const HistoryMain = styled.div`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.DARK};
`;
const HistoryMeta = styled.div`
   font-size: 11px;
   color: ${colors.GRAY};
`;
const RestoreBtn = styled.button`
   border: 1px solid ${colors.PRIMARY};
   background: #fff;
   color: ${colors.PRIMARY};
   border-radius: 8px;
   padding: 7px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const Section = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;

const SectionTitle = styled.h4`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const InlineRow = styled.div`
   display: flex;
   gap: 12px;
   flex-wrap: wrap;
`;

const InlineField = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
`;

const Lbl = styled.label`
   font-size: 12px;
   color: ${colors.GRAY3};
   white-space: nowrap;
`;

const SaveHint = styled.span`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.SUCCESS};
`;

const ReplaceLink = styled.button`
   align-self: flex-start;
   background: none;
   border: none;
   padding: 2px 0;
   font-size: 12px;
   color: ${colors.GRAY4};
   text-decoration: underline;
   cursor: pointer;
   &:hover {
      color: ${colors.MAIN_DARK};
   }
`;

const CompactInput = styled.input<{ $w?: string }>`
   width: ${({ $w }) => $w || '100%'};
   padding: 5px 8px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 5px;
   font-size: 12px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const DiagBox = styled.div<{ $good: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin: 8px 0;
   padding: 10px 12px;
   border: 1px solid
      ${({ $good }) => ($good ? colors.SUCCESS : colors.WARNING)};
   border-left-width: 3px;
   border-radius: 8px;
   background: ${({ $good }) => ($good ? colors.SUCCESS : colors.WARNING)}1a;
`;

const WarnBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   margin-top: 8px;
   padding: 10px 12px;
   border: 1px solid ${colors.WARNING};
   border-left-width: 3px;
   border-radius: 8px;
   background: ${colors.WARNING}1a;
`;

const WarnTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const WarnBody = styled.p`
   font-size: 11px;
   line-height: 1.5;
   color: ${colors.MAIN_DARK70};
`;

const HelperText = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

/* The NodeMCU's WiFi shown as a FACT, not a field — on a Ruijie there is one
   correct value and the product owns it. Reads as state the operator can check,
   with nothing to type. */
const JoinRow = styled.div`
   display: flex;
   align-items: baseline;
   flex-wrap: wrap;
   gap: 4px 8px;
   padding: 8px 10px;
   border-radius: 8px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY13};
`;

/* Same two-tab shape the setup wizard uses, so the choice looks the same in
   both places an operator can make it. */
const ConnTabs = styled.div`
   display: inline-flex;
   align-self: flex-start;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
`;

const ConnTab = styled.button<{ $active: boolean }>`
   font-family: inherit;
   font-size: 11.5px;
   font-weight: 700;
   padding: 7px 14px;
   border: none;
   cursor: pointer;
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   color: ${({ $active }) => ($active ? colors.WHITE : colors.GRAY3)};
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const JoinLabel = styled.span`
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.06em;
   text-transform: uppercase;
   color: ${colors.GRAY5};
`;

const JoinName = styled.span`
   font-size: 13px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
   word-break: break-all;
`;

const JoinMark = styled.span<{ $ok: boolean }>`
   font-size: 11px;
   font-weight: 700;
   color: ${({ $ok }) => ($ok ? colors.SUCCESS : colors.DANGER)};
`;

/* ── Status legend ────────────────────────────────────────────────────
   The swatch colours are deliberately the SAME three as the live pills
   (RouterPill + DeviceStatusBadge): green SUCCESS, amber WARNING, red
   DANGER. A legend that invents its own palette teaches the wrong thing. */

const LegendToggle = styled.button`
   /* The Section is a flex column, so a button stretches full width and its
      own default text-align centres the label. Both need undoing. */
   align-self: flex-start;
   text-align: left;
   margin-top: 8px;
   padding: 0;
   border: none;
   background: none;
   font-size: 11px;
   font-weight: 700;
   color: ${colors.PRIMARY};
   cursor: pointer;
   &:hover {
      text-decoration: underline;
   }
`;

const Legend = styled.div`
   align-self: stretch;
   text-align: left;
   margin-top: 8px;
   padding: 12px;
   border-radius: 10px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY13};
`;

const LegendNote = styled.p`
   margin: 0 0 10px;
   font-size: 11px;
   line-height: 1.55;
   color: ${colors.MAIN_DARK70};
   &:last-child {
      margin: 10px 0 0;
   }
`;

const LegendGroup = styled.p`
   margin: 12px 0 6px;
   font-size: 9px;
   font-weight: 800;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   color: ${colors.GRAY5};
   &:first-of-type {
      margin-top: 0;
   }
`;

const LegendRow = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   padding: 5px 0;
`;

const Swatch = styled.span<{ $tone: 'good' | 'warn' | 'bad' }>`
   flex-shrink: 0;
   width: 10px;
   height: 10px;
   margin-top: 3px;
   border-radius: 50%;
   background: ${({ $tone }) =>
      $tone === 'good'
         ? colors.SUCCESS
         : $tone === 'warn'
           ? colors.WARNING
           : colors.DANGER};
   /* A ring, so the three are still distinguishable without colour. */
   box-shadow: 0 0 0 2px
      ${({ $tone }) =>
         $tone === 'good'
            ? `${colors.SUCCESS}33`
            : $tone === 'warn'
              ? `${colors.WARNING}33`
              : `${colors.DANGER}33`};
`;

const LegendName = styled.p`
   margin: 0;
   font-size: 11.5px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const LegendWhat = styled.p`
   margin: 1px 0 0;
   font-size: 11px;
   line-height: 1.5;
   color: ${colors.MAIN_DARK70};
`;

const ToggleRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 2px 0;
   gap: 8px;
`;

const ToggleLabel = styled.span`
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const ToggleInput = styled.input`
   width: 16px;
   height: 16px;
   cursor: pointer;
`;

/* Rates table */

const PerPesoRow = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 10px 12px;
   margin-bottom: 8px;
   background: var(--app-surface, #fff);
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   /* On phones keep the label + number input together on the first row,
      and let the long hint wrap to its own full-width line below. */
   @media (max-width: 640px) {
      flex-wrap: wrap;
      row-gap: 8px;
   }
`;

const PerPesoLbl = styled.span`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   flex-shrink: 0;
`;

const PerPesoHint = styled.span`
   font-size: 11px;
   line-height: 1.4;
   color: ${colors.GRAY8};
   flex: 1;
   strong { color: ${colors.MAIN_DARK}; }
   @media (max-width: 640px) {
      flex-basis: 100%;
   }
`;

const RateErrorBanner = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   width: 100%;
   margin-bottom: 10px;
   padding: 10px 14px;
   border-radius: 8px;
   border: 1px solid ${colors.DANGER};
   background: ${colors.DANGER}12;
   color: ${colors.DANGER};
   font-size: 12px;
   font-weight: 600;
   line-height: 1.4;
   &::before {
      content: '!';
      flex: 0 0 auto;
      display: grid;
      place-items: center;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${colors.DANGER};
      color: #fff;
      font-size: 11px;
      font-weight: 800;
   }
`;

const RateHeader = styled.div`
   display: flex;
   gap: 6px;
   padding: 0 4px;
   /* Columns don't line up once rows wrap on phones — hide the header there;
      each field is self-describing (₱ number, min/hrs, pause dropdown, label). */
   @media (max-width: 640px) {
      display: none;
   }
`;

const RateHeaderCell = styled.span<{ $w?: string }>`
   width: ${({ $w }) => $w || 'auto'};
   flex: ${({ $w }) => ($w ? '0 0 auto' : '1')};
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const RemoveBtn = styled.button`
   width: 24px;
   height: 24px;
   border: 1px solid ${colors.DANGER}30;
   background: ${colors.DANGER}10;
   color: ${colors.DANGER};
   border-radius: 5px;
   font-size: 16px;
   line-height: 1;
   cursor: pointer;
   display: grid;
   place-items: center;
   padding: 0;
   &:hover { background: ${colors.DANGER}20; }
`;

const RateRow = styled.div`
   display: flex;
   gap: 6px;
   align-items: center;
   /* On phones the fixed-width cells overflowed and clipped the Price input.
      Redesign as a self-contained card: remove (×) pinned to the top-right,
      Price + Duration paired on the first row, Pause window and Label each
      spanning the full width. Every field fills its cell — no dead space. */
   @media (max-width: 640px) {
      display: grid;
      grid-template-columns: 96px 1fr;
      gap: 10px 8px;
      align-items: start;
      position: relative;
      padding: 40px 14px 14px;
      margin-bottom: 10px;
      border: 1px solid ${colors.GRAY12};
      border-radius: 10px;
      background: var(--app-surface, #fff);

      > * {
         width: 100% !important;
         min-width: 0;
      }
      /* Pause window (2nd cell) + Label (2nd input) span the full row */
      > div:nth-of-type(2),
      > input:nth-of-type(2) {
         grid-column: 1 / -1;
      }
      /* Remove button pinned to the card's top-right corner (scoped to
         the × so the mobile Done button can flow in the grid) */
      > ${RemoveBtn} {
         position: absolute;
         top: 8px;
         right: 8px;
         width: 30px !important;
         height: 30px;
      }
   }
`;

/* Mobile read-only rate row (portal style): ₱price · label · duration,
   Edit expands the row into the editable card above. Only rendered on
   phones, so no media query needed. */
const RateReadRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   margin-bottom: 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 10px 12px;
   background: var(--app-surface, #fff);
`;

const RateReadPrice = styled.div`
   font-size: 17px;
   font-weight: 800;
   min-width: 46px;
`;

const RateReadInfo = styled.div`
   flex: 1;
   min-width: 0;
`;

const RateReadLabel = styled.div`
   font-size: 13px;
   font-weight: 700;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const RateReadTime = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY5};
`;

const RateEditBtn = styled.button`
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: ${colors.GRAY3};
   border-radius: 7px;
   padding: 6px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover { background: ${colors.GRAY14}; }
`;

const RateDoneBtn = styled.button`
   grid-column: 1 / -1;
   border: none;
   background: ${colors.PRIMARY};
   color: #fff;
   border-radius: 8px;
   padding: 9px 12px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
`;

/* Mobile expanded editor — labeled fields like the client portal's
   inline rate editor, accent border marks the row being edited. */
const RateEditCard = styled.div`
   display: grid;
   grid-template-columns: 96px 1fr;
   gap: 10px 8px;
   align-items: start;
   position: relative;
   padding: 40px 14px 14px;
   margin-bottom: 10px;
   border: 1px solid ${colors.PRIMARY};
   border-radius: 10px;
   background: var(--app-surface, #fff);

   input, select {
      width: 100% !important;
      min-width: 0;
   }
   > ${RemoveBtn} {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 30px;
      height: 30px;
   }
`;

const EditField = styled.div<{ $full?: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 3px;
   min-width: 0;
   ${({ $full }) => ($full ? 'grid-column: 1 / -1;' : '')}
`;

const EditFieldLabel = styled.label`
   font-size: 10.5px;
   font-weight: 700;
   color: ${colors.GRAY5};
`;

/* One line under a rate field, in plain words. The control this replaced was
   labelled "Pause limit" and 759 rates across the fleet had been set on it by
   operators who believed it meant validity — so the field now says out loud
   what its clock actually does. */
const RateFieldHint = styled.span`
   font-size: 10.5px;
   line-height: 1.35;
   color: ${colors.GRAY5};
`;

const DurationCell = styled.div<{ $w?: string }>`
   width: ${({ $w }) => $w ?? 'auto'};
   flex: 0 0 ${({ $w }) => $w ?? 'auto'};
`;

const AddRateBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px dashed ${colors.PRIMARY}60;
   border-radius: 6px;
   padding: 5px 10px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   align-self: flex-start;
   &:hover { background: ${colors.PRIMARY}10; }
`;

const EmptyNote = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   font-style: italic;
   padding: 8px 0;
`;

// Sub-tab bar inside the Rates panel — Coin / Voucher segmentation.
// Visually subordinate to the main DeviceSettings TabBar above.
const SubTabBar = styled.div`
   display: flex;
   gap: 6px;
   background: ${colors.GRAY14};
   padding: 4px;
   border-radius: 8px;
   align-self: flex-start;
   margin-bottom: 4px;
`;

const SubTabBtn = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.WHITE : 'transparent')};
   color: ${({ $active }) => ($active ? colors.GRAY1 : colors.GRAY5)};
   border: none;
   padding: 6px 14px;
   border-radius: 6px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none')};
   transition: background 0.15s, color 0.15s;

   &:hover { color: ${colors.GRAY1}; }
`;

const SubTabCount = styled.span`
   font-size: 10px;
   font-weight: 700;
   padding: 1px 6px;
   border-radius: 8px;
   background: ${colors.GRAY13};
   color: ${colors.GRAY3};
`;

const RateActionsRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   margin-top: 4px;
`;

const BulkPrintRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   margin-top: 12px;
   padding-top: 12px;
   border-top: 1px solid ${colors.GRAY12};
`;

const ApplyBtn = styled.button`
   background: transparent;
   color: ${colors.GRAY3};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   padding: 5px 10px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;

   &:hover:not(:disabled) {
      background: ${colors.GRAY14};
      border-color: ${colors.GRAY11};
   }

   &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
   }
`;

// ApplyRatesToDevicesModal — small inline dialog (NOT routed through
// useUIStore) inside the Rates section. Lists the owner's other vendos
// (mains + subs, grouped by parent) with a checkbox each. Submit calls
// copyRatesToDevicesApi and shows a summary toast.
const ApplyOverlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.55);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1000;
   padding: 16px;
`;

const ApplyDialog = styled.div`
   width: 100%;
   max-width: 480px;
   max-height: 90vh;
   background: ${colors.WHITE};
   border-radius: 12px;
   padding: 18px;
   display: flex;
   flex-direction: column;
   gap: 12px;
   overflow: hidden;
`;

const ApplyTitle = styled.h3`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.GRAY1};
   margin: 0;
`;

const ApplySub = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin: 0;
   line-height: 1.5;
`;

const ApplyList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   overflow-y: auto;
   max-height: 360px;
   padding: 4px 2px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
`;

const ApplyGroup = styled.div`
   display: flex;
   flex-direction: column;
`;

const ApplyGroupHeader = styled.div`
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
   padding: 8px 10px 4px;
`;

const ApplyRow = styled.label<{ $sub?: boolean }>`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 10px;
   margin-left: ${({ $sub }) => ($sub ? '16px' : '0')};
   border-radius: 6px;
   font-size: 12px;
   color: ${colors.GRAY1};
   cursor: pointer;

   &:hover { background: ${colors.GRAY14}; }
`;

const ApplyRowName = styled.span`
   font-weight: 600;
`;

const ApplyRowMeta = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

/* ─── Apply Rates side-drawer (mirrors DataPlanModal LinkVendoDrawer) ─── */

const ApplyDrawerBackdrop = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.4);
   /* Must beat the PromptWrapper modal (z-index 1500/1501) so the
      drawer + its backdrop sit on top, otherwise the DeviceSettings
      modal eats clicks. */
   z-index: 2000;
   display: flex;
   justify-content: flex-end;
`;

const ApplyDrawer = styled.div`
   width: 380px;
   max-width: 100%;
   height: 100%;
   background: ${colors.WHITE};
   border-left: 1px solid ${colors.GRAY12};
   padding: 18px 20px;
   display: flex;
   flex-direction: column;
   gap: 14px;
   animation: applyDrawerSlideIn 0.2s ease-out;

   @keyframes applyDrawerSlideIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
   }
`;

const ApplyDrawerHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: flex-start;
   gap: 12px;
`;

const ApplyDrawerTitle = styled.h3`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.GRAY1};
   margin: 0 0 6px;
`;

const ApplyDrawerSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   margin: 0;
`;

const ApplyDrawerClose = styled.button`
   background: transparent;
   border: none;
   color: ${colors.GRAY3};
   font-size: 22px;
   font-weight: 700;
   line-height: 1;
   cursor: pointer;
   padding: 0 4px;

   &:hover { color: ${colors.GRAY1}; }
`;

const ApplyDrawerListWrap = styled.div`
   flex: 1 1 auto;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin: 4px -4px;
   padding: 0 4px;
`;

const ApplyDrawerRow = styled.div<{ $sub?: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   margin-left: ${({ $sub }) => ($sub ? '14px' : '0')};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   cursor: pointer;
   transition: background 0.15s, border-color 0.15s;

   &:hover {
      background: ${colors.GRAY14};
      border-color: ${colors.PRIMARY};
   }
`;

const ApplyCheckbox = styled.div<{ $checked: boolean }>`
   width: 18px;
   height: 18px;
   border-radius: 4px;
   border: 1.5px solid ${({ $checked }) => ($checked ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $checked }) => ($checked ? colors.PRIMARY : 'transparent')};
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   transition: all 0.15s;
`;

const ApplyDrawerRowBody = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const ApplyDrawerRowName = styled.div`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const ApplyDrawerRowId = styled.div`
   font-size: 10px;
   color: ${colors.GRAY5};
   font-family: monospace;
`;

const ApplyDrawerGroupHead = styled.div`
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
   padding: 12px 4px 4px;
`;

const ApplyDrawerEmpty = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   text-align: center;
   padding: 24px 8px;
`;

const ApplyReplaceRow = styled.label`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   padding: 10px 0 12px;
   font-size: 13px;
   color: ${colors.GRAY3};
   cursor: pointer;
   input {
      margin-top: 2px;
   }
`;

const ApplyDrawerFoot = styled.div`
   display: flex;
   gap: 8px;
   padding-top: 12px;
   border-top: 1px solid ${colors.GRAY12};
`;

const ApplyCancelBtn = styled.button`
   background: transparent;
   color: ${colors.GRAY3};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   padding: 7px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;

   &:hover:not(:disabled) {
      background: ${colors.GRAY14};
   }

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const ApplyPrimaryBtn = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   padding: 7px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;

   &:hover:not(:disabled) {
      filter: brightness(1.08);
   }

   &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
   }
`;

/* ─── Portal-mode radio (Session tab) ────────────────────── */

const PortalModeRow = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
   gap: 8px;
   margin-top: 6px;
`;

const PortalModeOpt = styled.button<{ $active: boolean }>`
   position: relative;
   background: ${({ $active }) =>
      $active ? `${colors.PRIMARY}10` : colors.WHITE};
   border: 2px solid
      ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   padding: 14px 12px 12px;
   text-align: left;
   cursor: pointer;
   display: flex;
   flex-direction: column;
   gap: 6px;
   transition: background 0.15s, border-color 0.15s, transform 0.08s;
   box-shadow: ${({ $active }) =>
      $active ? `0 0 0 3px ${colors.PRIMARY}20` : 'none'};

   &:hover {
      border-color: ${colors.PRIMARY};
   }

   &:active {
      transform: scale(0.99);
   }
`;

const PortalModeCheck = styled.span`
   position: absolute;
   top: 8px;
   right: 8px;
   width: 20px;
   height: 20px;
   border-radius: 50%;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   display: inline-flex;
   align-items: center;
   justify-content: center;
`;

const PortalModeIcon = styled.span<{ $active: boolean }>`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 36px;
   height: 36px;
   border-radius: 8px;
   background: ${({ $active }) =>
      $active ? `${colors.PRIMARY}20` : colors.GRAY14};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   margin-bottom: 2px;
   transition: background 0.15s, color 0.15s;
`;

const PortalModeLbl = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

const PortalModeDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

/* ─── Bulk Print modals ─────────────────────────────────────── */

const BulkField = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const BulkLbl = styled.label`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY3};
`;

const BulkInput = styled.input`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   color: ${colors.GRAY1};
   background: ${colors.WHITE};

   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const BulkSelect = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   color: ${colors.GRAY1};
   background: ${colors.WHITE};

   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const BulkLogoPreview = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   padding: 6px 8px;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 6px;
   background: ${colors.GRAY14};
   max-width: 100%;

   img {
      max-width: 60px;
      max-height: 60px;
      object-fit: contain;
      background: ${colors.WHITE};
      border-radius: 4px;
      padding: 2px;
   }

   button {
      background: transparent;
      border: none;
      color: ${colors.GRAY3};
      font-size: 18px;
      font-weight: 700;
      cursor: pointer;
      padding: 0 4px;
      line-height: 1;

      &:hover { color: ${colors.PRIMARY}; }
   }
`;

/* Bulk print drawer — same right-side slide-in pattern as the Apply
   rates drawer above, just wider so the A4 preview canvas (186mm ≈
   702px) fits without horizontal scrolling. z-index 2000 beats the
   PromptWrapper modal (1500/1501) — same fix as the Apply drawer. */
const PrintOverlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(0, 0, 0, 0.4);
   z-index: 2000;
   display: flex;
   justify-content: flex-end;
`;

const PrintDialog = styled.div`
   width: 380px;
   max-width: 100%;
   height: 100%;
   background: ${colors.WHITE};
   border-left: 1px solid ${colors.GRAY12};
   display: flex;
   flex-direction: column;
   overflow: hidden;
   animation: printDrawerSlideIn 0.2s ease-out;

   @keyframes printDrawerSlideIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
   }
`;

/* Drawer header / body / footer — slim sidebar layout matching the
   Apply rates drawer. Renamed from PrintControls to disambiguate. */
const PrintDrawerHead = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: flex-start;
   gap: 12px;
   padding: 16px 18px;
   border-bottom: 1px solid ${colors.GRAY13};
   background: ${colors.GRAY14};
   flex-shrink: 0;
`;

const PrintDrawerBody = styled.div`
   flex: 1 1 auto;
   overflow-y: auto;
   padding: 16px 18px;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;

const PrintDrawerFoot = styled.div`
   display: flex;
   gap: 8px;
   padding: 12px 18px;
   border-top: 1px solid ${colors.GRAY13};
   flex-shrink: 0;
   justify-content: flex-end;
   background: ${colors.WHITE};
`;

const PreviewSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 12px 14px;
   border-radius: 10px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY13};
`;

const PreviewLabel = styled.div`
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.06em;
   color: ${colors.GRAY5};
`;

const PreviewCardWrap = styled.div`
   display: flex;
   justify-content: center;
   padding: 8px 0;
`;

const PreviewHint = styled.div`
   font-size: 10px;
   color: ${colors.GRAY5};
   text-align: center;
   font-style: italic;
`;

const GeneratedBanner = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 10px;
   padding: 12px 14px;
   border-radius: 8px;
   background: rgba(34, 197, 94, 0.1);
   border: 1px solid rgba(34, 197, 94, 0.3);
`;

const GeneratedBannerIcon = styled.div`
   color: #16a34a;
   flex-shrink: 0;
`;

const GeneratedBannerText = styled.div`
   font-size: 12px;
   color: ${colors.GRAY1};
   line-height: 1.5;
   em { font-style: italic; }
`;

/* Off-screen host — keeps the canvas in the DOM but tucks it 100k px
   off-screen so it never paints. Print CSS overrides position to put
   it back at 0,0 for printing. */
const OffscreenPrintHost = styled.div`
   position: fixed;
   left: -100000px;
   top: 0;
   width: 186mm;
   pointer-events: none;
`;

/* Kept for backward-compat in case other code references it. */
const PrintControls = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 16px 20px;
`;

const PrintHeaderRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: flex-start;
   gap: 16px;
   flex-wrap: wrap;
`;

const PrintTitle = styled.h3`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.GRAY1};
   margin: 0 0 4px;
`;

const PrintSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin: 0;
`;

const PrintFormGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, 1fr);
   gap: 10px 14px;

   @media (max-width: 640px) {
      grid-template-columns: 1fr;
   }
`;

const PrintNotice = styled.div`
   padding: 12px 14px;
   border-radius: 8px;
   background: ${colors.GRAY14};
   border: 1px dashed ${colors.GRAY12};
   color: ${colors.GRAY3};
   font-size: 12px;
`;

const PrintEmptyState = styled.div`
   min-height: 320px;
   display: flex;
   align-items: center;
   justify-content: center;
   text-align: center;
   color: ${colors.GRAY5};
`;

const PrintEmptyIcon = styled.div`
   display: flex;
   justify-content: center;
   margin-bottom: 6px;
   color: ${colors.GRAY11};
`;

const PrintEmptyMsg = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;

const PrintCheckbox = styled.label`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   font-size: 12px;
   color: ${colors.GRAY3};
   cursor: pointer;
   user-select: none;
`;

const PrintActionRow = styled.div`
   display: flex;
   gap: 8px;
   justify-content: flex-end;
`;

const PrintCanvasScroll = styled.div`
   flex: 1 1 auto;
   overflow-y: auto;
   background: ${colors.GRAY14};
   padding: 16px;
`;

// PrintCanvas is the A4-sized printable region. Width is set to A4
// usable area (200mm = 210mm − 5mm margin × 2). On screen we show it
// at natural size with a white background like a sheet of paper.
const PrintCanvas = styled.div`
   width: 200mm;
   min-height: 287mm; /* A4 height minus 5mm margin × 2 — single-page floor */
   background: ${colors.WHITE};
   margin: 0 auto;
   box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
   padding: 0;
`;

// 4 × 47mm columns + tight gaps fill the 200mm printable width almost
// exactly (4×47 + 3×2.5 = 195.5mm) — with 7 rows that's 28 cards per
// sheet vs the old 3×50.8mm layout's 18 (which centered with ~2cm of
// dead margin on each side — the "1 inch margin" complaint).
const PrintGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(4, 47mm);
   gap: 2.5mm;
   justify-content: center;
   padding: 6mm 0;

   /* Print: remove screen padding so cards use full A4 area inside
      the 5mm @page margin. */
   @media print {
      padding: 0;
   }
`;

const PrintCard = styled.div`
   width: 47mm; /* was 2in (50.8mm) — trimmed to fit 4 per row on A4 */
   height: 35.2mm; /* keeps the 4:3 card ratio */
   border: 1.5px dashed #555;
   border-radius: 2mm;
   padding: 2mm;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   text-align: center;
   gap: 1.5mm;
   background: #ffffff;
   color: #111111;
   page-break-inside: avoid;
   break-inside: avoid;
`;

const PrintCardHeader = styled.div`
   display: flex;
   align-items: center;
   gap: 1.5mm;
   max-width: 100%;
   min-height: 0;
`;

const PrintCardLogo = styled.img`
   max-height: 6mm;
   max-width: 12mm;
   object-fit: contain;
`;

const PrintCardStore = styled.div`
   font-size: 8pt;
   font-weight: 700;
   color: #222;
   /* Allow wrapping so long resort names like "Wonder Beach Resort &
      Spa" don't render as "Wonder Beach Resort & S…" — the printed card
      gives more vertical room than horizontal. word-break covers
      exotic unbroken strings (single long word > max-width). */
   max-width: 36mm;
   line-height: 1.15;
   word-wrap: break-word;
   word-break: break-word;
`;

const PrintCardCode = styled.div`
   font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
   font-size: 16pt;
   font-weight: 800;
   letter-spacing: 0.15em;
   color: #000;
`;

const PrintCardMeta = styled.div`
   font-size: 8pt;
   font-weight: 600;
   color: #444;
`;

// Print stylesheet. Two important fixes baked in here:
//
// 1) display:none > visibility:hidden for siblings.
//    visibility:hidden keeps layout space — so the dashboard's full
//    height pushes the print canvas down a whole A4 before it appears.
//    display:none removes those siblings from the print layout entirely
//    so the canvas can start at the top of page 1.
//
// 2) Canvas stays in NORMAL flow (position: static) so the browser can
//    paginate the card grid across multiple A4 sheets. Absolute / fixed
//    positioning clips at the first page boundary.
//
// Tight 8mm page margin so cards fill more of the sheet. print-color-
// adjust:exact preserves the dashed card borders + backgrounds (Chrome
// strips backgrounds by default).
const PrintGlobalStyle = createGlobalStyle`
   @media print {
      @page {
         size: A4;
         margin: 5mm;
      }
      html, body {
         margin: 0 !important;
         padding: 0 !important;
         background: #ffffff !important;
         height: auto !important;
         overflow: visible !important;
      }
      /* Hide every direct child of body so the dashboard's layout
         doesn't push the print canvas to the bottom of the page. */
      body > * {
         display: none !important;
      }
      /* But REVEAL the off-screen host that contains our canvas. */
      body > .bulk-print-offscreen {
         display: block !important;
         position: static !important;
         left: auto !important;
         top: auto !important;
         pointer-events: auto !important;
         width: auto !important;
      }
      .bulk-print-canvas {
         position: static !important;
         box-shadow: none !important;
         margin: 0 !important;
         padding: 0 !important;
         width: 100% !important;
         min-height: 0 !important;
         background: #ffffff !important;
         -webkit-print-color-adjust: exact !important;
         print-color-adjust: exact !important;
      }
      .bulk-print-canvas * {
         -webkit-print-color-adjust: exact !important;
         print-color-adjust: exact !important;
      }
   }
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   padding-top: 10px;
   border-top: 1px solid ${colors.GRAY13};
   flex-shrink: 0;
`;

/* ─── Portal Theme tab ─────────────────────────────────────── */

const ThemeGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 10px;
   @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
   @media (max-width: 420px) { grid-template-columns: 1fr; }
`;

const CustomCallout = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   margin-top: 14px;
   padding: 12px 14px;
   border: 1px solid ${colors.PRIMARY}40;
   background: ${colors.PRIMARY}0a;
   border-radius: 10px;
   flex-wrap: wrap;
`;

const CustomCalloutTitle = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

/* No max-width any more — the callout lost its button, so the copy gets
   the full width instead of wrapping early against empty space. */
const CustomCalloutSub = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const ThemeCard = styled.button<{ $selected: boolean; $accent: string; $special?: boolean }>`
   position: relative;
   background: ${({ $selected, $accent }) => ($selected ? `${$accent}10` : colors.WHITE)};
   border: 2px solid ${({ $selected, $accent, $special }) => ($selected ? $accent : $special ? '#c084fc' : colors.GRAY12)};
   box-shadow: ${({ $special }) => ($special ? '0 6px 18px rgb(168 85 247 / 0.25)' : 'none')};
   border-radius: 10px;
   padding: 8px;
   cursor: pointer;
   text-align: left;
   font-family: inherit;
   display: flex;
   flex-direction: column;
   gap: 8px;
   transition: all 0.15s;
   &:hover { border-color: ${({ $accent }) => $accent}; }
`;

/* Customizable: an illustrated card that looks special to click (Ray 2026-10-23). */
const sparkle = keyframes`
   0%, 100% { opacity: 0.35; transform: scale(0.8); }
   50% { opacity: 1; transform: scale(1.15); }
`;
const hueSpin = keyframes`
   0% { background-position: 0% 50%; }
   100% { background-position: 200% 50%; }
`;
const CustomSwatchBox = styled.div`
   position: relative;
   height: 96px;
   border-radius: 6px;
   overflow: hidden;
   background: linear-gradient(110deg, #ff6a3d, #ff3d7f, #a855f7, #3b82f6, #22d3ee, #ff6a3d);
   background-size: 200% 100%;
   animation: ${hueSpin} 8s linear infinite;
   svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
   }
   .spark {
      transform-origin: center;
      transform-box: fill-box;
      animation: ${sparkle} 1.8s ease-in-out infinite;
   }
   .spark.b { animation-delay: 0.6s; }
   .spark.c { animation-delay: 1.2s; }
   @media (prefers-reduced-motion: reduce) {
      animation: none;
      .spark { animation: none; }
   }
`;
const CustomRibbon = styled.span`
   position: absolute;
   top: 6px;
   right: 6px;
   z-index: 1;
   font-size: 9.5px;
   font-weight: 800;
   letter-spacing: 0.06em;
   color: #7c3aed;
   background: #fff;
   padding: 3px 7px;
   border-radius: 999px;
   box-shadow: 0 2px 6px rgb(0 0 0 / 0.2);
`;
export function CustomSwatch() {
   return (
      <CustomSwatchBox aria-hidden>
         <CustomRibbon>✨ MAKE IT YOURS</CustomRibbon>
         <svg viewBox="0 0 240 96" preserveAspectRatio="xMidYMid meet">
            <defs>
               <filter id="csSoft" x="-30%" y="-30%" width="160%" height="160%">
                  <feDropShadow dx="0" dy="2" stdDeviation="2.2" floodColor="#4c1d95" floodOpacity="0.28" />
               </filter>
            </defs>
            {/* palette */}
            <g transform="translate(20 22)">
               <path d="M28 2C12 2 2 14 2 28c0 13 10 22 21 22 6 0 7-4 5-8-2-4 1-8 6-8h7c8 0 13-6 13-13C54 10 43 2 28 2z" fill="#fff" filter="url(#csSoft)" />
               <circle cx="16" cy="20" r="4.5" fill="#ff3d7f" />
               <circle cx="28" cy="13" r="4.5" fill="#f59e0b" />
               <circle cx="40" cy="18" r="4.5" fill="#22c55e" />
               <circle cx="15" cy="33" r="4.5" fill="#3b82f6" />
            </g>
            {/* brush */}
            <g transform="translate(70 18) rotate(35)">
               <rect x="0" y="0" width="7" height="36" rx="3" fill="#fbbf24" filter="url(#csSoft)" />
               <rect x="-1" y="34" width="9" height="8" fill="#d1d5db" filter="url(#csSoft)" />
               <path d="M-1 42h9l-2 12c-1 4-4 4-5 0z" fill="#a855f7" filter="url(#csSoft)" />
            </g>
            {/* phone with a custom page */}
            <g transform="translate(108 10)">
               <rect x="0" y="0" width="44" height="76" rx="10" fill="rgba(255,255,255,0.55)" filter="url(#csSoft)" />
               <rect x="3.5" y="5" width="37" height="66" rx="7" fill="#fff" />
               <rect x="8" y="10" width="28" height="14" rx="3" fill="#ffd6e5" />
               <circle cx="14" cy="17" r="3" fill="#ff3d7f" />
               <rect x="19" y="15" width="14" height="4" rx="2" fill="#ff8ab3" />
               <rect x="8" y="28" width="28" height="8" rx="4" fill="#22c55e" />
               <rect x="8" y="40" width="13" height="8" rx="4" fill="#3b82f6" />
               <rect x="23" y="40" width="13" height="8" rx="4" fill="#f59e0b" />
               <rect x="8" y="52" width="28" height="12" rx="3" fill="#ede9fe" />
               <path d="M12 61l6-6 4 4 3-3 7 5" fill="none" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            {/* stickers: layouts you can pick */}
            <g fontSize="18">
               <text x="166" y="56">🏨</text>
               <text x="200" y="58">🏖️</text>
               <text x="172" y="86">🎮</text>
               <text x="204" y="86">🎵</text>
            </g>
            {/* sparkles */}
            <path className="spark" d="M96 12l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" fill="#fff" />
            <path className="spark b" d="M160 80l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5z" fill="#fff" />
            <path className="spark c" d="M14 78l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5z" fill="#fff" />
         </svg>
      </CustomSwatchBox>
   );
}

const ThemeSwatch = styled.div<{ $pageBg: string }>`
   background: ${({ $pageBg }) => $pageBg};
   border-radius: 6px;
   padding: 8px;
   height: 96px;
   display: flex;
   flex-direction: column;
   gap: 5px;
   position: relative;
   overflow: hidden;
`;

const SwatchHeader = styled.span<{ $color: string }>`
   font-size: 10px;
   font-weight: 700;
   color: ${({ $color }) => $color};
`;

const SwatchCard = styled.div<{ $bg: string; $border: string }>`
   background: ${({ $bg }) => $bg};
   border: 1px solid ${({ $border }) => `${$border}40`};
   border-radius: 4px;
   padding: 5px 6px;
   display: flex;
   align-items: center;
   gap: 4px;
   flex: 1;
`;

const SwatchDot = styled.div<{ $color: string }>`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background: ${({ $color }) => $color};
   flex-shrink: 0;
`;

const SwatchLine = styled.div<{ $color: string }>`
   height: 4px;
   border-radius: 2px;
   background: ${({ $color }) => $color};
   opacity: 0.6;
   flex: 1;
`;

const SwatchLineShort = styled.div<{ $color: string }>`
   height: 4px;
   width: 16px;
   border-radius: 2px;
   background: ${({ $color }) => $color};
   opacity: 0.3;
   flex-shrink: 0;
`;

const SwatchBtn = styled.div<{ $color: string }>`
   height: 10px;
   border-radius: 4px;
   background: ${({ $color }) => $color};
`;

const ThemeMeta = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const ThemeName = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const ThemeDesc = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const ThemeFooter = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 6px;
`;

const SelectedPill = styled.span`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.SUCCESS};
   background: ${colors.SUCCESS}15;
   padding: 2px 8px;
   border-radius: 10px;
`;

const SelectPlaceholder = styled.span`
   width: 1px;
`;

const PreviewLink = styled.span`
   font-size: 10px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   cursor: pointer;
   padding: 2px 6px;
   border-radius: 4px;
   &:hover { background: ${colors.PRIMARY}12; }
`;

/* Danger Zone */

const OwnershipCard = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
   gap: 8px;
   padding: 18px 20px;
   background: linear-gradient(
      160deg,
      ${colors.PRIMARY}10 0%,
      ${colors.PRIMARY}05 100%
   );
   border: 1px solid ${colors.PRIMARY}30;
   border-radius: 10px;
`;

const OwnershipIcon = styled.div`
   font-size: 24px;
   line-height: 1;
`;

const OwnershipHeading = styled.h3`
   margin: 0;
   font-size: 14px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const OwnershipText = styled.p`
   font-size: 11px;
   line-height: 1.5;
   color: ${colors.GRAY3};
   margin: 0;
   max-width: 440px;
   strong {
      color: ${colors.GRAY_DARK};
      font-weight: 700;
   }
`;

const OwnershipFeatures = styled.ul`
   list-style: none;
   padding: 0;
   margin: 2px 0;
   display: flex;
   flex-direction: column;
   gap: 4px;
   align-items: flex-start;
`;

const OwnershipFeature = styled.li`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 11px;
   color: ${colors.GRAY3};
`;

const FeatureDot = styled.span<{ $color: string }>`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background: ${({ $color }) => $color};
   flex-shrink: 0;
`;

const StartTransferBtn = styled.button`
   margin-top: 4px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   padding: 9px 20px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   min-width: 180px;
   transition: opacity 0.15s;
   &:hover {
      opacity: 0.9;
   }
`;

const LoadingNote = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   padding: 12px;
`;

const PendingPanel = styled.div`
   padding: 14px 16px;
   background: ${colors.WARNING}10;
   border: 1px solid ${colors.WARNING}40;
   border-radius: 10px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const PendingMetaRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;

const PendingLabel = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.WARNING};
`;

const PendingPaymentBadge = styled.span<{
   $type: 'free' | 'full-cash' | 'installment';
}>`
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   background: ${({ $type }) =>
      $type === 'full-cash'
         ? `${colors.SUCCESS}1f`
         : $type === 'installment'
         ? `${colors.WARNING}26`
         : `${colors.PRIMARY}15`};
   color: ${({ $type }) =>
      $type === 'full-cash'
         ? colors.SUCCESS
         : $type === 'installment'
         ? colors.WARNING
         : colors.PRIMARY};
`;

const PendingBody = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   strong {
      color: ${colors.GRAY_DARK};
   }
`;

const PendingNote = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   background: ${colors.GRAY14};
   border-radius: 6px;
   padding: 6px 10px;
   font-style: italic;
`;

const PendingLinkRow = styled.div`
   display: flex;
   gap: 6px;
`;

const PendingLinkInput = styled.input`
   flex: 1;
   padding: 8px 10px;
   border-radius: 6px;
   border: 1px solid ${colors.GRAY11};
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 11px;
   color: ${colors.GRAY_DARK};
   background: ${colors.WHITE};
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const PendingCopyBtn = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   padding: 0 12px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover {
      opacity: 0.92;
   }
`;

const PendingHelp = styled.div`
   font-size: 10px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

const PendingCancelBtn = styled.button`
   align-self: flex-start;
   background: transparent;
   border: none;
   padding: 0;
   color: ${colors.DANGER};
   font-size: 11px;
   font-weight: 700;
   text-decoration: underline;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const DangerZone = styled.div`
   margin-top: 16px;
   padding: 12px;
   background: ${colors.DANGER}08;
   border: 1px solid ${colors.DANGER}40;
   border-radius: 8px;
`;

const DangerHeader = styled.h4`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.DANGER};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin: 0 0 6px 0;
`;

const DangerBody = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

/* One bordered box per danger action, so the stacked red buttons read as
   SEPARATE tools (lost/stolen vs remove-to-OpenWRT vs delete) instead of a
   menu of equal next steps — operators were reading them as "pick one". */
const DangerItem = styled.div`
   border: 1px solid ${colors.DANGER}30;
   border-radius: 8px;
   padding: 10px 12px;
   display: flex;
   flex-direction: column;
   gap: 8px;
   background: ${colors.WHITE};
`;

const DangerItemTitle = styled.h5`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY2};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin: 0;
`;

const DangerText = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const DangerBtn = styled.button`
   align-self: flex-start;
   font-family: inherit;
   font-size: 11px;
   font-weight: 700;
   padding: 6px 12px;
   background: ${colors.DANGER};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   cursor: pointer;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   &:hover { opacity: 0.9; }
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const ReflashBtn = styled.button`
   align-self: flex-start;
   font-family: inherit;
   font-size: 12px;
   font-weight: 600;
   padding: 8px 14px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   cursor: pointer;
   margin-top: 4px;
   &:hover { opacity: 0.9; }
   &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
   }
`;

/* Deliberately NOT the blue primary button. Blue reads as "the normal next
   thing to do"; this only appears when something is wrong, so it carries the
   same amber the Degraded pill uses. Amber fill with dark text — amber on
   white is too weak to read at this size. */
const DiagnoseBtn = styled.button`
   align-self: flex-start;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 8px 14px;
   background: ${colors.WARNING};
   /* Literal, not a theme token: the amber fill does not change between light
      and dark, so the text on it must not either — MAIN_DARK would flip to a
      pale colour in dark mode and become unreadable on yellow. */
   color: #4a3600;
   border: 1px solid ${colors.WARNING};
   border-radius: 8px;
   cursor: pointer;
   margin-top: 4px;
   &:hover {
      opacity: 0.9;
   }
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

/* ── Coin wiring (item E): Standard/Custom tabs + compact 2-col grid ── */
const PinTabRow = styled.div`
   display: inline-flex;
   align-self: flex-start; /* don't stretch to the Section's full width */
   gap: 2px;
   background: ${colors.GRAY14}; /* surface-muted track (theme-aware) */
   border: 1px solid ${colors.GRAY11};
   border-radius: 8px;
   padding: 2px;
   margin: 4px 0 10px;
`;
const PinTab = styled.button<{ $active?: boolean }>`
   font-family: inherit;
   font-size: 12px;
   font-weight: 600;
   padding: 5px 16px;
   border: none;
   border-radius: 6px;
   cursor: pointer;
   background: ${({ $active }) => ($active ? colors.WHITE : 'transparent')};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY6)};
   box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none')};
`;
const PinGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
   gap: 10px 14px;
   margin-top: 6px;
`;
const PinCell = styled.div`
   display: flex;
   flex-direction: column;
   gap: 3px;
`;
const PinLbl = styled.span`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY6};
`;
const PinVal = styled.span`
   font-size: 14px;
   font-weight: 600;
   font-family: ui-monospace, monospace;
   color: ${colors.GRAY8};
`;
const PinSelect = styled.select`
   font-family: inherit;
   font-size: 13px;
   padding: 7px 8px;
   border: 1px solid ${colors.GRAY3};
   border-radius: 8px;
   background: ${colors.WHITE};
   color: ${colors.GRAY8};
   cursor: pointer;
`;

const SsidRow = styled.div`
   display: flex;
   gap: 8px;
   margin-top: 4px;
`;

const SsidInput = styled.input`
   flex: 1;
   font-family: inherit;
   font-size: 13px;
   padding: 8px 12px;
   border: 1px solid #d8d8d8;
   border-radius: 8px;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const SsidSaveBtn = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 600;
   padding: 8px 16px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   cursor: pointer;
   &:hover { opacity: 0.9; }
   &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
   }
`;

const CoinBoxWaitNote = styled.div`
   font-size: 12px;
   line-height: 1.5;
   color: #8a6d1a;
   background: #fdf6e3;
   border: 1px solid #f0e0b0;
   border-radius: 8px;
   padding: 8px 12px;
   margin-top: 8px;
`;

const SwapAckList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin: 8px 0 4px;
`;

const SwapAckRow = styled.label`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   cursor: pointer;
`;

const SwapAckLabel = styled.span`
   font-size: 12px;
   line-height: 1.4;
   color: ${colors.MAIN_DARK};
`;

const ConfirmRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

const ConfirmText = styled.span`
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const CancelLink = styled.button`
   font-family: inherit;
   font-size: 11px;
   font-weight: 700;
   padding: 6px 10px;
   background: transparent;
   color: ${colors.GRAY3};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   cursor: pointer;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   &:hover {
      color: ${colors.MAIN_DARK};
      border-color: ${colors.GRAY10};
   }
`;

// Rates tab content. Toggles between Time-rate mode (existing per-device
// rate editor) and Data-rate mode (read-only display of the linked
// DataPlan's rates). Mode is mutually exclusive — driven by the
// device's activeRateMode field, which the Data Plans modal flips on
// link/unlink. The toggle here is informational; the real switch
// happens by linking/unlinking from the Data Plans page.
type RateTab = 'coin' | 'voucher';

// Exported for the mount test: this panel is where an operator types a rate's
// "Valid for", and the value has to survive from the keystroke to updateRate.
// Mounting the whole settings modal to prove that would test everything except
// the thing in question.
export function RatesTabPanel({
   apiDevice,
   deviceId,
   rates,
   updateRate,
   removeRate,
   addRate,
   ratePerPeso,
   setRatePerPeso,
}: {
   apiDevice: ApiVendoDevice | undefined;
   deviceId: string;
   rates: RatePlan[];
   updateRate: (key: string, field: keyof RatePlan, value: any) => void;
   removeRate: (key: string) => void;
   addRate: (type: RateTab) => string;
   ratePerPeso: string;
   setRatePerPeso: (value: string) => void;
}) {
   const isDataMode = apiDevice?.activeRateMode === 'data' && !!apiDevice?.activeDataPlan;
   const [rateTab, setRateTab] = useState<RateTab>('coin');
   const [applyOpen, setApplyOpen] = useState(false);

   // Mobile-only UX (Ray 2026-07-08, mirrors the client portal): rows
   // render read-only (₱price · label · duration) and only the tapped
   // row expands to the editable card. Desktop keeps the always-editable
   // grid unchanged. New draft rates (blank price) open expanded.
   const isMobile = useIsMobile();
   const [editingKey, setEditingKey] = useState<string | null>(null);
   const rowExpanded = (r: RatePlan) => !isMobile || editingKey === r.key;
   const fmtMins = (mins: number) => {
      const d = decomposeMinutes(mins);
      const unit = d.value === 1 ? d.unit.slice(0, -1) : d.unit;
      return `${d.value} ${unit}`;
   };
   /* A refused duration/pause entry, keyed "<rate key>:<field>". Shown as
      ONE full-width banner above the rows rather than under the box —
      inline it grew the row and pushed Price / Label out of line. */
   const [rateErrors, setRateErrors] = useState<Record<string, string>>({});
   const noteRateError = (slot: string, message: string | null) =>
      setRateErrors((prev) => {
         if (!message) {
            if (!(slot in prev)) return prev;
            const { [slot]: _drop, ...rest } = prev;
            return rest;
         }
         if (prev[slot] === message) return prev;
         return { ...prev, [slot]: message };
      });

   const [bulkPrintOpen, setBulkPrintOpen] = useState(false);
   const [voucherInventoryOpen, setVoucherInventoryOpen] = useState(false);

   // Fetch the linked data plan so we can tell whether it has any
   // voucher-method rates. When it does, the Voucher sub-tab swaps to a
   // read-only "Linked to <plan>" view (mirroring Coin). When it doesn't,
   // the Voucher sub-tab falls back to the device's editable VendoRate
   // voucher rows so the operator can still configure / sell time
   // vouchers while they're building out the data plan.
   const { data: linkedPlan } = useQuery({
      queryKey: ['data-plan', apiDevice?.activeDataPlan ?? null],
      queryFn: () => getDataPlan(apiDevice!.activeDataPlan!),
      enabled: !!apiDevice?.activeDataPlan,
   });
   const dataPlanHasVoucherRates = !!(linkedPlan?.rates ?? []).find(
      (r) => r.method === 'voucher'
   );

   const visibleRates = rates.filter((r) => r.type === rateTab);
   const coinCount = rates.filter((r) => r.type === 'coin').length;
   const voucherCount = rates.filter((r) => r.type === 'voucher').length;

   return (
      <Panel>
         <ModeBanner $variant={isDataMode ? 'data' : 'time'}>
            <ModeBannerLbl>
               {isDataMode ? 'Data + Voucher rates' : 'Time rates'}
            </ModeBannerLbl>
            <ModeBannerSub>
               {isDataMode
                  ? 'Coin transactions are sourced from the linked Data Plan. Voucher rates stay editable here.'
                  : 'Coin rates are matched when a customer drops coins into the vendo. Voucher rates are surfaced in the captive portal\'s Voucher tab — customers buy them with GCash or redeem a printed code.'}
            </ModeBannerSub>
         </ModeBanner>

         <SubTabBar>
            <SubTabBtn
               $active={rateTab === 'coin'}
               onClick={() => setRateTab('coin')}
               type="button"
            >
               Coin
               <SubTabCount>{coinCount}</SubTabCount>
            </SubTabBtn>
            <SubTabBtn
               $active={rateTab === 'voucher'}
               onClick={() => setRateTab('voucher')}
               type="button"
            >
               Voucher
               <SubTabCount>{voucherCount}</SubTabCount>
            </SubTabBtn>
         </SubTabBar>

         {/* Data mode swaps the editable rows for a read-only "Linked to
             plan" view per sub-tab. Coin tab always swaps when data
             mode. Voucher tab only swaps when the linked plan actually
             has voucher-method rates — otherwise we keep showing the
             editable time-voucher rows as a fallback. */}
         {rateTab === 'coin' && isDataMode && (
            <DataPlanReadOnly planId={apiDevice!.activeDataPlan!} method="coin" />
         )}
         {rateTab === 'voucher' && isDataMode && dataPlanHasVoucherRates && (
            <DataPlanReadOnly planId={apiDevice!.activeDataPlan!} method="voucher" />
         )}

         {rateTab === 'coin' && !isDataMode && (
            <PerPesoRow>
               <PerPesoLbl>Minutes per ₱1</PerPesoLbl>
               <CompactInput
                  type="number"
                  min={1}
                  $w="90px"
                  value={ratePerPeso}
                  placeholder="6"
                  onChange={(e) => setRatePerPeso(e.target.value)}
               />
               <PerPesoHint>
                  Default conversion when a customer drops a coin that doesn't
                  match a configured rate exactly. Example: at <strong>6 min/₱1</strong>,
                  a ₱5 drop with no matching rate grants 30 minutes.
               </PerPesoHint>
            </PerPesoRow>
         )}

         {/* Rates table + actions hide whenever the DataPlanReadOnly
             panel above is showing — that is, Coin sub-tab in data
             mode, OR Voucher sub-tab in data mode with voucher-method
             rates on the plan. Voucher sub-tab without plan voucher
             rates keeps its editable VendoRate rows as fallback. */}
         {!(
            (rateTab === 'coin' && isDataMode) ||
            (rateTab === 'voucher' && isDataMode && dataPlanHasVoucherRates)
         ) && (
            <>
               {(() => {
                  const shown = visibleRates
                     .flatMap((r) => [
                        rateErrors[`${r.key}:duration`],
                        rateErrors[`${r.key}:validity`],
                     ])
                     .find(Boolean);
                  return shown ? (
                     <RateErrorBanner role="alert">{shown}</RateErrorBanner>
                  ) : null;
               })()}
               <RateHeader>
                  <RateHeaderCell $w="80px">Price (₱)</RateHeaderCell>
                  <RateHeaderCell $w="160px">Duration</RateHeaderCell>
                  <RateHeaderCell $w="180px">Validity (pause)</RateHeaderCell>
                  <RateHeaderCell>Label</RateHeaderCell>
                  <RateHeaderCell $w="32px"></RateHeaderCell>
               </RateHeader>
               {visibleRates.map((r) =>
                  rowExpanded(r) && isMobile ? (
                     /* Mobile edit card — labeled fields, portal-style. */
                     <RateEditCard key={r.key}>
                        <EditField>
                           <EditFieldLabel>Price ₱</EditFieldLabel>
                           <CompactInput
                              type="number"
                              value={r.price}
                              placeholder="5"
                              onChange={(e) => updateRate(r.key, 'price', e.target.value)}
                           />
                        </EditField>
                        <EditField>
                           <EditFieldLabel>Duration</EditFieldLabel>
                           <DurationUnitInput
                              value={Number(r.duration) > 0 ? Number(r.duration) : undefined}
                              onChange={(mins) =>
                                 updateRate(r.key, 'duration', mins != null ? String(mins) : '')
                              }
                              onError={(m) => noteRateError(`${r.key}:duration`, m)}
                              min={1}
                              compact
                           />
                        </EditField>
                        <EditField $full>
                           <EditFieldLabel>Validity (pause)</EditFieldLabel>
                           <DurationUnitInput
                              value={r.validityMinutes}
                              onChange={(mins) => updateRate(r.key, 'validityMinutes', mins as any)}
                              onError={(m) => noteRateError(`${r.key}:validity`, m)}
                              min={0}
                              compact
                           />
                           <RateFieldHint>
                              Example: 7 days. If the customer pauses and stays
                              away 7 days in total, the time left is gone.
                              Coming back does not reset it — pause 6 days and
                              only 1 day is left. Set 0 to keep it for ever.
                           </RateFieldHint>
                        </EditField>
                        <EditField $full>
                           <EditFieldLabel>Name (optional)</EditFieldLabel>
                           <CompactInput
                              value={r.label}
                              placeholder={rateTab === 'voucher' ? 'e.g. Day Pass' : 'e.g. Quick Browse'}
                              onChange={(e) => updateRate(r.key, 'label', e.target.value)}
                           />
                        </EditField>
                        {/* No delete inside EDIT mode (Ray) — removing a
                            rate lives on the read-only row's ×. */}
                        <RateDoneBtn type="button" onClick={() => setEditingKey(null)}>
                           Done
                        </RateDoneBtn>
                     </RateEditCard>
                  ) : rowExpanded(r) ? (
                     <RateRow key={r.key}>
                        <CompactInput
                           type="number"
                           $w="80px"
                           value={r.price}
                           placeholder="5"
                           onChange={(e) => updateRate(r.key, 'price', e.target.value)}
                        />
                        <DurationCell $w="160px">
                           <DurationUnitInput
                              value={Number(r.duration) > 0 ? Number(r.duration) : undefined}
                              onChange={(mins) =>
                                 updateRate(r.key, 'duration', mins != null ? String(mins) : '')
                              }
                              onError={(m) => noteRateError(`${r.key}:duration`, m)}
                              min={1}
                              compact
                           />
                        </DurationCell>
                        <DurationCell
                           $w="180px"
                           title="How long paused time is kept. Counts only while the customer is away, and coming back does not reset it — pause 6 days out of 7 and 1 day is left. 0 = kept for ever."
                        >
                           <DurationUnitInput
                              value={r.validityMinutes}
                              onChange={(mins) => updateRate(r.key, 'validityMinutes', mins as any)}
                              onError={(m) => noteRateError(`${r.key}:validity`, m)}
                              min={0}
                              compact
                           />
                        </DurationCell>
                        <CompactInput
                           value={r.label}
                           placeholder={rateTab === 'voucher' ? 'e.g. Day Pass' : 'e.g. Quick Browse'}
                           onChange={(e) => updateRate(r.key, 'label', e.target.value)}
                        />
                        <RemoveBtn onClick={() => removeRate(r.key)} title="Remove rate">×</RemoveBtn>
                     </RateRow>
                  ) : (
                     <RateReadRow key={r.key}>
                        <RateReadPrice>₱{r.price}</RateReadPrice>
                        <RateReadInfo>
                           <RateReadLabel>
                              {r.label || (Number(r.duration) > 0 ? fmtMins(Number(r.duration)) : '—')}
                           </RateReadLabel>
                           <RateReadTime>
                              {Number(r.duration) > 0 ? fmtMins(Number(r.duration)) : 'no duration'}
                              {r.validityMinutes != null &&
                                 ` · pause ${fmtMins(r.validityMinutes)}`}
                           </RateReadTime>
                        </RateReadInfo>
                        <RateEditBtn type="button" onClick={() => setEditingKey(r.key)}>
                           Edit
                        </RateEditBtn>
                        <RemoveBtn onClick={() => removeRate(r.key)} title="Remove rate">×</RemoveBtn>
                     </RateReadRow>
                  )
               )}
               {visibleRates.length === 0 && (
                  <EmptyNote>
                     {rateTab === 'voucher'
                        ? 'No voucher rates. Click "+ Add Rate" to create one — customers will see it on the captive portal\'s Voucher tab.'
                        : 'No coin rates. Click "+ Add Rate" to create one.'}
                  </EmptyNote>
               )}
               <RateActionsRow>
                  <AddRateBtn
                     onClick={() => {
                        const key = addRate(rateTab);
                        if (isMobile) setEditingKey(key);
                     }}
                  >
                     + Add Rate
                  </AddRateBtn>
                  {(() => {
                     const currentCount = rateTab === 'voucher' ? voucherCount : coinCount;
                     return (
                        <ApplyBtn
                           type="button"
                           onClick={() => setApplyOpen(true)}
                           disabled={currentCount === 0}
                           title={
                              currentCount === 0
                                 ? `Add at least one ${rateTab} rate first`
                                 : `Copy these ${rateTab} rates to your other devices`
                           }
                        >
                           Apply to other devices
                        </ApplyBtn>
                     );
                  })()}
               </RateActionsRow>
            </>
         )}

         {/* Bulk-print button surfaces on the Voucher tab regardless of
             whether the editable VendoRate block is showing (time mode
             or fallback) or whether DataPlanReadOnly took over (data
             mode with voucher rates on the plan). Operator can print
             time vouchers OR data vouchers — the modal merges both
             sources into one dropdown. */}
         {rateTab === 'voucher' && (() => {
            const savedTimeVoucherCount = rates.filter(
               (r) => r.type === 'voucher' && r._id
            ).length;
            const dataVoucherRatesCount = (linkedPlan?.rates ?? []).filter(
               (r) => r.method === 'voucher'
            ).length;
            const totalPrintable = savedTimeVoucherCount + dataVoucherRatesCount;
            return (
               <BulkPrintRow>
                  <ApplyBtn
                     type="button"
                     onClick={() => setBulkPrintOpen(true)}
                     disabled={totalPrintable === 0}
                     title={
                        totalPrintable === 0
                           ? 'Save at least one voucher rate (time or data) first'
                           : 'Print physical voucher cards on A4 paper'
                     }
                  >
                     Bulk print vouchers
                  </ApplyBtn>
                  <ApplyBtn
                     type="button"
                     onClick={() => setVoucherInventoryOpen(true)}
                     title="Reprint unused vouchers or browse redeemed history"
                  >
                     Vouchers · reprint &amp; history
                  </ApplyBtn>
               </BulkPrintRow>
            );
         })()}

         {applyOpen && (
            <ApplyRatesToDevicesModal
               sourceDeviceId={deviceId}
               type={rateTab}
               onClose={() => setApplyOpen(false)}
            />
         )}
         {bulkPrintOpen && (
            <BulkPrintGenerateModal
               deviceId={deviceId}
               voucherRates={visibleRates.filter((r): r is RatePlan & { _id: string } => !!r._id)}
               dataPlanId={apiDevice?.activeDataPlan ?? null}
               onClose={() => setBulkPrintOpen(false)}
            />
         )}
         {voucherInventoryOpen && (
            <VoucherInventoryModal
               deviceId={deviceId}
               onClose={() => setVoucherInventoryOpen(false)}
            />
         )}
      </Panel>
   );
}

// Modal that lets the owner copy the current voucher rates from this
// device to one or more of their other vendos. Sub-vendos are listed
// indented under their parent so the operator can target a sub
// independently of the main (rates live per-device, not per-cluster).
function ApplyRatesToDevicesModal({
   sourceDeviceId,
   type,
   onClose,
}: {
   sourceDeviceId: string;
   type: 'coin' | 'voucher';
   onClose: () => void;
}) {
   const queryClient = useQueryClient();
   const [selected, setSelected] = useState<Set<string>>(new Set());
   const [applying, setApplying] = useState(false);
   // Off by default: the other vendo keeps any extra price it already sells.
   const [replace, setReplace] = useState(false);

   const { data: devicesData, isLoading } = useQuery({
      queryKey: ['vendo-devices-for-apply'],
      queryFn: listDevices,
   });

   // Pending outgoing transfers — same filter rule the data-plan link
   // picker uses. React Query reuses the cached entry across the app.
   const { data: outgoingTransfers } = useQuery({
      queryKey: ['transfers', 'outgoing'],
      queryFn: () => listTransfersApi('outgoing'),
   });
   const pendingTransferIds = buildPendingTransferIds(outgoingTransfers);

   // Owner-owned, setup-done, no pending transfer. The copy endpoint
   // also rejects sold devices server-side; the picker filter just keeps
   // the dropdown clean of rows that would 403 anyway.
   const all = (devicesData?.devices ?? []).filter(
      (d) => isPickableDevice(d, pendingTransferIds)
   );
   // Group: mains first (with their subs nested). Exclude the source
   // device. A device with parent=null is a main; everything else hangs
   // off the main via parent.
   const mains = all.filter((d) => !d.parent && d._id !== sourceDeviceId);
   const subsByParent = new Map<string, ApiVendoDevice[]>();
   for (const d of all) {
      if (d.parent && d._id !== sourceDeviceId) {
         const list = subsByParent.get(d.parent) ?? [];
         list.push(d);
         subsByParent.set(d.parent, list);
      }
   }
   // Source device might itself be a sub — surface its sibling subs
   // separately if so (they'd otherwise be hidden because the source
   // main is filtered out only via _id check, not by being source's
   // parent).
   const sourceDevice = all.find((d) => d._id === sourceDeviceId);
   const sourceParentId = sourceDevice?.parent || null;

   const toggle = (id: string) => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   };

   const handleApply = async () => {
      if (applying) return;
      if (selected.size === 0) {
         toast.error('Pick at least one device to apply to');
         return;
      }
      setApplying(true);
      try {
         const result = await copyRatesToDevicesApi({
            sourceDeviceId,
            targetDeviceIds: Array.from(selected),
            type,
            replace,
         });
         queryClient.invalidateQueries({ queryKey: ['device-rates'] });
         const parts = [`Updated ${result.updated}`, `added ${result.created}`];
         if (result.removed) parts.push(`removed ${result.removed}`);
         toast.success(parts.join(' · '), { autoClose: 3500 });
         onClose();
      } catch (err: any) {
         const errMsg =
            err?.response?.data?.error ?? err?.message ?? 'Failed to copy rates';
         toast.error(errMsg);
         setApplying(false);
      }
   };

   return createPortal(
      <ApplyDrawerBackdrop onClick={onClose}>
         <ApplyDrawer onClick={(e) => e.stopPropagation()}>
            <ApplyDrawerHead>
               <div>
                  <ApplyDrawerTitle>Apply {type} rates</ApplyDrawerTitle>
                  <ApplyDrawerSub>
                     Each selected vendo gets the same {type} rates as this one.
                     A rate at the same price is updated to match; a price it
                     does not have yet is added.
                  </ApplyDrawerSub>
               </div>
               <ApplyDrawerClose
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
               >
                  ×
               </ApplyDrawerClose>
            </ApplyDrawerHead>

            <ApplyDrawerListWrap>
               {isLoading ? (
                  <SkeletonLines lines={3} />
               ) : mains.length === 0 && subsByParent.size === 0 ? (
                  <ApplyDrawerEmpty>
                     You have no other vendo devices to apply to.
                  </ApplyDrawerEmpty>
               ) : (
                  <>
                     {mains.map((main) => {
                        const subs = subsByParent.get(main._id) ?? [];
                        return (
                           <React.Fragment key={main._id}>
                              <ApplyDrawerRow
                                 onClick={() => toggle(main._id)}
                                 $sub={false}
                              >
                                 <ApplyCheckbox $checked={selected.has(main._id)}>
                                    {selected.has(main._id) && (
                                       <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M20 6 9 17l-5-5" />
                                       </svg>
                                    )}
                                 </ApplyCheckbox>
                                 <ApplyDrawerRowBody>
                                    <ApplyDrawerRowName>{main.name}</ApplyDrawerRowName>
                                    <ApplyDrawerRowId>{main.deviceId}</ApplyDrawerRowId>
                                 </ApplyDrawerRowBody>
                              </ApplyDrawerRow>
                              {subs.map((sub) => (
                                 <ApplyDrawerRow
                                    key={sub._id}
                                    onClick={() => toggle(sub._id)}
                                    $sub
                                 >
                                    <ApplyCheckbox $checked={selected.has(sub._id)}>
                                       {selected.has(sub._id) && (
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                             <path d="M20 6 9 17l-5-5" />
                                          </svg>
                                       )}
                                    </ApplyCheckbox>
                                    <ApplyDrawerRowBody>
                                       <ApplyDrawerRowName>{sub.name}</ApplyDrawerRowName>
                                       <ApplyDrawerRowId>sub of {main.name}</ApplyDrawerRowId>
                                    </ApplyDrawerRowBody>
                                 </ApplyDrawerRow>
                              ))}
                           </React.Fragment>
                        );
                     })}
                     {sourceParentId &&
                        (subsByParent.get(sourceParentId)?.length ?? 0) > 0 && (
                           <>
                              <ApplyDrawerGroupHead>Sibling sub-vendos</ApplyDrawerGroupHead>
                              {(subsByParent.get(sourceParentId) ?? []).map((sib) => (
                                 <ApplyDrawerRow
                                    key={sib._id}
                                    onClick={() => toggle(sib._id)}
                                    $sub
                                 >
                                    <ApplyCheckbox $checked={selected.has(sib._id)}>
                                       {selected.has(sib._id) && (
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                             <path d="M20 6 9 17l-5-5" />
                                          </svg>
                                       )}
                                    </ApplyCheckbox>
                                    <ApplyDrawerRowBody>
                                       <ApplyDrawerRowName>{sib.name}</ApplyDrawerRowName>
                                       <ApplyDrawerRowId>sibling sub</ApplyDrawerRowId>
                                    </ApplyDrawerRowBody>
                                 </ApplyDrawerRow>
                              ))}
                           </>
                        )}
                  </>
               )}
            </ApplyDrawerListWrap>

            <ApplyReplaceRow>
               <input
                  type="checkbox"
                  checked={replace}
                  onChange={(e) => setReplace(e.target.checked)}
                  disabled={applying}
               />
               <span>
                  Also remove {type} rates the other vendo has that this one does not
               </span>
            </ApplyReplaceRow>

            <ApplyDrawerFoot>
               <ApplyCancelBtn onClick={onClose} disabled={applying} type="button">
                  Cancel
               </ApplyCancelBtn>
               <ApplyPrimaryBtn
                  onClick={handleApply}
                  disabled={applying || selected.size === 0}
                  type="button"
               >
                  {applying ? 'Applying…' : `Apply to ${selected.size}`}
               </ApplyPrimaryBtn>
            </ApplyDrawerFoot>
         </ApplyDrawer>
      </ApplyDrawerBackdrop>,
      document.body
   );
}

/* ───── Bulk Print Vouchers (two-modal flow) ─────────────────
 *
 * Modal 1 (Generate): operator picks rate + copies + branding
 *   (store name, logo). On submit we POST /api/vendo/vouchers/
 *   bulk-generate and the server mints N codes.
 *
 * Modal 2 (Preview): the freshly minted codes are laid out on an
 *   A4 canvas — 50.8mm × 38.1mm cards (2in × 1.5in) in a 3×6 grid
 *   per page. An "Include amount and hours" checkbox toggles those
 *   fields per card. The Print button calls window.print() and
 *   a print stylesheet hides everything except the canvas so the
 *   paper output is clean.
 *
 * Logo upload is client-only — we never send it to the server.
 * The file becomes a data URL via FileReader, embedded inline in
 * the printable cards. Keeps the server stateless about branding
 * (operators can re-print at any moment with different branding).
 */

// Last-used store name for printed cards — shared between the bulk
// print modal and the reprint drawer so reprinted cards carry the same
// branding without retyping.
const PRINT_STORE_NAME_KEY = 'smartfi.print.storeName';

/**
 * Voucher inventory drawer — the two lists operators asked for:
 *   - Unredeemed: still-valid codes, checkbox-selectable, PRINT AGAIN
 *     (lost/damaged cards). Reuses the same off-screen A4 print canvas
 *     as bulk print, so reprints look identical to originals.
 *   - Redeemed: read-only history, newest redemption first. Legacy rows
 *     redeemed before `redeemedAt` existed fall back to their update
 *     time (marked with ~).
 * Wide drawer, not tall — per the modal width-over-height rule.
 */
export function VoucherInventoryModal({
   deviceId,
   onClose,
}: {
   /** Omit for the owner-wide list (the /wifi-vouchers page). */
   deviceId?: string;
   onClose: () => void;
}) {
   const [view, setView] = useState<'unredeemed' | 'redeemed'>('unredeemed');
   const [page, setPage] = useState(1);
   const [selected, setSelected] = useState<Set<string>>(new Set());
   // Rate filter. '' = every rate. A voucher stores no rate reference,
   // so a rate here IS its price+duration pair — see the API types.
   const [rateKey, setRateKey] = useState('');
   const [batch, setBatch] = useState('');
   // Ray 2026-09-15: time vs data, one vendo (owner-wide list), code search,
   // and paging off → one tall scrolling list.
   const [kind, setKind] = useState<'' | 'time' | 'data'>('');
   const [vendo, setVendo] = useState('');
   const [q, setQ] = useState('');
   const [paginate, setPaginate] = useState(true);
   const allDevicesForFilter = useVendoDevicesStore((st) => st.devices);
   const devicesFetchedForFilter = useVendoDevicesStore((st) => st.fetched);
   const fetchDevicesForFilter = useVendoDevicesStore((st) => st.fetch);
   // The owner-wide Vouchers page never loads the devices store, so the
   // Vendo dropdown showed only "All vendos" (Ray 2026-09-15).
   useEffect(() => {
      if (!deviceId && !devicesFetchedForFilter) void fetchDevicesForFilter();
   }, [deviceId, devicesFetchedForFilter, fetchDevicesForFilter]);
   const [confirmDelete, setConfirmDelete] = useState(false);
   const [deleting, setDeleting] = useState(false);
   const queryClient = useQueryClient();
   const [exporting, setExporting] = useState(false);
   const [exportError, setExportError] = useState<string | null>(null);
   // Prefill from the last print run (bulk or reprint) — operators kept
   // getting nameless reprint cards because this started empty.
   const [storeName, setStoreName] = useState(
      () => localStorage.getItem(PRINT_STORE_NAME_KEY) || ''
   );
   const [includeDetails, setIncludeDetails] = useState(true);
   const LIMIT = paginate ? 10 : 2000;

   // price+duration parsed back out of the dropdown's value.
   const rateFilter = useMemo(() => {
      if (!rateKey) return {};
      const [price, duration] = rateKey.split(':').map(Number);
      return Number.isFinite(price) && Number.isFinite(duration)
         ? { price, duration }
         : {};
   }, [rateKey]);

   const { data, isLoading } = useQuery({
      // rateKey is in the key or a filter change would serve the
      // previous rate's page from cache.
      queryKey: ['device-vouchers', deviceId ?? 'all', view, page, rateKey, batch, kind, vendo, q, paginate],
      queryFn: () =>
         listDeviceVouchersApi({
            deviceId,
            status: view,
            page: paginate ? page : 1,
            limit: LIMIT,
            ...(paginate ? {} : { all: 1 }),
            ...rateFilter,
            ...(batch ? { batch } : {}),
            ...(kind ? { kind } : {}),
            ...(vendo && !deviceId ? { vendo } : {}),
            ...(q.trim() ? { q: q.trim() } : {}),
         } as any),
   });

   const { data: groupData } = useQuery({
      queryKey: ['voucher-rate-groups', deviceId ?? 'all', view, kind, vendo],
      queryFn: () => listVoucherRateGroupsApi({ deviceId, status: view, ...(kind ? { kind } : {}), ...(vendo && !deviceId ? { vendo } : {}) } as any),
   });
   const rateGroups = groupData?.groups ?? [];
   const batches = groupData?.batches ?? [];
   const rows = data?.vouchers ?? [];

   // Delete selected: first tap arms, second tap (within 6 s) deletes.
   // Used codes are never deleted from here — they are sales records.
   const handleDeleteSelected = async () => {
      if (deleting || selected.size === 0) return;
      if (!confirmDelete) {
         setConfirmDelete(true);
         setTimeout(() => setConfirmDelete(false), 6000);
         return;
      }
      setDeleting(true);
      try {
         const r = await bulkDeleteVouchersApi(Array.from(selected));
         toast.success(
            r.skipped
               ? `Deleted ${r.deleted} · kept ${r.skipped} used code${r.skipped === 1 ? '' : 's'}`
               : `Deleted ${r.deleted} voucher${r.deleted === 1 ? '' : 's'}`
         );
         setSelected(new Set());
         queryClient.invalidateQueries({ queryKey: ['device-vouchers'] });
         queryClient.invalidateQueries({ queryKey: ['voucher-rate-groups'] });
      } catch (err: any) {
         toast.error(err?.response?.data?.error ?? 'Could not delete');
      } finally {
         setDeleting(false);
         setConfirmDelete(false);
      }
   };
   const total = data?.total ?? 0;
   const totalPages = Math.max(1, Math.ceil(total / LIMIT));

   const toggle = (id: string) =>
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   const allOnPageSelected =
      rows.length > 0 && rows.every((v) => selected.has(v._id));
   const toggleAllOnPage = () =>
      setSelected((prev) => {
         const next = new Set(prev);
         if (allOnPageSelected) rows.forEach((v) => next.delete(v._id));
         else rows.forEach((v) => next.add(v._id));
         return next;
      });

   // Print rows are accumulated ACROSS pages: keep every selected row we
   // have seen so the operator can pick from page 1 and page 3 in one
   // print run. Map survives page flips; entries drop when deselected.
   const seenRowsRef = useRef<Map<string, ApiDeviceVoucher>>(new Map());
   rows.forEach((v) => seenRowsRef.current.set(v._id, v));
   const printRows = Array.from(selected)
      .map((id) => seenRowsRef.current.get(id))
      .filter((v): v is ApiDeviceVoucher => !!v);

   const handlePrint = () => {
      if (printRows.length === 0) return;
      try {
         localStorage.setItem(PRINT_STORE_NAME_KEY, storeName.trim());
      } catch { /* private mode — non-fatal */ }
      window.print();
   };

   const switchView = (v: 'unredeemed' | 'redeemed') => {
      setView(v);
      setPage(1);
      // The two tabs have different rate groups behind them, so a filter
      // carried across would show "no vouchers" for a rate that has some.
      setRateKey('');
      setSelected(new Set());
      setExportError(null);
   };

   /** Changing the rate MUST clear the selection. printRows accumulates
    *  across pages, so without this an operator filters to ₱20, hits
    *  Print, and gets ₱50 cards they ticked three filters ago. */
   const changeRate = (key: string) => {
      setRateKey(key);
      setPage(1);
      setSelected(new Set());
      seenRowsRef.current.clear();
      setExportError(null);
   };

   const rateGroupLabel = (g: ApiVoucherRateGroup) =>
      `₱${g.price} · ${
         g.capMb != null ? fmtDataCap(g.capMb) : formatPrintMinutes(g.duration)
      } (${g.count})`;

   const activeRateLabel = rateGroups.find(
      (g) => `${g.price}:${g.duration}` === rateKey
   );

   const handleExport = async () => {
      setExporting(true);
      setExportError(null);
      try {
         const { blob, filename } = await exportVouchersCsvApi({
            deviceId,
            status: view,
            ...rateFilter,
         });
         const url = URL.createObjectURL(blob);
         const a = document.createElement('a');
         a.href = url;
         a.download = filename;
         document.body.appendChild(a);
         a.click();
         a.remove();
         URL.revokeObjectURL(url);
      } catch (err: any) {
         // The server refuses an export above its row cap with a readable
         // message; surface that rather than a bare "failed".
         let msg = 'Could not build the file. Try again.';
         const data = err?.response?.data;
         if (data instanceof Blob) {
            try {
               msg = JSON.parse(await data.text()).error || msg;
            } catch { /* not JSON — keep the generic line */ }
         } else if (data?.error) {
            msg = data.error;
         }
         setExportError(msg);
      } finally {
         setExporting(false);
      }
   };

   const fmtWhen = (v: ApiDeviceVoucher) => {
      if (view === 'redeemed') {
         return v.redeemedAt
            ? new Date(v.redeemedAt).toLocaleString()
            : `~${new Date(v.updatedAt).toLocaleString()}`;
      }
      return new Date(v.createdAt).toLocaleDateString();
   };
   const fmtRate = (v: ApiDeviceVoucher) =>
      v.capMb != null ? fmtDataCap(v.capMb) : formatPrintMinutes(v.duration);

   const body = (
      <>
         <PrintOverlay className="bulk-print-overlay" onClick={onClose}>
            <PrintDialog
               className="bulk-print-dialog"
               onClick={(e) => e.stopPropagation()}
               style={{ maxWidth: 720 }}
            >
               <PrintDrawerBody>
                  <VInvTabRow>
                     <VInvTab
                        type="button"
                        $active={view === 'unredeemed'}
                        onClick={() => switchView('unredeemed')}
                     >
                        Unredeemed
                     </VInvTab>
                     <VInvTab
                        type="button"
                        $active={view === 'redeemed'}
                        onClick={() => switchView('redeemed')}
                     >
                        Redeemed history
                     </VInvTab>
                     <VInvCount>
                        {total} voucher{total === 1 ? '' : 's'}
                     </VInvCount>
                  </VInvTabRow>

                  <VInvFilters>
                     <VInvField>
                        <label htmlFor="voucher-kind-filter">Kind</label>
                        <VInvRateSelect
                           id="voucher-kind-filter"
                           value={kind}
                           onChange={(e) => { setKind(e.target.value as '' | 'time' | 'data'); setPage(1); }}
                        >
                           <option value="">Time and data</option>
                           <option value="time">Time rates</option>
                           <option value="data">Data rates</option>
                        </VInvRateSelect>
                     </VInvField>
                     {!deviceId && (
                        <VInvField>
                           <label htmlFor="voucher-vendo-filter">Vendo</label>
                           <VInvRateSelect
                              id="voucher-vendo-filter"
                              value={vendo}
                              onChange={(e) => { setVendo(e.target.value); setPage(1); }}
                           >
                              <option value="">All vendos</option>
                              {allDevicesForFilter.map((d) => (
                                 <option key={d._id} value={d._id}>{d.name}</option>
                              ))}
                           </VInvRateSelect>
                        </VInvField>
                     )}
                     {rateGroups.length > 1 && (
                        <VInvField>
                           <label htmlFor="voucher-rate-filter">Rate</label>
                           <VInvRateSelect
                              id="voucher-rate-filter"
                              value={rateKey}
                              onChange={(e) => changeRate(e.target.value)}
                           >
                              <option value="">
                                 All rates ({rateGroups.reduce((n, g) => n + g.count, 0)})
                              </option>
                              {rateGroups.map((g) => (
                                 <option
                                    key={`${g.price}:${g.duration}`}
                                    value={`${g.price}:${g.duration}`}
                                 >
                                    {rateGroupLabel(g)}
                                 </option>
                              ))}
                           </VInvRateSelect>
                        </VInvField>
                     )}
                     {batches.length > 0 && (
                        <VInvField>
                           <label htmlFor="voucher-batch-filter">Batch</label>
                           <VInvRateSelect
                              id="voucher-batch-filter"
                              value={batch}
                              onChange={(e) => {
                                 setBatch(e.target.value);
                                 setPage(1);
                              }}
                           >
                              <option value="">All batches</option>
                              {batches.map((b) => (
                                 <option key={b.label} value={b.label}>
                                    {b.label} ({b.count})
                                 </option>
                              ))}
                           </VInvRateSelect>
                        </VInvField>
                     )}
                  </VInvFilters>
                  <VInvFilters>
                     <VInvField $wide>
                        <label htmlFor="voucher-code-search">Search code</label>
                        <VInvSearch
                           id="voucher-code-search"
                           type="text"
                           placeholder="e.g. Z74Z8LXC"
                           value={q}
                           onChange={(e) => { setQ(e.target.value.toUpperCase()); setPage(1); }}
                        />
                     </VInvField>
                     <VInvToggle title="On: 10 per page. Off: one long list you scroll.">
                        <input
                           type="checkbox"
                           checked={paginate}
                           onChange={(e) => { setPaginate(e.target.checked); setPage(1); }}
                        />
                        Pages
                     </VInvToggle>
                  </VInvFilters>

                  {isLoading ? (
                     <SkeletonLines lines={3} />
                  ) : rows.length === 0 ? (
                     <VInvEmpty>
                        {rateKey
                           ? 'No vouchers on that rate. Pick "All rates" to see the rest.'
                           : view === 'unredeemed'
                           ? 'No unredeemed vouchers. Bulk-print a batch first.'
                           : 'No redeemed vouchers yet.'}
                     </VInvEmpty>
                  ) : (
                     <VInvTable $tall={!paginate}>
                     <VInvScroll>
                        <VInvHead $withCheck={view === 'unredeemed'} $withVendo={!deviceId}>
                           {view === 'unredeemed' && (
                              <input
                                 type="checkbox"
                                 checked={allOnPageSelected}
                                 onChange={toggleAllOnPage}
                                 title="Select everything on this page"
                              />
                           )}
                           <span>Code</span>
                           <span>Rate</span>
                           {!deviceId && <span>Vendo</span>}
                           <span>Type</span>
                           <span>{view === 'redeemed' ? 'Redeemed' : 'Printed'}</span>
                           <span>Batch · expires</span>
                        </VInvHead>
                        {rows.map((v) => (
                           <VInvRow
                              key={v._id}
                              $withCheck={view === 'unredeemed'}
                              $withVendo={!deviceId}
                              onClick={
                                 view === 'unredeemed'
                                    ? () => toggle(v._id)
                                    : undefined
                              }
                              $clickable={view === 'unredeemed'}
                           >
                              {view === 'unredeemed' && (
                                 <input
                                    type="checkbox"
                                    checked={selected.has(v._id)}
                                    onChange={() => toggle(v._id)}
                                    onClick={(e) => e.stopPropagation()}
                                 />
                              )}
                              <VInvCode>{v.code}</VInvCode>
                              <VInvRate>
                                 ₱{v.price} · {fmtRate(v)}
                              </VInvRate>
                              {!deviceId && (
                                 <VInvMeta>
                                    {typeof v.vendoDevice === 'object' && v.vendoDevice?.name ? v.vendoDevice.name : '—'}
                                 </VInvMeta>
                              )}
                              <VInvType $gcash={v.type === 'gcash'}>
                                 {v.type}
                              </VInvType>
                              <VInvWhen>{fmtWhen(v)}</VInvWhen>
                              <VInvMeta title={v.moveable && v.activeMac ? `On phone ${v.activeMac}` : undefined}>
                                 {v.batchLabel || '—'}
                                 {v.validUntil ? ` · until ${new Date(v.validUntil).toLocaleDateString()}` : ''}
                                 {v.moveable ? ' · moveable' : ''}
                              </VInvMeta>
                           </VInvRow>
                        ))}
                     </VInvScroll>
                     </VInvTable>
                  )}

                  {paginate && totalPages > 1 && (
                     <VInvPager>
                        <ApplyCancelBtn
                           type="button"
                           disabled={page <= 1}
                           onClick={() => setPage((p) => p - 1)}
                        >
                           ← Prev
                        </ApplyCancelBtn>
                        <VInvCount>
                           page {page} / {totalPages}
                        </VInvCount>
                        <ApplyCancelBtn
                           type="button"
                           disabled={page >= totalPages}
                           onClick={() => setPage((p) => p + 1)}
                        >
                           Next →
                        </ApplyCancelBtn>
                     </VInvPager>
                  )}

                  {view === 'unredeemed' && (
                     <VInvPrintOpts>
                        <input
                           type="text"
                           value={storeName}
                           onChange={(e) => setStoreName(e.target.value)}
                           placeholder="Store name on the cards (optional)"
                        />
                        <label>
                           <input
                              type="checkbox"
                              checked={includeDetails}
                              onChange={(e) => setIncludeDetails(e.target.checked)}
                           />
                           Show price &amp; time on cards
                        </label>
                     </VInvPrintOpts>
                  )}
               </PrintDrawerBody>

               <PrintDrawerFoot className="bulk-print-controls">
                  <ApplyCancelBtn type="button" onClick={onClose}>
                     Close
                  </ApplyCancelBtn>
                  {view === 'unredeemed' && selected.size > 0 && (
                     <ApplyCancelBtn
                        type="button"
                        onClick={handleDeleteSelected}
                        disabled={deleting}
                        style={{ color: colors.DANGER, borderColor: colors.DANGER }}
                        title="Removes the selected unused codes. Used codes are kept."
                     >
                        {deleting
                           ? 'Deleting…'
                           : confirmDelete
                           ? `Tap again to delete ${selected.size}`
                           : `Delete ${selected.size} selected`}
                     </ApplyCancelBtn>
                  )}
                  {/* The label carries the active rate: nobody should be
                      able to export a filtered file believing it is the
                      whole inventory. */}
                  <ApplyCancelBtn
                     type="button"
                     onClick={handleExport}
                     disabled={exporting || total === 0}
                     title="Download these vouchers as a spreadsheet"
                  >
                     {exporting
                        ? 'Building…'
                        : activeRateLabel
                        ? `Export CSV (₱${activeRateLabel.price} · ${
                             activeRateLabel.capMb != null
                                ? fmtDataCap(activeRateLabel.capMb)
                                : formatPrintMinutes(activeRateLabel.duration)
                          })`
                        : 'Export CSV'}
                  </ApplyCancelBtn>
                  {view === 'unredeemed' && (
                     <ApplyPrimaryBtn
                        type="button"
                        onClick={handlePrint}
                        disabled={printRows.length === 0}
                     >
                        Print {printRows.length || ''} selected
                     </ApplyPrimaryBtn>
                  )}
               </PrintDrawerFoot>
               {exportError && <VInvExportError role="alert">{exportError}</VInvExportError>}
            </PrintDialog>
         </PrintOverlay>

         {/* Off-screen A4 canvas — same machinery as bulk print, fed the
             SELECTED rows. Revealed only by the print stylesheet. */}
         {printRows.length > 0 && (
            <OffscreenPrintHost className="bulk-print-offscreen">
               <PrintCanvas className="bulk-print-canvas">
                  <PrintGrid>
                     {printRows.map((v) => (
                        <PrintCard key={v._id}>
                           {storeName && (
                              <PrintCardHeader>
                                 <PrintCardStore>{storeName}</PrintCardStore>
                              </PrintCardHeader>
                           )}
                           <PrintCardCode>{v.code}</PrintCardCode>
                           {includeDetails && (
                              <PrintCardMeta>
                                 ₱{v.price} · {fmtRate(v)}
                              </PrintCardMeta>
                           )}
                        </PrintCard>
                     ))}
                  </PrintGrid>
               </PrintCanvas>
            </OffscreenPrintHost>
         )}
         <PrintGlobalStyle />
      </>
   );

   return createPortal(body, document.body);
}

const VInvTabRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-bottom: 12px;
`;
const VInvTab = styled.button<{ $active: boolean }>`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 6px 12px;
   border-radius: 8px;
   border: 1px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $active }) => ($active ? `${colors.PRIMARY}14` : colors.WHITE)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY4)};
   cursor: pointer;
`;
const VInvCount = styled.span`
   margin-left: auto;
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const VInvFilterRow = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 8px;
   margin-bottom: 10px;
   label {
      font-size: 11px;
      font-weight: 700;
      color: ${colors.GRAY5};
   }
`;
const VInvRateSelect = styled.select`
   font-family: inherit;
   font-size: 12px;
   padding: 5px 8px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.GRAY4};
   cursor: pointer;
   max-width: 260px;
`;
const VInvExportError = styled.div`
   padding: 8px 14px 12px;
   font-size: 11px;
   color: ${colors.DANGER};
   text-align: right;
`;
const VInvEmpty = styled.div`
   padding: 28px 12px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 10px;
`;
/* Columns are fixed widths and the table scrolls SIDEWAYS in a narrow
   drawer instead of squeezing "₱5 · 300 MB" onto three lines (Ray
   2026-09-15). Paging off → a tall box that scrolls DOWN. */
const invCols = ($withCheck: boolean, $withVendo?: boolean) =>
   `${$withCheck ? '24px ' : ''}120px 150px ${$withVendo ? '140px ' : ''}70px 90px 160px`;
const VInvTable = styled.div<{ $tall?: boolean }>`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   overflow: hidden;
   ${({ $tall }) => ($tall ? 'max-height: 60vh; display: flex; flex-direction: column;' : '')}
`;
const VInvScroll = styled.div`
   overflow: auto;
   min-height: 0;
   > * { min-width: max-content; }
`;
const VInvRate = styled.span`
   white-space: nowrap;
   font-weight: 600;
`;
/* One tidy grid: a small label OVER each full-width control, the search
   spanning the row, Pages at the end. Never a label orphaned from its
   control by wrapping (Ray 2026-09-15). */
const VInvFilters = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 8px 10px;
   align-items: flex-end;
   margin-bottom: 10px;
`;
/* Each select takes an equal share of its row — three selects fill three
   thirds, four fill four quarters, never an empty cell. The search takes
   the whole of its row bar the Pages toggle. */
const VInvField = styled.div<{ $wide?: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 3px;
   min-width: 0;
   flex: ${({ $wide }) => ($wide ? '1 1 200px' : '1 1 120px')};
   label {
      font-size: 10.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: ${colors.GRAY5};
   }
   select, input {
      width: 100%;
      max-width: none;
   }
`;
const VInvSearch = styled.input`
   font-family: inherit;
   font-size: 12px;
   padding: 5px 8px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.GRAY4};
   letter-spacing: 0.04em;
   height: 30px;
   &::placeholder { letter-spacing: 0; color: ${colors.GRAY9}; }
`;
const VInvToggle = styled.label`
   display: inline-flex;
   align-items: center;
   justify-content: flex-end;
   gap: 6px;
   height: 30px;
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY5};
   cursor: pointer;
   white-space: nowrap;
`;
const VInvHead = styled.div<{ $withCheck: boolean; $withVendo?: boolean }>`
   display: grid;
   grid-template-columns: ${({ $withCheck, $withVendo }) => invCols($withCheck, $withVendo)};
   gap: 8px;
   align-items: center;
   padding: 8px 12px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
   font-size: 10px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
`;
const VInvRow = styled.div<{ $withCheck: boolean; $withVendo?: boolean; $clickable?: boolean }>`
   display: grid;
   grid-template-columns: ${({ $withCheck, $withVendo }) => invCols($withCheck, $withVendo)};
   gap: 8px;
   align-items: center;
   padding: 9px 12px;
   border-bottom: 1px solid ${colors.GRAY14};
   font-size: 12.5px;
   color: ${colors.MAIN_DARK};
   cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
   &:last-child {
      border-bottom: none;
   }
   &:hover {
      background: ${({ $clickable }) =>
         $clickable ? `${colors.PRIMARY}08` : 'transparent'};
   }
`;
const VInvMeta = styled.span`
   font-size: 11.5px;
   color: ${colors.GRAY5};
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;
const BulkTwoCol = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 10px;
   @media (max-width: 520px) {
      grid-template-columns: 1fr;
   }
`;
const BulkMoreToggle = styled.button`
   background: transparent;
   border: none;
   padding: 6px 0;
   color: ${colors.BLUE};
   font-size: 13px;
   cursor: pointer;
   text-align: left;
`;
const BulkMore = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 10px;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 8px;
`;
const BulkHint = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY5};
   margin-top: 3px;
`;
const VInvCode = styled.span`
   font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
   font-weight: 800;
   letter-spacing: 0.08em;
`;
const VInvType = styled.span<{ $gcash: boolean }>`
   font-size: 10px;
   font-weight: 700;
   text-transform: uppercase;
   color: ${({ $gcash }) => ($gcash ? colors.PRIMARY : colors.GRAY4)};
`;
const VInvWhen = styled.span`
   font-size: 11.5px;
   color: ${colors.GRAY5};
   white-space: nowrap;
`;
const VInvPager = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   justify-content: flex-end;
   padding-top: 10px;
`;
const VInvPrintOpts = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
   margin-top: 12px;
   input[type='text'] {
      flex: 1;
      padding: 7px 10px;
      border: 1px solid ${colors.GRAY12};
      border-radius: 8px;
      font-family: inherit;
      font-size: 12.5px;
      color: ${colors.MAIN_DARK};
      background: ${colors.WHITE};
   }
   label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: ${colors.GRAY4};
      white-space: nowrap;
      cursor: pointer;
   }
`;

/**
 * Single modal that handles BOTH the bulk-generate inputs AND the A4
 * print preview in one place. No more stacked Generate → Preview
 * modals — operator stays in the same dialog the whole time:
 *
 *   1. Form across the top (rate / copies / store name / logo)
 *   2. Click Generate → server mints N codes
 *   3. Preview area below populates with the cards laid out on A4
 *   4. Toggle "Include amount and hours" to flip that line per card
 *   5. Click Print → window.print(); print stylesheet hides everything
 *      EXCEPT the .bulk-print-canvas so only the cards hit paper
 *
 * Portal-rendered so it escapes the DeviceSettings modal's stacking
 * context — full viewport overlay, no nested-modal weirdness.
 */
export function BulkPrintGenerateModal({
   deviceId,
   voucherRates,
   dataPlanId,
   onClose,
   advanced = false,
}: {
   deviceId: string;
   /** Show Moveable + More options (prefix, expiry, batch, speed, pause).
    *  Only the Vouchers page → Over the counter tab turns this on
    *  (Ray 2026-09-15); Device Settings and the sub-login stay simple. */
   advanced?: boolean;
   /** Saved time-voucher rates. Only these fields are read, so the
    *  sub-login portal can pass its API rows straight in. */
   voucherRates: Array<
      Pick<RatePlan, 'label'> & { _id: string; price: string | number; duration: string | number }
   >;
   /** When the device is linked to a data plan, pass the plan id so the
    *  modal can also offer the plan's voucher-method rates. */
   dataPlanId: string | null;
   onClose: () => void;
}) {
   // Pull the device's data plan (if any) to surface its voucher-method
   // rates in the same dropdown as the time vouchers. Operator picks one
   // from either list — the submit code below routes to the right
   // bulk-generate path based on the prefix encoded into the value.
   const { data: dataPlan } = useQuery({
      queryKey: ['data-plan', dataPlanId],
      queryFn: () => getDataPlan(dataPlanId!),
      enabled: !!dataPlanId,
   });
   const dataVoucherRates = (dataPlan?.rates ?? []).filter((r) => r.method === 'voucher');

   // Composite value scheme: 'time:<vendoRateId>' or 'data:<dataPlanRateId>'.
   // Lets a single <select> handle both kinds.
   const firstOption = voucherRates[0]
      ? `time:${voucherRates[0]._id}`
      : dataVoucherRates[0]
      ? `data:${dataVoucherRates[0]._id}`
      : 'custom:';
   const [rateKey, setRateKey] = useState<string>(firstOption);
   const [copies, setCopies] = useState<string>('20');
   // "Custom" — typed amount + duration, no saved rate needed.
   const [customPrice, setCustomPrice] = useState<string>('');
   const [customMinutes, setCustomMinutes] = useState<number | undefined>(undefined);
   // Moveable: one phone at a time, the newest phone takes the time over.
   const [moveable, setMoveable] = useState(false);
   // More options (all optional).
   const [moreOpen, setMoreOpen] = useState(false);
   const [prefix, setPrefix] = useState('');
   const [codeLength, setCodeLength] = useState('8');
   const [validUntil, setValidUntil] = useState('');
   const [batchLabel, setBatchLabel] = useState('');
   const [downloadMbps, setDownloadMbps] = useState('');
   const [uploadMbps, setUploadMbps] = useState('');
   const [validityMinutes, setValidityMinutes] = useState<number | undefined>(undefined);
   const [storeName, setStoreName] = useState<string>(
      () => localStorage.getItem(PRINT_STORE_NAME_KEY) || ''
   );
   const [logoDataUrl, setLogoDataUrl] = useState<string>('');
   const [includeDetails, setIncludeDetails] = useState(true);
   const [submitting, setSubmitting] = useState(false);
   const [generated, setGenerated] = useState<BulkVoucher[] | null>(null);

   const handleLogoPick = (file: File | null) => {
      if (!file) {
         setLogoDataUrl('');
         return;
      }
      if (file.size > 2 * 1024 * 1024) {
         toast.error('Logo file must be 2 MB or smaller');
         return;
      }
      const reader = new FileReader();
      reader.onload = () => setLogoDataUrl(String(reader.result || ''));
      reader.onerror = () => toast.error('Could not read logo file');
      reader.readAsDataURL(file);
   };

   const handleGenerate = async () => {
      if (submitting) return;
      const n = parseInt(copies, 10);
      if (!Number.isFinite(n) || n < 1 || n > 500) {
         toast.error('Copies must be between 1 and 500');
         return;
      }
      if (!rateKey) {
         toast.error('Pick a voucher rate');
         return;
      }
      // Composite value: 'time:<id>' → VendoRate (time voucher),
      // 'data:<id>' → DataPlanRate (data voucher), 'custom:' → typed.
      const [kind, id] = rateKey.split(':');
      if (kind === 'custom') {
         if (!(Number(customPrice) > 0)) {
            toast.error('Type the amount, e.g. 20');
            return;
         }
         if (!(Number(customMinutes) >= 1)) {
            toast.error('Type the duration, e.g. 3 hours');
            return;
         }
      }
      const len = parseInt(codeLength, 10);
      if (!Number.isFinite(len) || len < 4 || len > 20) {
         toast.error('Code length must be 4 to 20, e.g. 8');
         return;
      }
      if (prefix && !/^[A-Za-z0-9]{1,8}$/.test(prefix)) {
         toast.error('Prefix: letters and numbers only, up to 8, e.g. PROMO');
         return;
      }
      const mbpsOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
      setSubmitting(true);
      try {
         const { vouchers } = await bulkGenerateVouchersApi({
            deviceId,
            ...(kind === 'data'
               ? { dataPlanRateId: id }
               : kind === 'custom'
               ? { custom: { price: Number(customPrice), durationMinutes: Number(customMinutes) } }
               : { rateId: id }),
            copies: n,
            moveable,
            prefix: prefix.trim().toUpperCase(),
            codeLength: len,
            validUntil: validUntil ? new Date(validUntil).toISOString() : null,
            batchLabel: batchLabel.trim(),
            downloadMbps: mbpsOrNull(downloadMbps),
            uploadMbps: mbpsOrNull(uploadMbps),
            validityMinutes: validityMinutes ?? null,
         });
         setGenerated(vouchers);
      } catch (err: any) {
         const errMsg =
            err?.response?.data?.error ?? err?.message ?? 'Failed to generate vouchers';
         toast.error(errMsg);
      } finally {
         setSubmitting(false);
      }
   };

   const handlePrint = () => {
      try {
         localStorage.setItem(PRINT_STORE_NAME_KEY, storeName.trim());
      } catch { /* non-fatal */ }
      window.print();
   };

   const handleReset = () => {
      setGenerated(null);
   };

   // "Custom" needs no saved rate, so the form is always usable.
   const noRates = false;

   const body = (
      <>
         <PrintOverlay className="bulk-print-overlay" onClick={onClose}>
            <PrintDialog
               className="bulk-print-dialog"
               onClick={(e) => e.stopPropagation()}
            >
               <PrintDrawerHead className="bulk-print-controls">
                  <div>
                     <PrintTitle>Bulk print vouchers</PrintTitle>
                     <PrintSub>
                        Generate physical voucher cards. The actual cards
                        only render in the browser's print dialog — saves
                        screen space here.
                     </PrintSub>
                  </div>
                  <ApplyDrawerClose
                     type="button"
                     onClick={onClose}
                     aria-label="Close"
                  >
                     ×
                  </ApplyDrawerClose>
               </PrintDrawerHead>

               <PrintDrawerBody className="bulk-print-controls">
                  {noRates ? (
                     <PrintNotice>
                        Save at least one voucher rate first, then come back.
                     </PrintNotice>
                  ) : (
                     <>
                        <BulkField>
                           <BulkLbl>Voucher rate</BulkLbl>
                           <BulkSelect
                              value={rateKey}
                              onChange={(e) => setRateKey(e.target.value)}
                              disabled={submitting || !!generated}
                           >
                              {voucherRates.length > 0 && (
                                 <optgroup label="Time vouchers">
                                    {voucherRates.map((r) => (
                                       <option key={`time-${r._id}`} value={`time:${r._id}`}>
                                          ₱{r.price} · {r.label}
                                          {r.duration ? ` · ${r.duration} min` : ''}
                                       </option>
                                    ))}
                                 </optgroup>
                              )}
                              {dataVoucherRates.length > 0 && (
                                 <optgroup label="Data vouchers (from data plan)">
                                    {dataVoucherRates.map((r) => (
                                       <option key={`data-${r._id}`} value={`data:${r._id}`}>
                                          ₱{r.price} · {r.label}
                                          {r.capMb
                                             ? r.capMb >= 1000
                                                ? ` · ${(r.capMb / 1000).toFixed(r.capMb % 1000 === 0 ? 0 : 1)} GB`
                                                : ` · ${r.capMb} MB`
                                             : ''}
                                       </option>
                                    ))}
                                 </optgroup>
                              )}
                              <option value="custom:">Custom — type the amount and duration</option>
                           </BulkSelect>
                        </BulkField>

                        {rateKey === 'custom:' && (
                           <BulkTwoCol>
                              <BulkField>
                                 <BulkLbl>Amount (₱)</BulkLbl>
                                 <BulkInput
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="e.g. 20"
                                    value={customPrice}
                                    onChange={(e) => setCustomPrice(e.target.value)}
                                    disabled={submitting || !!generated}
                                 />
                              </BulkField>
                              <BulkField>
                                 <BulkLbl>Duration</BulkLbl>
                                 <DurationUnitInput
                                    value={customMinutes}
                                    onChange={(mins) => setCustomMinutes(mins ?? undefined)}
                                    min={1}
                                    compact
                                 />
                              </BulkField>
                           </BulkTwoCol>
                        )}

                        <BulkField>
                           <BulkLbl>Copies (1–500)</BulkLbl>
                           <BulkInput
                              type="number"
                              min={1}
                              max={500}
                              value={copies}
                              onChange={(e) => setCopies(e.target.value)}
                              disabled={submitting || !!generated}
                           />
                        </BulkField>

                        {advanced && rateKey.split(':')[0] !== 'data' && (
                           <PrintCheckbox>
                              <input
                                 type="checkbox"
                                 checked={moveable}
                                 onChange={(e) => setMoveable(e.target.checked)}
                                 disabled={submitting || !!generated}
                              />
                              Moveable — one phone at a time. A new phone that types the
                              code takes the remaining time; the old phone is cut.
                           </PrintCheckbox>
                        )}

                        <BulkField>
                           <BulkLbl>Store name (optional)</BulkLbl>
                           <BulkInput
                              type="text"
                              value={storeName}
                              onChange={(e) => setStoreName(e.target.value)}
                              placeholder="e.g. Wonder Beach Resort"
                              maxLength={60}
                              disabled={submitting}
                           />
                        </BulkField>

                        <BulkField>
                           <BulkLbl>Logo (optional, ≤2 MB)</BulkLbl>
                           <BulkInput
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                 handleLogoPick(e.target.files?.[0] ?? null)
                              }
                              disabled={submitting}
                           />
                           {logoDataUrl && (
                              <BulkLogoPreview>
                                 <img src={logoDataUrl} alt="Logo preview" />
                                 <button
                                    type="button"
                                    onClick={() => setLogoDataUrl('')}
                                    title="Remove logo"
                                 >
                                    ×
                                 </button>
                              </BulkLogoPreview>
                           )}
                        </BulkField>

                        <PrintCheckbox>
                           <input
                              type="checkbox"
                              checked={includeDetails}
                              onChange={(e) =>
                                 setIncludeDetails(e.target.checked)
                              }
                           />
                           Include amount and hours on each card
                        </PrintCheckbox>

                        {advanced && (
                        <>
                        <BulkMoreToggle type="button" onClick={() => setMoreOpen((o) => !o)}>
                           {moreOpen ? '▾' : '▸'} More options (prefix, expiry, batch, speed, pause)
                        </BulkMoreToggle>
                        {moreOpen && (
                           <BulkMore>
                              <BulkTwoCol>
                                 <BulkField>
                                    <BulkLbl>Prefix (optional)</BulkLbl>
                                    <BulkInput
                                       type="text"
                                       placeholder="e.g. PROMO"
                                       maxLength={8}
                                       value={prefix}
                                       onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                                       disabled={submitting || !!generated}
                                    />
                                 </BulkField>
                                 <BulkField>
                                    <BulkLbl>Code length (4–20)</BulkLbl>
                                    <BulkInput
                                       type="number"
                                       min={4}
                                       max={20}
                                       value={codeLength}
                                       onChange={(e) => setCodeLength(e.target.value)}
                                       disabled={submitting || !!generated}
                                    />
                                 </BulkField>
                              </BulkTwoCol>
                              <BulkTwoCol>
                                 <BulkField>
                                    <BulkLbl>Valid until (optional)</BulkLbl>
                                    <BulkInput
                                       type="datetime-local"
                                       value={validUntil}
                                       onChange={(e) => setValidUntil(e.target.value)}
                                       disabled={submitting || !!generated}
                                    />
                                    <BulkHint>After this date the code no longer works.</BulkHint>
                                 </BulkField>
                                 <BulkField>
                                    <BulkLbl>Batch label (optional)</BulkLbl>
                                    <BulkInput
                                       type="text"
                                       placeholder="e.g. JULY-PROMO"
                                       maxLength={40}
                                       value={batchLabel}
                                       onChange={(e) => setBatchLabel(e.target.value)}
                                       disabled={submitting || !!generated}
                                    />
                                    <BulkHint>Find this print run later in the voucher list.</BulkHint>
                                 </BulkField>
                              </BulkTwoCol>
                              <BulkTwoCol>
                                 <BulkField>
                                    <BulkLbl>Download speed (Mbps)</BulkLbl>
                                    <BulkInput
                                       type="text"
                                       inputMode="decimal"
                                       placeholder="blank = vendo default"
                                       value={downloadMbps}
                                       onChange={(e) => setDownloadMbps(e.target.value)}
                                       disabled={submitting || !!generated}
                                    />
                                 </BulkField>
                                 <BulkField>
                                    <BulkLbl>Upload speed (Mbps)</BulkLbl>
                                    <BulkInput
                                       type="text"
                                       inputMode="decimal"
                                       placeholder="blank = vendo default"
                                       value={uploadMbps}
                                       onChange={(e) => setUploadMbps(e.target.value)}
                                       disabled={submitting || !!generated}
                                    />
                                 </BulkField>
                              </BulkTwoCol>
                              <BulkField>
                                 <BulkLbl>Validity (pause)</BulkLbl>
                                 <DurationUnitInput
                                    value={validityMinutes}
                                    onChange={(mins) => setValidityMinutes(mins ?? undefined)}
                                    min={0}
                                    compact
                                 />
                                 <BulkHint>
                                    How long paused time is kept for these codes. Blank = the
                                    rate's or vendo's rule. 0 = kept for ever.
                                 </BulkHint>
                              </BulkField>
                           </BulkMore>
                        )}
                        </>
                        )}


                        {/* Live preview — single sample card showing how
                            each printed voucher will look with the
                            current form values. Updates as the operator
                            types store name, picks a rate, uploads
                            logo, toggles the include-amount checkbox.
                            Code is a fixed placeholder (real codes are
                            minted server-side on Generate). */}
                        {(() => {
                           const [kind, id] = rateKey.split(':');
                           const timeRate = kind === 'time'
                              ? voucherRates.find((r) => r._id === id)
                              : null;
                           const dataRate = kind === 'data'
                              ? dataVoucherRates.find((r) => r._id === id)
                              : null;
                           const previewPrice =
                              kind === 'custom'
                                 ? Number(customPrice) || undefined
                                 : timeRate?.price ?? dataRate?.price;
                           const previewMeta = kind === 'custom'
                              ? formatPrintMinutes(Number(customMinutes) || 0)
                              : timeRate
                              ? formatPrintMinutes(Number(timeRate.duration) || 0)
                              : dataRate?.capMb != null
                              ? dataRate.capMb >= 1000
                                 ? `${(dataRate.capMb / 1000).toFixed(dataRate.capMb % 1000 === 0 ? 0 : 1)} GB`
                                 : `${dataRate.capMb} MB`
                              : '';
                           return (
                              <PreviewSection>
                                 <PreviewLabel>Sample card preview</PreviewLabel>
                                 <PreviewCardWrap>
                                    <PrintCard>
                                       <PrintCardHeader>
                                          {logoDataUrl && (
                                             <PrintCardLogo src={logoDataUrl} alt="" />
                                          )}
                                          {storeName.trim() && (
                                             <PrintCardStore>{storeName.trim()}</PrintCardStore>
                                          )}
                                       </PrintCardHeader>
                                       <PrintCardCode>ABCD1234</PrintCardCode>
                                       {includeDetails && previewPrice != null && (
                                          <PrintCardMeta>
                                             ₱{previewPrice} · {previewMeta}
                                          </PrintCardMeta>
                                       )}
                                    </PrintCard>
                                 </PreviewCardWrap>
                                 <PreviewHint>
                                    ABCD1234 is a placeholder. Real codes are
                                    minted when you click Generate.
                                 </PreviewHint>
                              </PreviewSection>
                           );
                        })()}

                        {generated && (
                           <GeneratedBanner>
                              <GeneratedBannerIcon>
                                 <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                 >
                                    <polyline points="20 6 9 17 4 12" />
                                 </svg>
                              </GeneratedBannerIcon>
                              <GeneratedBannerText>
                                 <strong>{generated.length} codes generated.</strong>
                                 <br />
                                 Click <em>Print</em> to open the browser print
                                 dialog with the A4 layout.
                              </GeneratedBannerText>
                           </GeneratedBanner>
                        )}
                     </>
                  )}
               </PrintDrawerBody>

               <PrintDrawerFoot className="bulk-print-controls">
                  <ApplyCancelBtn
                     type="button"
                     onClick={onClose}
                     disabled={submitting}
                  >
                     Cancel
                  </ApplyCancelBtn>
                  {generated ? (
                     <>
                        <ApplyCancelBtn type="button" onClick={handleReset}>
                           Edit
                        </ApplyCancelBtn>
                        <ApplyPrimaryBtn type="button" onClick={handlePrint}>
                           Print
                        </ApplyPrimaryBtn>
                     </>
                  ) : (
                     <ApplyPrimaryBtn
                        type="button"
                        onClick={handleGenerate}
                        disabled={submitting || noRates}
                     >
                        {submitting ? 'Generating…' : `Generate ${copies || 0}`}
                     </ApplyPrimaryBtn>
                  )}
               </PrintDrawerFoot>
            </PrintDialog>
         </PrintOverlay>

         {/* Off-screen print canvas — kept in the DOM after generate so
             the print CSS can reveal it without the operator ever seeing
             it in the drawer. Positioned far off-screen on the visible
             page; print stylesheet repositions to 0,0 at print time. */}
         {generated && (
            <OffscreenPrintHost className="bulk-print-offscreen">
               <PrintCanvas className="bulk-print-canvas">
                  <PrintGrid>
                     {generated.map((v) => (
                        <PrintCard key={v._id}>
                           <PrintCardHeader>
                              {logoDataUrl && (
                                 <PrintCardLogo src={logoDataUrl} alt="" />
                              )}
                              {storeName && (
                                 <PrintCardStore>{storeName}</PrintCardStore>
                              )}
                           </PrintCardHeader>
                           <PrintCardCode>{v.code}</PrintCardCode>
                           {includeDetails && (
                              <PrintCardMeta>
                                 ₱{v.price} ·{' '}
                                 {/* Data vouchers carry capMb instead of
                                     duration. Render GB above 1000 MB,
                                     MB below — same convention as the
                                     dashboard's formatMb helper. */}
                                 {v.capMb != null
                                    ? v.capMb >= 1000
                                       ? `${(v.capMb / 1000).toFixed(v.capMb % 1000 === 0 ? 0 : 1)} GB`
                                       : `${v.capMb} MB`
                                    : formatPrintMinutes(v.duration)}
                              </PrintCardMeta>
                           )}
                        </PrintCard>
                     ))}
                  </PrintGrid>
               </PrintCanvas>
            </OffscreenPrintHost>
         )}
         <PrintGlobalStyle />
      </>
   );

   return createPortal(body, document.body);
}

/** MB below 1 GB, GB above — the same convention the portal and the
 *  data-plan screens use, so one voucher reads identically everywhere. */
function fmtDataCap(mb: number): string {
   return mb >= 1000
      ? `${(mb / 1000).toFixed(mb % 1000 === 0 ? 0 : 1)} GB`
      : `${mb} MB`;
}

function formatPrintMinutes(mins: number): string {
   if (!Number.isFinite(mins) || mins <= 0) return '';
   if (mins < 60) return `${mins} min`;
   const hrs = Math.floor(mins / 60);
   const rem = mins % 60;
   if (rem === 0) return `${hrs}h`;
   return `${hrs}h ${rem}m`;
}

// Read-only display of the linked Data Plan's rates. Owner makes
// changes via the Data Plans page (button below jumps there).
function DataPlanReadOnly({
   planId,
   method = 'coin',
}: {
   planId: string;
   /** Which rate-method to surface. Coin shows coin-method rates (default,
    *  for the Coin sub-tab). 'voucher' shows voucher-method rates (for
    *  the Voucher sub-tab when the device is linked to a plan that has
    *  voucher rates configured). */
   method?: 'coin' | 'voucher';
}) {
   const navigate = useNavigate();
   const { data: plan, isLoading } = useQuery({
      queryKey: ['data-plan', planId],
      queryFn: () => getDataPlan(planId),
   });

   if (isLoading) return <SkeletonLines lines={3} />;
   if (!plan) return <PanelHint>Could not load the linked data plan.</PanelHint>;

   const filtered = plan.rates.filter((r) => (r.method ?? 'coin') === method);

   return (
      <>
         <LinkedPill>
            Linked to <strong>{plan.name}</strong>
            {plan.isp && <span> · {plan.isp}</span>}
         </LinkedPill>
         {filtered.length === 0 ? (
            <EmptyNote>
               {method === 'voucher'
                  ? 'No voucher rates on the plan yet. Add voucher-method rates in the Data Plans page.'
                  : 'No rates configured on the plan yet.'}
            </EmptyNote>
         ) : (
            <>
               <RateHeader>
                  <RateHeaderCell $w="90px">Price (₱)</RateHeaderCell>
                  <RateHeaderCell $w="100px">Data cap</RateHeaderCell>
                  <RateHeaderCell>Label</RateHeaderCell>
               </RateHeader>
               {filtered.map((r) => (
                  <RateRow key={r._id}>
                     <ReadCell $w="90px">₱{r.price}</ReadCell>
                     <ReadCell $w="100px">{formatMb(r.capMb)}</ReadCell>
                     <ReadCell>{r.label}</ReadCell>
                  </RateRow>
               ))}
            </>
         )}
         <ManageBtn onClick={() => navigate('/data-plans')}>
            Manage in Data Plans →
         </ManageBtn>
      </>
   );
}

// Banner that announces the rate mode at the top of the Rate Plans
// tab. Variant tints the background so 'data' and 'time' read as
// distinct without needing toggles.
const ModeBanner = styled.div<{ $variant: 'time' | 'data' }>`
   padding: 10px 14px;
   border-radius: 8px;
   background: ${(p) => p.$variant === 'data'
      ? `${colors.SUCCESS}10`
      : `${colors.PRIMARY}10`};
   border-left: 3px solid ${(p) => p.$variant === 'data' ? colors.SUCCESS : colors.PRIMARY};
   margin-bottom: 14px;
`;

const ModeBannerLbl = styled.div`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 2px;
`;

const ModeBannerSub = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.4;
`;

const LinkedPill = styled.div`
   padding: 8px 12px;
   border-radius: 6px;
   background: ${colors.GRAY14};
   color: ${colors.MAIN_DARK};
   font-size: 12px;
   margin-bottom: 12px;
`;

// Read-only cell shaped like CompactInput so the data-rate display
// row visually matches the time-rate editable row across the tab —
// same height, same border, same text color, just non-interactive.
const ReadCell = styled.span<{ $w?: string }>`
   width: ${({ $w }) => $w || '100%'};
   flex: ${({ $w }) => ($w ? '0 0 auto' : '1')};
   padding: 5px 8px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 5px;
   font-size: 12px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: ${colors.GRAY14};
   line-height: 1.6;
`;

const ManageBtn = styled.button`
   width: 100%;
   margin-top: 12px;
   padding: 10px;
   border-radius: 7px;
   background: white;
   border: 1px solid ${colors.GRAY3};
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover { border-color: ${colors.SECONDARY}; background: #eef2ff; }
`;

export default DeviceSettings;

const RecoveryWhere = styled.div`
   margin: 8px 0 12px;
   padding: 10px 12px;
   border-radius: 8px;
   background: #f4f7ff;
   border: 1px solid #dbe4ff;
   font-size: 13px;
   line-height: 1.6;
   color: #2b3550;
`;
